import { isNullOrUndefined, isUndefined, extend, setValue, getValue, deleteObject, createElement } from '@syncfusion/ej2-base';
import { DataManager, Query, ODataAdaptor, WebApiAdaptor, ODataV4Adaptor } from '@syncfusion/ej2-data';
import { getSwapKey, isScheduledTask, getTaskData, isRemoteData, getIndex, isCountRequired, updateDates } from '../base/utils';
import { ConstraintType } from '../base/enum';
import { CellEdit } from './cell-edit';
import { TaskbarEdit } from './taskbar-edit';
import { DialogEdit } from './dialog-edit';
import { Dialog } from '@syncfusion/ej2-popups';
import { MultiSelect, CheckBoxSelection, DropDownList } from '@syncfusion/ej2-dropdowns';
import { ConnectorLineEdit } from '../task-dependency/connector-line-edit';
import { TreeGrid, Edit as TreeGridEdit } from '@syncfusion/ej2-treegrid';
/**
 * The Edit Module is used to handle editing actions.
 *
 */
export class Edit {
    constructor(parent) {
        this.isFromDeleteMethod = false;
        this.targetedRecords = [];
        this.isValidatedEditedRecord = false;
        this.createArray = true;
        this.violationArgs = {};
        this.deletedRecord = [];
        this.canReset = false;
        this.isFlatDataHaveUnAsignedTask = false;
        /** @hidden */
        this.updateParentRecords = [];
        /** @hidden */
        this.isaddtoBottom = false;
        this.confirmDialog = null;
        this.taskbarMoved = false;
        this.predecessorUpdated = false;
        this.isBreakLoop = false;
        this.isDialogEditing = false;
        /**
         * @private
         */
        this.deletedTaskDetails = [];
        this.parent = parent;
        this.parent.predecessorModule.validatedChildItems = [];
        if (this.parent.editSettings.allowEditing && this.parent.editSettings.mode === 'Auto') {
            this.cellEditModule = new CellEdit(this.parent);
        }
        if (this.parent.taskFields.dependency && this.parent.editSettings.allowEditing) {
            this.parent.connectorLineEditModule = new ConnectorLineEdit(this.parent);
        }
        if (this.parent.editSettings.allowAdding || (this.parent.editSettings.allowEditing &&
            (this.parent.editSettings.mode === 'Dialog' || this.parent.editSettings.mode === 'Auto'))) {
            this.dialogModule = new DialogEdit(this.parent);
        }
        if (this.parent.editSettings.allowTaskbarEditing) {
            this.taskbarEditModule = new TaskbarEdit(this.parent);
        }
        if (this.parent.editSettings.allowDeleting) {
            const confirmDialog = createElement('div', {
                id: this.parent.element.id + '_deleteConfirmDialog'
            });
            this.parent.element.appendChild(confirmDialog);
            this.renderDeleteConfirmDialog();
        }
        this.parent.treeGrid.recordDoubleClick = this.recordDoubleClick.bind(this);
        this.parent.treeGrid.editSettings.allowAdding = this.parent.editSettings.allowAdding;
        this.parent.treeGrid.editSettings.allowDeleting = this.parent.editSettings.allowDeleting;
        this.parent.treeGrid.editSettings.showDeleteConfirmDialog = this.parent.editSettings.showDeleteConfirmDialog;
        this.parent.treeGrid.editSettings.allowNextRowEdit = this.parent.editSettings.allowNextRowEdit;
        if (this.parent.editSettings.mode === 'Dialog') {
            TreeGrid.Inject(TreeGridEdit);
        }
        this.updateDefaultColumnEditors();
    }
    getModuleName() {
        return 'edit';
    }
    /**
     * Method to update default edit params and editors for Gantt
     *
     * @returns {void} .
     */
    updateDefaultColumnEditors() {
        const customEditorColumns = [this.parent.taskFields.id, this.parent.taskFields.progress, this.parent.taskFields.resourceInfo,
            this.parent.taskFields.type, 'taskType'];
        for (let i = 0; i < customEditorColumns.length; i++) {
            if (!isNullOrUndefined(customEditorColumns[i]) && customEditorColumns[i].length > 0) {
                const column = this.parent.getColumnByField(customEditorColumns[parseInt(i.toString(), 10)], this.parent.treeGridModule.treeGridColumns);
                if (column) {
                    if (column.field === this.parent.taskFields.id) {
                        this.updateIDColumnEditParams(column);
                    }
                    else if (column.field === this.parent.taskFields.progress && isNullOrUndefined(column.edit)) {
                        this.updateProgessColumnEditParams(column);
                    }
                    else if (column.field === this.parent.taskFields.resourceInfo) {
                        this.updateResourceColumnEditor(column);
                    }
                    else if (column.field === this.parent.taskFields.type || column.field === 'taskType') {
                        this.updateTaskTypeColumnEditor(column);
                    }
                }
            }
        }
    }
    /**
     * Method to update editors for id column in Gantt
     *
     * @param {ColumnModel} column .
     * @returns {void} .
     */
    updateIDColumnEditParams(column) {
        const editParam = {
            min: 0,
            decimals: 0,
            enableRtl: this.parent.enableRtl,
            validateDecimalOnType: true,
            format: 'n0',
            showSpinButton: false
        };
        this.updateEditParams(column, editParam);
    }
    /**
     * Method to update edit params of default progress column
     *
     * @param {ColumnModel} column .
     * @returns {void} .
     */
    updateProgessColumnEditParams(column) {
        const editParam = {
            min: 0,
            enableRtl: this.parent.enableRtl,
            decimals: 0,
            validateDecimalOnType: true,
            max: 100,
            format: 'n0'
        };
        this.updateEditParams(column, editParam);
    }
    /**
     * Assign edit params for id and progress columns
     *
     * @param {ColumnModel} column .
     * @param {object} editParam .
     * @returns {void} .
     */
    updateEditParams(column, editParam) {
        if (isNullOrUndefined(column.edit)) {
            column.edit = {};
            column.edit.params = {};
        }
        else if (isNullOrUndefined(column.edit.params)) {
            column.edit.params = {};
        }
        extend(editParam, column.edit.params);
        column.edit.params = editParam;
        const ganttColumn = this.parent.getColumnByField(column.field, this.parent.ganttColumns);
        ganttColumn.edit = column.edit;
    }
    /**
     * Method to update resource column editor for default resource column
     *
     * @param {ColumnModel} column .
     * @returns {void} .
     */
    updateResourceColumnEditor(column) {
        this.parent.treeGridModule.currentEditRow = {};
        if (this.parent.editSettings.allowEditing && isNullOrUndefined(column.edit) && this.parent.editSettings.mode === 'Auto') {
            column.editType = 'dropdownedit';
            column.edit = this.getResourceEditor();
            const ganttColumn = this.parent.getColumnByField(column.field, this.parent.ganttColumns);
            ganttColumn.editType = 'dropdownedit';
            ganttColumn.edit = column.edit;
        }
    }
    /**
     * Method to create resource custom editor
     *
     * @returns {IEditCell} .
     */
    getResourceEditor() {
        const resourceSettings = this.parent.resourceFields;
        const editObject = {};
        let editor;
        MultiSelect.Inject(CheckBoxSelection);
        editObject.write = (args) => {
            this.parent.treeGridModule.currentEditRow = {};
            editor = new MultiSelect({
                dataSource: new DataManager(this.parent.resources),
                fields: { text: resourceSettings.name, value: resourceSettings.id },
                enableRtl: this.parent.enableRtl,
                mode: 'CheckBox',
                showDropDownIcon: true,
                popupHeight: '350px',
                delimiterChar: ',',
                value: this.parent.treeGridModule.getResourceIds(args.rowData)
            });
            editor.appendTo(args.element);
        };
        editObject.read = (element) => {
            let value = element.ej2_instances[0].value;
            const resourcesName = [];
            if (isNullOrUndefined(value)) {
                value = [];
            }
            for (let i = 0; i < value.length; i++) {
                for (let j = 0; j < this.parent.resources.length; j++) {
                    if (this.parent.resources[j][resourceSettings.id] === value[i]) {
                        resourcesName.push(this.parent.resources[j][resourceSettings.name]);
                        break;
                    }
                }
            }
            this.parent.treeGridModule.currentEditRow[this.parent.taskFields.resourceInfo] = value;
            return resourcesName.join(',');
        };
        editObject.destroy = () => {
            if (editor) {
                editor.destroy();
            }
        };
        return editObject;
    }
    /**
     * Method to update task type column editor for task type
     *
     * @param {ColumnModel} column .
     * @returns {void} .
     */
    updateTaskTypeColumnEditor(column) {
        if (this.parent.editSettings.allowEditing && isNullOrUndefined(column.edit) && this.parent.editSettings.mode === 'Auto') {
            column.editType = 'dropdownedit';
            column.edit = this.getTaskTypeEditor();
            const ganttColumn = this.parent.getColumnByField(column.field, this.parent.ganttColumns);
            ganttColumn.editType = 'dropdownedit';
            ganttColumn.edit = column.edit;
        }
    }
    /**
     * Method to create task type custom editor
     *
     * @returns {IEditCell} .
     */
    getTaskTypeEditor() {
        const editObject = {};
        let editor;
        const types = [{ 'ID': 1, 'Value': 'FixedUnit' }, { 'ID': 2, 'Value': 'FixedWork' }, { 'ID': 3, 'Value': 'FixedDuration' }];
        editObject.write = (args) => {
            this.parent.treeGridModule.currentEditRow = {};
            editor = new DropDownList({
                dataSource: new DataManager(types),
                enableRtl: this.parent.enableRtl,
                fields: { value: 'Value' },
                popupHeight: '350px',
                value: getValue('taskType', args.rowData.ganttProperties)
            });
            editor.appendTo(args.element);
        };
        editObject.read = (element) => {
            const value = element.ej2_instances[0].value;
            const key = this.parent.taskFields.type || 'taskType';
            this.parent.treeGridModule.currentEditRow[key] = value;
            return value;
        };
        editObject.destroy = () => {
            if (editor) {
                editor.destroy();
            }
        };
        return editObject;
    }
    /**
     * @returns {void} .
     * @private
     */
    reUpdateEditModules() {
        const editSettings = this.parent.editSettings;
        if (editSettings.allowEditing) {
            if (this.parent.editModule.cellEditModule && editSettings.mode === 'Dialog') {
                this.cellEditModule.destroy();
                this.parent.treeGrid.recordDoubleClick = this.recordDoubleClick.bind(this);
            }
            else if (isNullOrUndefined(this.parent.editModule.cellEditModule) && editSettings.mode === 'Auto') {
                this.cellEditModule = new CellEdit(this.parent);
            }
            if (this.parent.editModule.dialogModule && editSettings.mode === 'Auto') {
                this.parent.treeGrid.recordDoubleClick = undefined;
            }
            else if (isNullOrUndefined(this.parent.editModule.dialogModule)) {
                this.dialogModule = new DialogEdit(this.parent);
            }
        }
        else {
            if (this.cellEditModule) {
                this.cellEditModule.destroy();
            }
            if (this.dialogModule) {
                this.dialogModule.destroy();
            }
        }
        if (editSettings.allowDeleting && editSettings.showDeleteConfirmDialog) {
            if (isNullOrUndefined(this.confirmDialog)) {
                const confirmDialog = createElement('div', {
                    id: this.parent.element.id + '_deleteConfirmDialog'
                });
                this.parent.element.appendChild(confirmDialog);
                this.renderDeleteConfirmDialog();
            }
        }
        else if (!editSettings.allowDeleting || !editSettings.showDeleteConfirmDialog) {
            if (this.confirmDialog && !this.confirmDialog.isDestroyed) {
                this.confirmDialog.destroy();
            }
        }
        if (editSettings.allowTaskbarEditing) {
            if (isNullOrUndefined(this.parent.editModule.taskbarEditModule)) {
                this.taskbarEditModule = new TaskbarEdit(this.parent);
            }
        }
        else {
            if (this.taskbarEditModule) {
                this.taskbarEditModule.destroy();
            }
        }
    }
    recordDoubleClick(args) {
        if (this.parent.editSettings.allowEditing && this.parent.editSettings.mode === 'Dialog') {
            let ganttData;
            if (args.row) {
                const rowIndex = getValue('rowIndex', args.row);
                ganttData = this.parent.currentViewData[rowIndex];
            }
            if (!isNullOrUndefined(ganttData)) {
                this.dialogModule.openEditDialog(ganttData);
            }
        }
        this.parent.ganttChartModule.recordDoubleClick(args);
    }
    /**
     * @returns {void} .
     * @private
     */
    destroy() {
        if (this.cellEditModule) {
            this.cellEditModule.destroy();
        }
        if (this.taskbarEditModule) {
            this.taskbarEditModule.destroy();
        }
        if (this.dialogModule) {
            this.dialogModule.destroy();
        }
        if (this.confirmDialog && !this.confirmDialog.isDestroyed) {
            this.confirmDialog.destroy();
        }
    }
    /**
     * Method to update record with new values.
     *
     * @param {Object} data - Defines new data to update.
     * @returns {void} .
     */
    updateRecordByID(data) {
        if (this.parent.enableImmutableMode && this.parent.editSettings.allowEditing &&
            this.parent.treeGrid.element.getElementsByClassName('e-editedbatchcell').length > 0) {
            this.parent.treeGrid.endEdit();
        }
        if (!this.parent.readOnly) {
            const tasks = this.parent.taskFields;
            if (isNullOrUndefined(data) || isNullOrUndefined(data[tasks.id])) {
                return;
            }
            const ganttData = this.parent.viewType === 'ResourceView' ?
                this.parent.flatData[this.parent.getTaskIds().indexOf((data['hasChildRecords'] ? 'R' : 'T') + data[tasks.id])] : this.parent.getRecordByID(data[tasks.id]);
            if (!isNullOrUndefined(ganttData[tasks.milestone])) {
                if (ganttData[tasks.milestone] === true) {
                    ganttData[tasks.milestone] = false;
                }
            }
            if (this.parent.undoRedoModule && !this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent['isUndoRedoItemPresent']('Edit') && ganttData) {
                this.parent.undoRedoModule['createUndoCollection']();
                const details = {};
                details['requestType'] = ((this.parent.contextMenuModule && this.parent.contextMenuModule.item) ? this.parent.contextMenuModule.item : 'methodUpdate');
                details['modifiedRecords'] = extend([], [ganttData], [], true);
                this.parent.undoRedoModule['getUndoCollection'][this.parent.undoRedoModule['getUndoCollection'].length - 1] = details;
            }
            if (!isNullOrUndefined(this.parent.editModule) && ganttData) {
                this.parent.isOnEdit = true;
                this.validateUpdateValues(data, ganttData, true);
                if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed']) {
                    if (this.parent.viewType === 'ProjectView' && data['ganttProperties'].predecessor && ganttData['ganttProperties'].predecessor) {
                        for (let i = 0; i < data['ganttProperties'].predecessor.length; i++) {
                            const isValid = ganttData.ganttProperties.predecessor.filter((pred) => {
                                if ((pred.from === data['ganttProperties'].predecessor[i].from &&
                                    pred.to === data['ganttProperties'].predecessor[i].to
                                    && data['ganttProperties'].predecessor[i].offset !== pred.offset)) {
                                    const record = this.parent.flatData[this.parent.ids.indexOf(pred.to)];
                                    const changeValue = record.ganttProperties.predecessor.filter((pred) => {
                                        return (pred.from === data['ganttProperties'].predecessor[i].from && pred.to === data['ganttProperties'].predecessor[i].to);
                                    });
                                    changeValue[0].offset = data['ganttProperties'].predecessor[i].offset;
                                }
                                return pred.from !== data['ganttProperties'].predecessor[i].from && pred.from !== data['ganttProperties'].predecessor[i].to;
                            });
                            if (isValid.length > 0) {
                                for (let j = 0; j < isValid.length; j++) {
                                    const record = this.parent.flatData[this.parent.ids.indexOf(isValid[j].from)];
                                    for (let k = 0; k < record.ganttProperties.predecessor.length; k++) {
                                        if (record.ganttProperties.predecessor[k].from === isValid[j].from &&
                                            record.ganttProperties.predecessor[k].to === isValid[j].to) {
                                            record.ganttProperties.predecessor.splice(k, 1);
                                            break;
                                        }
                                    }
                                }
                                break;
                            }
                        }
                    }
                    else if (!data['ganttProperties'].predecessor && ganttData.ganttProperties.predecessor) {
                        for (let i = 0; i < ganttData.ganttProperties.predecessor.length; i++) {
                            let id;
                            if (ganttData.ganttProperties.taskId.toString() === ganttData.ganttProperties.predecessor[i].from) {
                                id = ganttData.ganttProperties.predecessor[i].to;
                            }
                            else {
                                id = ganttData.ganttProperties.predecessor[i].from;
                            }
                            const parentRec = this.parent.flatData[this.parent.ids.indexOf(id)];
                            if (!isNullOrUndefined(parentRec) && !isNullOrUndefined(parentRec.ganttProperties) &&
                                !isNullOrUndefined(parentRec.ganttProperties.predecessor)) {
                                for (let j = 0; j < parentRec.ganttProperties.predecessor.length; j++) {
                                    if (parentRec.ganttProperties.predecessor[j].from ===
                                        ganttData.ganttProperties.predecessor[i].from &&
                                        parentRec.ganttProperties.predecessor[j].to ===
                                            ganttData.ganttProperties.predecessor[i].to) {
                                        parentRec.ganttProperties.predecessor.splice(j, 1);
                                    }
                                }
                            }
                        }
                    }
                    ganttData.ganttProperties.resourceInfo = data['ganttProperties'].resourceInfo;
                    ganttData[this.parent.taskFields.resourceInfo] = data[this.parent.taskFields.resourceInfo];
                    ganttData['taskData'][this.parent.taskFields.resourceInfo] = data['taskData'][this.parent.taskFields.resourceInfo];
                    this.parent.setRecordValue('resourceNames', data['ganttProperties'].resourceNames, ganttData.ganttProperties, true);
                    this.parent.setRecordValue('predecessorsName', data['ganttProperties'].predecessorsName, ganttData.ganttProperties, true);
                    if (this.parent.taskFields.dependency) {
                        this.parent.setRecordValue(this.parent.taskFields.dependency, data[this.parent.taskFields.dependency], ganttData);
                    }
                    this.parent.setRecordValue('predecessor', data['ganttProperties'].predecessor, ganttData.ganttProperties, true);
                }
                if (data[this.parent.taskFields.resourceInfo]) {
                    if (ganttData.ganttProperties.duration === 0 &&
                        (ganttData.ganttProperties.taskType !== 'FixedWork' || this.parent['isConvertedMilestone'])) {
                        this.parent.dataOperation.updateWorkWithDuration(ganttData);
                    }
                    if (!this.parent.undoRedoModule || !this.parent.undoRedoModule['isUndoRedoPerformed']) {
                        this.updateResourceRelatedFields(ganttData, 'resource');
                    }
                    this.parent.dateValidationModule.calculateEndDate(ganttData);
                }
                const keys = Object.keys(data);
                if (keys.indexOf(tasks.startDate) !== -1 || keys.indexOf(tasks.endDate) !== -1 ||
                    keys.indexOf(tasks.duration) !== -1) {
                    this.parent.dataOperation.calculateScheduledValues(ganttData, ganttData.taskData, false);
                }
                if (keys.indexOf(tasks.baselineDuration) !== -1) {
                    this.parent.dataOperation.calculateScheduledValuesforBaseline(ganttData, ganttData.taskData, false);
                }
                this.parent.dataOperation.updateWidthLeft(ganttData);
                if (!isUndefined(data[this.parent.taskFields.dependency]) &&
                    data[this.parent.taskFields.dependency] !== ganttData.ganttProperties.predecessorsName) {
                    this.parent.connectorLineEditModule.updatePredecessor(ganttData, data[this.parent.taskFields.dependency]);
                }
                else {
                    const args = {};
                    args.data = ganttData;
                    if (this.parent.viewType === 'ResourceView') {
                        args.action = 'methodUpdate';
                    }
                    this.parent.editModule.initiateUpdateAction(args);
                }
            }
        }
    }
    /**
     *
     * @param {object} data .
     * @param {IGanttData} ganttData .
     * @param {boolean} isFromDialog .
     * @returns {void} .
     * @private
     */
    validateUpdateValues(data, ganttData, isFromDialog) {
        const ganttObj = this.parent;
        const tasks = ganttObj.taskFields;
        const ganttPropByMapping = getSwapKey(ganttObj.columnMapping);
        const scheduleFieldNames = [];
        let isScheduleValueUpdated = false;
        for (const key of Object.keys(data)) {
            if (tasks.startDate === key || tasks.endDate === key || tasks.duration === key || tasks.baselineDuration === key) {
                const isBaseline = (tasks.baselineDuration === key) ? true : false;
                if (isNullOrUndefined(data[`${key}`]) && !ganttObj.allowUnscheduledTasks) {
                    continue;
                }
                const ganttProps = ganttData.ganttProperties;
                const isDurationKey = tasks.duration === key || tasks.baselineDuration === key;
                if (isFromDialog) {
                    if (isDurationKey) {
                        ganttObj.dataOperation.updateDurationValue(data[key], ganttProps, isBaseline);
                        if (!isBaseline && ganttProps.duration > 0 && ganttProps.isMilestone) {
                            this.parent.setRecordValue('isMilestone', false, ganttProps, true);
                        }
                    }
                    else {
                        let tempDate = typeof data[key] === 'string' ? new Date(data[key]) : data[key];
                        if (key === tasks.endDate && isNullOrUndefined(ganttProps.startDate) && (isNullOrUndefined(data[tasks.duration]) || data[tasks.duration] === '' || Number.isNaN(data[tasks.duration]))) {
                            tempDate = this.parent.editModule.dialogModule['isFromEditDialog'] ? ganttData.ganttProperties.endDate : data[tasks.endDate];
                        }
                        ganttObj.setRecordValue(ganttPropByMapping[key], tempDate, ganttProps, true);
                        if (ganttData[tasks.startDate] && !(ganttData[tasks.startDate] instanceof Date)) {
                            ganttData[tasks.startDate] = new Date(ganttData[tasks.startDate]);
                        }
                        if (ganttData[tasks.endDate] && !(ganttData[tasks.endDate] instanceof Date)) {
                            ganttData[tasks.endDate] = new Date(ganttData[tasks.endDate]);
                        }
                    }
                    ganttObj.dataOperation.updateMappingData(ganttData, ganttPropByMapping[key]);
                }
                else {
                    scheduleFieldNames.push(key);
                    isScheduleValueUpdated = true;
                }
            }
            else if (tasks.resourceInfo === key) {
                const resourceData = ganttObj.dataOperation.setResourceInfo(data);
                if (this.parent.viewType === 'ResourceView') {
                    if (JSON.stringify(resourceData) !== JSON.stringify(ganttData.ganttProperties.resourceInfo)) {
                        this.parent.editModule.dialogModule.isResourceUpdate = true;
                        this.parent.editModule.dialogModule.previousResource = !isNullOrUndefined(ganttData.ganttProperties.resourceInfo) ?
                            [...ganttData.ganttProperties.resourceInfo] : [];
                    }
                    else {
                        this.parent.editModule.dialogModule.isResourceUpdate = false;
                    }
                }
                if (!this.parent.undoRedoModule || !this.parent.undoRedoModule['isUndoRedoPerformed']) {
                    ganttData.ganttProperties.resourceInfo = resourceData;
                }
                ganttObj.dataOperation.updateMappingData(ganttData, 'resourceInfo');
            }
            else if (tasks.dependency === key) {
                //..
            }
            else if ([
                tasks.progress,
                tasks.notes,
                tasks.durationUnit,
                tasks.milestone,
                tasks.name,
                tasks.baselineStartDate,
                tasks.baselineEndDate,
                tasks.id,
                tasks.segments,
                tasks.cssClass,
                tasks.constraintDate,
                tasks.constraintType
            ].indexOf(key) !== -1) {
                const column = ganttObj.columnByField[key];
                /* eslint-disable-next-line */
                let value = data[key];
                if (!isNullOrUndefined(column) && (column.editType === 'datepickeredit' || column.editType === 'datetimepickeredit')) {
                    value = ganttObj.dataOperation.getDateFromFormat(value);
                }
                let ganttPropKey = ganttPropByMapping[key];
                switch (key) {
                    case tasks.id:
                        ganttPropKey = 'taskId';
                        break;
                    case tasks.name:
                        ganttPropKey = 'taskName';
                        break;
                    case tasks.constraintDate:
                        ganttPropKey = 'constraintDate';
                        break;
                    case tasks.constraintType:
                        ganttPropKey = 'constraintType';
                        break;
                    case tasks.cssClass:
                        ganttPropKey = 'cssClass';
                        break;
                    case tasks.milestone:
                        ganttPropKey = 'isMilestone';
                        if (!isNullOrUndefined(tasks.duration)) {
                            const ganttProp = ganttData.ganttProperties;
                            let durationValue = data[tasks.duration];
                            if (value) {
                                durationValue = 0;
                            }
                            else {
                                durationValue = durationValue <= 0 ? 1 : durationValue;
                            }
                            ganttObj.setRecordValue(tasks.duration, durationValue, ganttData, true);
                            ganttObj.setRecordValue('duration', durationValue, ganttProp, true);
                            ganttObj.setRecordValue('taskData.' + tasks.duration, durationValue, ganttData);
                        }
                        break;
                    case tasks.segments:
                        if (ganttData.ganttProperties.segments) {
                            ganttPropKey = 'segments';
                            /* eslint-disable-next-line */
                            if (data && !isNullOrUndefined(data[tasks.segments]) && data[tasks.segments].length > 0
                                && data['ganttProperties'] && data['ganttProperties'].segments) {
                                if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed']) {
                                    ganttData.ganttProperties.segments = data['ganttProperties'].segments;
                                }
                                const totDuration = ganttData.ganttProperties.segments.reduce((acc, segment) => acc + segment.duration, 0);
                                const sdate = ganttData.ganttProperties.startDate;
                                /* eslint-disable-next-line */
                                const edate = this.parent.dataOperation.getEndDate(sdate, totDuration, ganttData.ganttProperties.durationUnit, ganttData.ganttProperties, false);
                                ganttObj.setRecordValue('endDate', ganttObj.dataOperation.getDateFromFormat(edate), ganttData.ganttProperties, true);
                            }
                        }
                        break;
                }
                if (!isNullOrUndefined(ganttPropKey)) {
                    const seg = [];
                    if (ganttPropKey === 'segments' && value && value.length > 0 && !isNullOrUndefined(ganttData.ganttProperties.segments)) {
                        for (let i = 0; i < value.length; i++) {
                            const segment = {};
                            if (value[i][tasks.startDate]) {
                                segment['startDate'] = value[i][tasks.startDate];
                            }
                            if (value[i][tasks.endDate]) {
                                segment['endDate'] = value[i][tasks.endDate];
                            }
                            if (value[i][tasks.duration]) {
                                segment['duration'] = parseFloat(value[i][tasks.duration]);
                            }
                            if (value[i][tasks.id]) {
                                segment['id'] = value[i][tasks.id];
                            }
                            seg.push(segment);
                        }
                        ganttObj.setRecordValue(ganttPropKey, seg, ganttData.ganttProperties, true);
                    }
                    else {
                        if (ganttPropKey !== 'segments' || (ganttPropKey === 'segments' && (isNullOrUndefined(value) || (Array.isArray(value) && value.length === 0)))) {
                            ganttObj.setRecordValue(ganttPropKey, value, ganttData.ganttProperties, true);
                        }
                    }
                }
                if ((key === tasks.baselineStartDate || key === tasks.baselineEndDate) &&
                    (ganttData.ganttProperties.baselineStartDate && ganttData.ganttProperties.baselineEndDate)) {
                    const ganttProps = ganttData.ganttProperties;
                    ganttObj.setRecordValue('baselineStartDate', ganttObj.dataOperation.checkBaselineStartDate(ganttProps.baselineStartDate, ganttProps), ganttProps, true);
                    const dayEndTime = this.parent['getCurrentDayEndTime'](ganttProps.baselineEndDate);
                    if (ganttProps.baselineEndDate && ganttProps.baselineEndDate.getHours() === 0 &&
                        dayEndTime !== 86400) {
                        ganttObj.dataOperation.setTime(dayEndTime, ganttProps.baselineEndDate);
                    }
                    if ((ganttProps.baselineStartDate && ganttProps.baselineEndDate &&
                        (ganttProps.baselineStartDate.getTime() > ganttProps.baselineEndDate.getTime())) ||
                        ((!isNullOrUndefined(ganttProps.baselineStartDate) &&
                            !isNullOrUndefined(ganttProps.startDate) &&
                            (ganttProps.baselineStartDate.getTime() === ganttProps.startDate.getTime()))
                            && (!isNullOrUndefined(ganttProps.baselineEndDate) &&
                                !isNullOrUndefined(ganttProps.endDate) &&
                                (ganttProps.baselineEndDate.toLocaleDateString() ===
                                    ganttProps.endDate.toLocaleDateString())) &&
                            ganttProps.isMilestone)) {
                        ganttProps.baselineEndDate = ganttProps.baselineStartDate;
                    }
                    ganttObj.setRecordValue('baselineEndDate', ganttObj.dataOperation.checkBaselineEndDate(ganttProps.baselineEndDate), ganttProps, true);
                    ganttObj.setRecordValue('baselineLeft', ganttObj.dataOperation.calculateBaselineLeft(ganttProps), ganttProps, true);
                    ganttObj.setRecordValue('baselineWidth', ganttObj.dataOperation.calculateBaselineWidth(ganttProps), ganttProps, true);
                    if (ganttData[tasks.baselineStartDate] && !(ganttData[tasks.baselineStartDate] instanceof Date)) {
                        ganttData[tasks.baselineStartDate] = new Date(ganttData[tasks.baselineStartDate]);
                    }
                    if (ganttData[tasks.baselineEndDate] && !(ganttData[tasks.baselineEndDate] instanceof Date)) {
                        ganttData[tasks.baselineEndDate] = new Date(ganttData[tasks.baselineEndDate]);
                    }
                }
                ganttObj.setRecordValue('taskData.' + key, value, ganttData);
                /* eslint-disable-next-line */
                if (key === tasks.segments && data && !isNullOrUndefined(data[tasks.segments]) && data[tasks.segments].length > 0) {
                    if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'] && data['ganttProperties']
                        && data['ganttProperties'].segments) {
                        ganttData.ganttProperties.segments = data['ganttProperties'].segments;
                    }
                    ganttObj.dataOperation.setSegmentsInfo(ganttData, true);
                }
                ganttObj.setRecordValue(key, value, ganttData);
            }
            else if (tasks.indicators === key) {
                const value = data[key];
                ganttObj.setRecordValue('indicators', value, ganttData.ganttProperties, true);
                ganttObj.setRecordValue('taskData.' + key, value, ganttData);
                ganttObj.setRecordValue(key, value, ganttData);
            }
            else if (tasks.work === key) {
                ganttObj.setRecordValue('work', data[key], ganttData.ganttProperties, true);
                this.parent.dataOperation.updateMappingData(ganttData, 'work');
                this.parent.dataOperation.updateMappingData(ganttData, 'duration');
                this.parent.dataOperation.updateMappingData(ganttData, 'endDate');
            }
            else if (key === tasks.type) {
                const value = data[key];
                ganttObj.setRecordValue('taskType', value, ganttData.ganttProperties, true);
                ganttObj.setRecordValue(key, value, ganttData);
                ganttObj.setRecordValue('taskData.' + key, value, ganttData);
            }
            else if (key === tasks.expandState) {
                const value = data[key];
                this.parent.setRecordValue('taskData.' + key, value, ganttData);
                this.parent.setRecordValue(this.parent.taskFields.expandState, value, ganttData);
                this.parent.setRecordValue('expanded', value, ganttData);
                this.parent.treeGrid.refresh();
            }
            else if (ganttObj.customColumns.indexOf(key) !== -1) {
                const column = ganttObj.columnByField[key];
                /* eslint-disable-next-line */
                let value = data[key];
                if (isNullOrUndefined(column.edit)) {
                    if (column.editType === 'datepickeredit' || column.editType === 'datetimepickeredit') {
                        value = ganttObj.dataOperation.getDateFromFormat(value);
                    }
                }
                ganttObj.setRecordValue('taskData.' + key, value, ganttData);
                ganttObj.setRecordValue(key, value, ganttData);
            }
            else if (tasks.manual === key) {
                ganttObj.setRecordValue('isAutoSchedule', !data[key], ganttData.ganttProperties, true);
                this.parent.setRecordValue(key, data[key], ganttData);
                this.updateTaskScheduleModes(ganttData);
            }
        }
        if (isScheduleValueUpdated) {
            this.validateScheduleValues(scheduleFieldNames, ganttData, data);
        }
    }
    /**
     * To update duration, work, resource unit
     *
     * @param {IGanttData} currentData .
     * @param {string} column .
     * @returns {void} .
     */
    updateResourceRelatedFields(currentData, column) {
        const ganttProp = currentData.ganttProperties;
        const previousdata = this.parent.previousRecords;
        const taskType = ganttProp.taskType ? ganttProp.taskType : this.parent.taskType;
        let isEffectDriven;
        const isAutoSchedule = ganttProp.isAutoSchedule;
        const resources = (this.parent.editModule.dialogModule &&
            this.parent.editModule.dialogModule['currentResources']) ? this.parent.editModule.dialogModule['currentResources']
            : currentData.ganttProperties.resourceInfo;
        if (!isNullOrUndefined(resources)) {
            switch (taskType) {
                case 'FixedUnit':
                    if (!isNullOrUndefined(previousdata[ganttProp.uniqueID]) &&
                        !isNullOrUndefined(previousdata[ganttProp.uniqueID].ganttProperties) &&
                        (previousdata[ganttProp.uniqueID].ganttProperties.resourceNames
                            === null ||
                            previousdata[ganttProp.uniqueID].ganttProperties.resourceNames === '')) {
                        this.parent.dataOperation.updateWorkWithDuration(currentData);
                    }
                    if (resources.length === 0) {
                        return;
                    }
                    else if (isAutoSchedule && resources.length) {
                        if (column === 'resource' || column === 'work') {
                            this.parent.dataOperation.updateDurationWithWork(currentData);
                        }
                        else if (column === 'duration' || column === 'endDate') {
                            this.parent.dataOperation.updateWorkWithDuration(currentData);
                            if (ganttProp.duration === 0) {
                                this.parent.setRecordValue('isMilestone', true, ganttProp, true);
                            }
                        }
                    }
                    else if (!isAutoSchedule && column === 'work') {
                        this.parent.dataOperation.updateUnitWithWork(currentData);
                    }
                    else {
                        this.parent.dataOperation.updateWorkWithDuration(currentData);
                    }
                    break;
                case 'FixedWork':
                    if (resources.length === 0) {
                        return;
                    }
                    else if (isAutoSchedule) {
                        if (column === 'duration' || column === 'endDate') {
                            this.parent.dataOperation.updateUnitWithWork(currentData);
                            if (ganttProp.duration === 0) {
                                this.parent.setRecordValue('isMilestone', true, ganttProp, true);
                            }
                        }
                        else {
                            this.parent.dataOperation.updateDurationWithWork(currentData);
                        }
                    }
                    else {
                        if (column === 'work') {
                            this.parent.dataOperation.updateUnitWithWork(currentData);
                        }
                        else {
                            this.parent.dataOperation.updateWorkWithDuration(currentData);
                        }
                    }
                    break;
                case 'FixedDuration':
                    if (resources.length === 0) {
                        // To validate the work column, if set duration as 0 via celledit action, where resource colection is 0
                        this.parent.dataOperation.updateWorkWithDuration(currentData);
                        return;
                    }
                    if (resources.length && (column === 'work' || (isAutoSchedule &&
                        isEffectDriven && (column === 'resource')))) {
                        if (column === 'work') {
                            this.parent.dataOperation.updateUnitWithWork(currentData);
                        }
                        else {
                            this.parent.dataOperation.updateWorkWithDuration(currentData);
                        }
                    }
                    else {
                        this.parent.dataOperation.updateWorkWithDuration(currentData);
                    }
                    break;
            }
        }
        // To validate the work colum if set duration as 0, while resource is null/undefined
        else if (isNullOrUndefined(resources) && taskType === 'FixedDuration' && ganttProp.duration === 0) {
            this.parent.dataOperation.updateWorkWithDuration(currentData);
        }
    }
    validateScheduleValues(fieldNames, ganttData, data) {
        const ganttObj = this.parent;
        if (fieldNames.length > 2) {
            ganttObj.dataOperation.calculateScheduledValues(ganttData, data, false);
        }
        else if (fieldNames.length > 1) {
            this.validateScheduleByTwoValues(data, fieldNames, ganttData);
        }
        else {
            this.dialogModule.validateScheduleValuesByCurrentField(fieldNames[0], data[fieldNames[0]], ganttData);
        }
    }
    validateScheduleByTwoValues(data, fieldNames, ganttData) {
        const ganttObj = this.parent;
        let startDate;
        let endDate;
        let duration;
        const tasks = ganttObj.taskFields;
        const ganttProp = ganttData.ganttProperties;
        const isUnscheduledTask = ganttObj.allowUnscheduledTasks;
        if (fieldNames.indexOf(tasks.startDate) !== -1) {
            startDate = data[tasks.startDate];
        }
        if (fieldNames.indexOf(tasks.endDate) !== -1) {
            endDate = data[tasks.endDate];
        }
        if (fieldNames.indexOf(tasks.duration) !== -1) {
            duration = data[tasks.duration];
        }
        if (startDate && endDate || (isUnscheduledTask && (fieldNames.indexOf(tasks.startDate) !== -1) &&
            (fieldNames.indexOf(tasks.endDate) !== -1))) {
            ganttObj.setRecordValue('startDate', ganttObj.dataOperation.getDateFromFormat(startDate), ganttProp, true);
            ganttObj.setRecordValue('endDate', ganttObj.dataOperation.getDateFromFormat(endDate), ganttProp, true);
            ganttObj.dataOperation.calculateDuration(ganttData);
        }
        else if (endDate && duration || (isUnscheduledTask &&
            (fieldNames.indexOf(tasks.endDate) !== -1) && (fieldNames.indexOf(tasks.duration) !== -1))) {
            ganttObj.setRecordValue('endDate', ganttObj.dataOperation.getDateFromFormat(endDate), ganttProp, true);
            ganttObj.dataOperation.updateDurationValue(duration, ganttProp);
        }
        else if (startDate && duration || (isUnscheduledTask && (fieldNames.indexOf(tasks.startDate) !== -1)
            && (fieldNames.indexOf(tasks.duration) !== -1))) {
            ganttObj.setRecordValue('startDate', ganttObj.dataOperation.getDateFromFormat(startDate), ganttProp, true);
            ganttObj.dataOperation.updateDurationValue(duration, ganttProp);
        }
    }
    isTaskbarMoved(data) {
        let isMoved = false;
        const taskData = data.ganttProperties;
        const prevData = this.parent.previousRecords &&
            this.parent.previousRecords[data.uniqueID];
        if (prevData && prevData.ganttProperties) {
            const prevStart = getValue('ganttProperties.startDate', prevData);
            const prevEnd = getValue('ganttProperties.endDate', prevData);
            const prevDuration = getValue('ganttProperties.duration', prevData);
            const prevDurationUnit = getValue('ganttProperties.durationUnit', prevData);
            const keys = Object.keys(prevData.ganttProperties);
            if (keys.indexOf('startDate') !== -1 || keys.indexOf('endDate') !== -1 ||
                keys.indexOf('duration') !== -1 || keys.indexOf('durationUnit') !== -1) {
                if ((isNullOrUndefined(prevStart) && !isNullOrUndefined(taskData.startDate)) ||
                    (isNullOrUndefined(prevEnd) && !isNullOrUndefined(taskData.endDate)) ||
                    (isNullOrUndefined(taskData.startDate) && !isNullOrUndefined(prevStart)) ||
                    (isNullOrUndefined(taskData.endDate) && !isNullOrUndefined(prevEnd)) ||
                    (prevStart && prevStart.getTime() !== taskData.startDate.getTime())
                    || (prevEnd && prevEnd.getTime() !== taskData.endDate.getTime())
                    || (!isNullOrUndefined(prevDuration) && prevDuration !== taskData.duration)
                    || (!isNullOrUndefined(prevDuration) && prevDuration === taskData.duration &&
                        prevDurationUnit !== taskData.durationUnit)) {
                    isMoved = true;
                }
            }
        }
        return isMoved;
    }
    removeImmediateParentDependency(predecessorArray, ganttInstance) {
        if (!Array.isArray(predecessorArray)) {
            return [];
        }
        const filtered = [];
        for (let i = 0; i < predecessorArray.length; i++) {
            const dependency = predecessorArray[i];
            const fromTask = ganttInstance.getRecordByID(dependency.from);
            const toTask = ganttInstance.getRecordByID(dependency.to);
            if (!fromTask || !toTask || !fromTask.ganttProperties || !toTask.ganttProperties) {
                filtered.push(dependency);
                continue;
            }
            const fromTaskId = fromTask.ganttProperties.taskId;
            const toTaskId = toTask.ganttProperties.taskId;
            const fromParent = fromTask.parentItem;
            const toParent = toTask.parentItem;
            const fromParentId = fromParent && fromParent.taskId;
            const toParentId = toParent && toParent.taskId;
            const isFromParentOfTo = !isNullOrUndefined(fromParentId) &&
                !isNullOrUndefined(toTaskId) &&
                fromParentId.toString() === toTaskId.toString();
            const isToParentOfFrom = !isNullOrUndefined(toParentId) &&
                !isNullOrUndefined(fromTaskId) &&
                toParentId.toString() === fromTaskId.toString();
            if (isFromParentOfTo || isToParentOfFrom) {
                continue;
            }
            filtered.push(dependency);
        }
        return filtered;
    }
    isPredecessorUpdated(data) {
        let isPredecessorUpdated = false;
        const prevData = this.parent.previousRecords[data.uniqueID];
        // eslint-disable-next-line
        if (prevData && prevData.ganttProperties && prevData.ganttProperties.hasOwnProperty('predecessor')) {
            if (data.ganttProperties.predecessorsName !== prevData.ganttProperties.predecessorsName &&
                !(data.ganttProperties.predecessorsName === '' &&
                    isNullOrUndefined(prevData.ganttProperties.predecessorsName))) {
                isPredecessorUpdated = true;
            }
            else {
                const cleanedPredecessors = this.removeImmediateParentDependency(prevData.ganttProperties.predecessor, this.parent);
                this.parent.setRecordValue('predecessor', cleanedPredecessors, data.ganttProperties, true);
            }
        }
        return isPredecessorUpdated;
    }
    /**
     * Method to check need to open predecessor validate dialog
     *
     * @param {IGanttData} data .
     * @returns {boolean} .
     */
    isCheckPredecessor(data) {
        let isValidatePredecessor = false;
        const prevData = this.parent.previousRecords[data.uniqueID];
        if (prevData && this.parent.taskFields.dependency && this.parent.isInPredecessorValidation &&
            this.parent.predecessorModule.getValidPredecessor(data).length > 0) {
            if (this.isTaskbarMoved(data)) {
                isValidatePredecessor = true;
            }
        }
        return isValidatePredecessor;
    }
    /**
     * Method to copy the ganttProperties values
     *
     * @param {IGanttData} data .
     * @param {IGanttData} updateData .
     * @returns {void} .
     * @private
     */
    updateGanttProperties(data, updateData) {
        const skipProperty = ['taskId', 'uniqueID', 'rowUniqueID', 'parentId', 'predecessor'];
        Object.keys(data.ganttProperties).forEach((property) => {
            if (skipProperty.indexOf(property) === -1) {
                updateData.ganttProperties[property] = data.ganttProperties[property];
            }
        });
    }
    shouldShowDialog(taskViolation, validateMode) {
        if (!validateMode) {
            return false;
        }
        if (taskViolation === 'MustStartOn') {
            return !validateMode.respectMustStartOn;
        }
        if (taskViolation === 'MustFinishOn') {
            return !validateMode.respectMustFinishOn;
        }
        if (taskViolation === 'StartNoLaterThan') {
            return !validateMode.respectStartNoLaterThan;
        }
        if (taskViolation === 'FinishNoLaterThan') {
            return !validateMode.respectFinishNoLaterThan;
        }
        return false;
    }
    isTaskToMilestone(args) {
        let isMilestone = false;
        if (args && args.data) {
            const ganttProps = args.data.ganttProperties;
            const duration = ganttProps.duration;
            const startDate = ganttProps.startDate;
            const endDate = ganttProps.endDate;
            if (!isNullOrUndefined(this.parent.taskFields.duration)) {
                if (duration === 0) {
                    isMilestone = true;
                }
            }
            else if (startDate &&
                endDate &&
                new Date(startDate).getTime() === new Date(endDate).getTime()) {
                isMilestone = true;
            }
            const previousData = this.parent.previousRecords && this.parent.previousRecords[ganttProps.uniqueID];
            if (previousData && previousData.ganttProperties && !isNullOrUndefined(previousData.ganttProperties.isMilestone)) {
                const prevStart = previousData.ganttProperties.startDate;
                const isSameStart = startDate && prevStart && new Date(startDate).getTime() === new Date(prevStart).getTime();
                if (!previousData.ganttProperties.isMilestone && isMilestone && (isSameStart || !prevStart)) {
                    isMilestone = true;
                }
                else {
                    isMilestone = false;
                }
            }
            else {
                isMilestone = false;
            }
        }
        return isMilestone;
    }
    /**
     * Method to update all dependent record on edit action
     *
     * @param {ITaskAddedEventArgs} args .
     * @returns {void} .
     * @private
     */
    initiateUpdateAction(args) {
        let isValidatePredecessor = this.isCheckPredecessor(args.data);
        const isMilestone = this.isTaskToMilestone(args);
        if (isMilestone && args.data.ganttProperties.predecessor) {
            const predecessorArray = args.data.ganttProperties.predecessor;
            const milestoneDate = this.parent.predecessorModule.getPredecessorDate(args.data, predecessorArray, null);
            if (milestoneDate &&
                new Date(milestoneDate).getTime() !==
                    new Date(args.data.ganttProperties.startDate).getTime()) {
                this.parent.setRecordValue('startDate', new Date(milestoneDate.getTime()), args.data.ganttProperties, true);
                this.parent.setRecordValue('endDate', new Date(milestoneDate.getTime()), args.data.ganttProperties, true);
                this.parent.dataOperation.updateTaskData(args.data);
            }
        }
        let parentData;
        let childRecordIndex;
        if (!isNullOrUndefined(args.data.parentItem) && !isValidatePredecessor) {
            parentData = this.parent.getRecordByID(args.data.parentItem.taskId);
            if (this.isTaskbarMoved(args.data) && this.parent.predecessorModule.getValidPredecessor(parentData).length > 0
                && this.parent.isInPredecessorValidation) {
                isValidatePredecessor = true;
            }
            else {
                isValidatePredecessor = false;
            }
        }
        else if (args.data.childRecords.length > 0 && !isValidatePredecessor) {
            isValidatePredecessor = this.isCheckPredecessor(args.data);
            if (!isValidatePredecessor && this.isTaskbarMoved(args.data)) {
                for (let i = 0; i < args.data.childRecords.length; i++) {
                    if (this.parent.predecessorModule.getValidPredecessor(args.data.childRecords[i]).length > 0) {
                        childRecordIndex = i;
                        isValidatePredecessor = true;
                    }
                }
            }
        }
        if (!this.parent.undoRedoModule || (this.parent.undoRedoModule && !this.parent.undoRedoModule['currentAction'] ||
            (this.parent.undoRedoModule['currentAction'] && this.parent.undoRedoModule['currentAction']['action'] !== 'indent' &&
                this.parent.undoRedoModule['currentAction']['action'] !== 'outdent'))) {
            this.taskbarMoved = this.isTaskbarMoved(args.data);
        }
        this.predecessorUpdated = this.isPredecessorUpdated(args.data);
        if (this.predecessorUpdated) {
            this.parent.isConnectorLineUpdate = true;
            this.parent.connectorLineEditModule.addRemovePredecessor(args.data);
        }
        let validateObject = {};
        if (isValidatePredecessor && this.parent.autoCalculateDateScheduling && !(this.parent.isLoad &&
            this.parent.treeGrid.loadChildOnDemand && this.parent.taskFields.hasChildMapping)) {
            if (!isNullOrUndefined(parentData)) {
                validateObject = this.parent.connectorLineEditModule.validateTypes(parentData, args.data);
            }
            else if (!isNullOrUndefined(childRecordIndex)) {
                validateObject = this.parent.connectorLineEditModule.validateTypes(args.data.childRecords[childRecordIndex], args.data);
            }
            else {
                validateObject = this.parent.connectorLineEditModule.validateTypes(args.data);
            }
            this.parent.isConnectorLineUpdate = true;
            const isValidPredecessor = this.parent.predecessorModule['filterPredecessorsByTarget'](args.data.ganttProperties.predecessor, args.data, this.parent.viewType);
            const constraintType = args && args.data && args.data.ganttProperties
                ? args.data.ganttProperties.constraintType
                : null;
            const hasViolationType = !isNullOrUndefined(getValue('violationType', validateObject)) ||
                constraintType === ConstraintType.MustStartOn ||
                constraintType === ConstraintType.MustFinishOn;
            if (hasViolationType && isNullOrUndefined(getValue('violationType', validateObject))) {
                const predecessors = args.data.ganttProperties.predecessor;
                let matchedPredecessor = null;
                const currentTaskId = args.data.ganttProperties.taskId;
                for (let i = 0; i < predecessors.length; i++) {
                    const link = predecessors[i];
                    if (link.to.toString() === currentTaskId.toString()) {
                        matchedPredecessor = link;
                        break;
                    }
                }
                if (matchedPredecessor) {
                    const parentItem = this.parent.connectorLineModule.getRecordByID(matchedPredecessor.from);
                    validateObject['parentTask'] = parentItem;
                }
            }
            if (hasViolationType && (isValidPredecessor.length !== 0 || !args.data.ganttProperties.predecessor)) {
                let newArgs;
                let showDialog = true;
                if (this.parent.taskFields.constraintDate && this.parent.taskFields.constraintType) {
                    this.taskViolation = this.parent.constraintViolationType;
                    this.parent.constraintViolationType = 'PredecessorLink';
                    newArgs = this.validateConstraintViolation(args);
                }
                else {
                    newArgs = this.validateTaskEvent(args);
                }
                let shouldRespectLink = false;
                let editedField = '';
                if (this.parent.editModule && this.parent.editModule.cellEditModule && this.parent.editModule.cellEditModule.editedColumn) {
                    editedField = this.parent.editModule.cellEditModule.editedColumn.field || '';
                }
                const constraintType = newArgs.data.ganttProperties.constraintType;
                const taskFields = this.parent.taskFields;
                const action = args.taskBarEditAction;
                if (editedField && editedField.trim() !== '') {
                    if (constraintType === ConstraintType.MustStartOn && editedField === taskFields.startDate) {
                        shouldRespectLink = true;
                        this.parent.constraintViolationType = 'MustStartOn';
                        this.taskViolation = 'MustStartOn';
                    }
                    else if (constraintType === ConstraintType.MustFinishOn && editedField === taskFields.endDate) {
                        shouldRespectLink = true;
                        this.parent.constraintViolationType = 'MustFinishOn';
                        this.taskViolation = 'MustFinishOn';
                    }
                }
                else if (args.action !== 'DialogEditing') {
                    if (constraintType === ConstraintType.MustStartOn) {
                        if (action !== 'ProgressResizing' && action !== 'RightResizing') {
                            shouldRespectLink = true;
                            this.parent.constraintViolationType = 'MustStartOn';
                            this.taskViolation = 'MustStartOn';
                        }
                        else if (action !== 'ProgressResizing') {
                            showDialog = false;
                        }
                    }
                    else if (constraintType === ConstraintType.MustFinishOn) {
                        if (action !== 'ProgressResizing' && action !== 'LeftResizing') {
                            shouldRespectLink = true;
                            this.parent.constraintViolationType = 'MustFinishOn';
                            this.taskViolation = 'MustFinishOn';
                        }
                        else if (action !== 'ProgressResizing') {
                            showDialog = false;
                        }
                    }
                }
                if (showDialog && (editedField === taskFields.constraintType || editedField === taskFields.constraintDate) &&
                    (this.taskViolation === 'MustFinishOn' || this.taskViolation === 'MustStartOn')) {
                    showDialog = false;
                }
                else if (showDialog) {
                    showDialog = this.shouldShowDialog(this.taskViolation, newArgs.validateMode);
                }
                let dependencyValidationResult = null;
                if (this.parent &&
                    this.parent.editModule &&
                    this.parent.editModule.taskbarEditModule &&
                    typeof this.parent.editModule.taskbarEditModule['isValidDependency'] === 'function') {
                    dependencyValidationResult = this.parent.editModule.taskbarEditModule['isValidDependency'](args.data);
                }
                if (dependencyValidationResult) {
                    const isValid = dependencyValidationResult.isValid;
                    if (!showDialog) {
                        if ((this.parent.updateOffsetOnTaskbarEdit === false && isValid === false &&
                            newArgs.validateMode.respectLink === false) ||
                            (((newArgs.data.ganttProperties.constraintType === ConstraintType.MustStartOn && shouldRespectLink) ||
                                (newArgs.data.ganttProperties.constraintType === ConstraintType.MustFinishOn && shouldRespectLink)) &&
                                newArgs.validateMode.respectLink === false)) {
                            newArgs.validateMode.respectLink = true;
                        }
                        if (this.parent.updateOffsetOnTaskbarEdit === false &&
                            isValid === false &&
                            newArgs.validateMode.respectLink === false) {
                            newArgs.validateMode.respectLink = true;
                        }
                        if ((newArgs.data.ganttProperties.constraintType === ConstraintType.MustStartOn && shouldRespectLink) ||
                            (newArgs.data.ganttProperties.constraintType === ConstraintType.MustFinishOn && shouldRespectLink)) {
                            newArgs.validateMode.respectLink = true;
                        }
                    }
                }
                if ((newArgs.validateMode.preserveLinkWithEditing === false &&
                    newArgs.validateMode.removeLink === false &&
                    newArgs.validateMode.respectLink === false) || showDialog) {
                    this.violationArgs = {
                        matchedModifiedTask: args.data,
                        args: args
                    };
                    if (this.taskViolation && this.parent.taskFields.constraintDate && this.parent.taskFields.constraintType) {
                        validateObject['violationType'] = this.taskViolation;
                        validateObject['predecessorLink'] = true;
                    }
                    this.parent.connectorLineEditModule.openValidationDialog(validateObject);
                }
                else {
                    if (this.parent.editModule && this.parent.editModule.dialogModule &&
                        this.parent.editModule.dialogModule['isEdit'] && this.predecessorUpdated) {
                        this.isValidatedEditedRecord = true;
                        this.parent.predecessorModule.validatePredecessor(args.data, [], '');
                    }
                    this.parent.connectorLineEditModule.applyPredecessorOption();
                }
            }
            else {
                this.updateEditedTask(args);
            }
        }
        else {
            if (this.taskbarMoved) {
                this.parent.isConnectorLineUpdate = true;
            }
            this.updateEditedTask(args);
        }
    }
    /**
     *
     * @param {ITaskbarEditedEventArgs} editedEventArgs method to trigger validate predecessor link by dialog
     * @returns {IValidateArgs} .
     */
    validateTaskEvent(editedEventArgs) {
        const newArgs = {};
        this.resetValidateArgs();
        this.parent.currentEditedArgs = newArgs;
        newArgs.cancel = false;
        newArgs.data = editedEventArgs.data;
        newArgs.requestType = 'validateLinkedTask';
        newArgs.validateMode = this.parent.dialogValidateMode;
        newArgs.editEventArgs = editedEventArgs;
        this.parent.actionBeginTask(newArgs);
        return newArgs;
    }
    validateConstraintViolation(editedEventArgs) {
        const newArgs = {};
        this.resetValidateArgs();
        this.parent.currentEditedArgs = newArgs;
        newArgs.cancel = false;
        newArgs.data = editedEventArgs.data;
        newArgs.requestType = 'validateTaskViolation';
        newArgs.violationType = this.parent.constraintViolationType;
        newArgs.validateMode = this.parent.dialogValidateMode;
        newArgs.editEventArgs = editedEventArgs;
        this.parent.actionBeginTask(newArgs);
        return newArgs;
    }
    resetValidateArgs() {
        this.parent.dialogValidateMode.preserveLinkWithEditing = true;
        this.parent.dialogValidateMode.removeLink = false;
        this.parent.dialogValidateMode.respectLink = false;
        this.parent.dialogValidateMode.respectMustFinishOn = false;
        this.parent.dialogValidateMode.respectMustFinishOn = false;
        this.parent.dialogValidateMode.respectStartNoLaterThan = false;
        this.parent.dialogValidateMode.respectFinishNoLaterThan = false;
    }
    validateChildPredecessors() {
        for (let i = 0; i < this.parent.predecessorModule.validatedChildItems.length; i++) {
            const child = this.parent.predecessorModule.validatedChildItems[i];
            if (child.ganttProperties.predecessor && child.ganttProperties.predecessor.length > 0) {
                if (!this.parent.editedPredecessorRecords.some((item) => item[this.parent.taskFields.id] === child[this.parent.taskFields.id])) {
                    this.parent.editedPredecessorRecords.push(child);
                }
                if (!this.isValidatedEditedRecord) {
                    this.isFirstCall = true;
                }
                this.parent.predecessorModule.validatePredecessor(child, [], '');
            }
        }
    }
    processSuccessorChainAndChildren(currentParent) {
        const getTaskId = (rec) => this.parent.viewType === 'ResourceView'
            ? rec.ganttProperties.taskId.toString()
            : rec.ganttProperties.rowUniqueID.toString();
        const taskId = getTaskId(currentParent);
        const queue = [];
        const directSuccessors = (currentParent.ganttProperties.predecessor || [])
            .filter((data) => data.from === taskId)
            .map((data) => ({ from: data.from, to: data.to }));
        queue.push(...directSuccessors);
        const visitedSuccessors = new Set();
        while (queue.length > 0) {
            const successor = queue.shift();
            if (visitedSuccessors.has(successor.to)) {
                continue;
            }
            visitedSuccessors.add(successor.to);
            const parentGanttRecord = this.parent.connectorLineModule.getRecordByID(successor.from);
            const record = this.parent.connectorLineModule.getRecordByID(successor.to);
            if (!record) {
                continue;
            }
            this.parent.predecessorModule['validateChildGanttRecord'](parentGanttRecord, record);
            if (record.hasChildRecords && record.ganttProperties.isAutoSchedule) {
                this.parent.predecessorModule['updateChildItems'](record);
            }
            if (record.parentItem && record.ganttProperties.isAutoSchedule) {
                this.parent.dataOperation.updateParentItems(record);
            }
            // Walk up parent hierarchy
            if (record.parentItem) {
                let parent = this.parent.getRecordByID(record.parentItem.taskId);
                while (parent) {
                    if (parent.ganttProperties.predecessor && parent.ganttProperties.predecessor.length) {
                        const parentId = getTaskId(parent);
                        for (const link of parent.ganttProperties.predecessor) {
                            if (link.from === parentId) {
                                const toRecord = this.parent.connectorLineModule.getRecordByID(link.to);
                                if (toRecord) {
                                    const fromRecord = this.parent.connectorLineModule.getRecordByID(link.from);
                                    this.parent.predecessorModule['validateChildGanttRecord'](fromRecord, toRecord);
                                }
                            }
                        }
                    }
                    parent = parent.parentItem ? this.parent.getRecordByID(parent.parentItem.taskId) : null;
                }
            }
            // Walk down all children (BFS)
            if (record.hasChildRecords) {
                const childQueue = [...record.childRecords];
                const visitedChildren = new Set();
                while (childQueue.length > 0) {
                    const child = childQueue.shift();
                    const childId = getTaskId(child);
                    if (visitedChildren.has(childId)) {
                        continue;
                    }
                    visitedChildren.add(childId);
                    if (child.ganttProperties.predecessor) {
                        for (const link of child.ganttProperties.predecessor) {
                            if (link.from === childId) {
                                const toRec = this.parent.connectorLineModule.getRecordByID(link.to);
                                if (toRec) {
                                    const fromRec = this.parent.connectorLineModule.getRecordByID(link.from);
                                    this.parent.predecessorModule['validateChildGanttRecord'](fromRec, toRec);
                                    if (toRec.hasChildRecords) {
                                        this.parent.predecessorModule['updateChildItems'](toRec);
                                    }
                                }
                            }
                        }
                    }
                    if (child.hasChildRecords) {
                        childQueue.push(...child.childRecords);
                    }
                }
            }
            // Add next level successors
            if (record.ganttProperties.predecessor) {
                const currentId = getTaskId(record);
                for (const link of record.ganttProperties.predecessor) {
                    if (link.from === currentId) {
                        queue.push({ from: link.from, to: link.to });
                    }
                }
            }
        }
    }
    /**
     *
     * @param {ITaskAddedEventArgs} args - Edited event args like taskbar editing, dialog editing, cell editing
     * @returns {void} .
     * @private
     */
    updateEditedTask(args) {
        const ganttRecord = args.data;
        this.editedRecord = ganttRecord;
        if (this.parent.autoCalculateDateScheduling && !(this.parent.isLoad && this.parent.treeGrid.loadChildOnDemand &&
            this.parent.taskFields.hasChildMapping)) {
            this.updateParentChildRecord(ganttRecord);
        }
        if (this.parent.predecessorModule) {
            this.parent.predecessorModule.isValidatedParentTaskID = '';
        }
        if ((this.parent.isConnectorLineUpdate || (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'])) && this.parent.autoCalculateDateScheduling &&
            !(this.parent.isLoad && this.parent.treeGrid.loadChildOnDemand && this.parent.taskFields.hasChildMapping)) {
            /* validating predecessor for updated child items */
            this.validateChildPredecessors();
            /** validating predecessor for current edited records */
            if (ganttRecord.ganttProperties.predecessor) {
                this.parent.isMileStoneEdited = ganttRecord.ganttProperties.isMilestone;
                if (this.taskbarMoved) {
                    this.parent.editedPredecessorRecords.push(ganttRecord);
                }
                if (!this.isValidatedEditedRecord) {
                    this.isFirstCall = true;
                    if (args.action === 'DialogEditing') {
                        this.isDialogEditing = true;
                    }
                    this.parent.predecessorModule.validatePredecessor(ganttRecord, [], '');
                    this.parent.predecessorModule.isChildRecordValidated = [];
                }
                this.isValidatedEditedRecord = false;
            }
            this.parent.predecessorModule.isValidatedParentTaskID = '';
            if (this.parent.allowParentDependency && this.parent.predecessorModule.isValidatedParentTaskID !==
                ganttRecord.ganttProperties.taskId && ganttRecord.hasChildRecords &&
                (this.parent.previousRecords[ganttRecord.uniqueID].ganttProperties.startDate) && (args.action !== 'TaskbarEditing') &&
                (!ganttRecord.hasChildRecords || (ganttRecord.hasChildRecords && ganttRecord.ganttProperties.isAutoSchedule))) {
                this.parent.predecessorModule['updateChildItems'](ganttRecord);
                this.validateChildPredecessors();
            }
            this.parent.predecessorModule.isValidatedParentTaskID = '';
            if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed']) {
                for (let i = 0; i < ganttRecord.childRecords.length; i++) {
                    if (ganttRecord.childRecords[i].ganttProperties.predecessor) {
                        this.parent.predecessorModule.validatePredecessor(ganttRecord.childRecords[i], [], '');
                    }
                }
            }
            this.updateParentItemOnEditing();
            this.parent.dataOperation.updateParentItems(ganttRecord, true);
        }
        /** Update parent up-to zeroth level */
        if (ganttRecord.parentItem) {
            if (this.parent.autoCalculateDateScheduling && !(this.parent.isLoad && this.parent.treeGrid.loadChildOnDemand &&
                this.parent.taskFields.hasChildMapping)) {
                this.parent.dataOperation.updateParentItems(ganttRecord, true);
            }
            let currentParent = this.parent.getRecordByID(ganttRecord.parentItem.taskId);
            // Traverse up the hierarchy to check for parent and grandparent
            while (!isNullOrUndefined(currentParent)) {
                if (!currentParent.ganttProperties.predecessorsName) {
                    this.isFirstCall = true;
                    this.parent.predecessorModule.validatePredecessor(currentParent, [], '');
                    this.updateParentItemOnEditing();
                }
                else if (currentParent.ganttProperties.predecessor && this.parent.updateOffsetOnTaskbarEdit &&
                    ((this.parent.undoRedoModule && !this.parent.undoRedoModule['isUndoRedoPerformed']) || !this.parent.undoRedoModule)) {
                    this.processSuccessorChainAndChildren(currentParent);
                }
                // Check if the current parent has its own parent (grandparent)
                if (currentParent.parentItem) {
                    currentParent = this.parent.getRecordByID(currentParent.parentItem.taskId);
                }
                else {
                    currentParent = null; // No further parent, exit the loop
                }
            }
        }
        if (this.parent.updateOffsetOnTaskbarEdit && args.data && this.parent.taskFields.dependency) {
            this.parent.predecessorModule['validatedOffsetIds'] = [];
            this.parent.predecessorModule['calculateOffset'](args.data);
        }
        this.parent.predecessorModule['validatedParentIds'] = [];
        if (isNullOrUndefined(this.dialogModule) || (this.dialogModule && !this.dialogModule['isFromDialogPredecessor'])) {
            let predecessorLinks = args.data.ganttProperties.predecessor;
            const editedRecords = this.parent.editedRecords;
            let modifiedTask;
            let modifiedTaskIndex = -1;
            let pairedPredecessorTask;
            const constraintDateField = this.parent.taskFields.constraintDate;
            const constraintTypeField = this.parent.taskFields.constraintType;
            const constraintTypesToCheck = [
                ConstraintType.MustStartOn,
                ConstraintType.MustFinishOn,
                ConstraintType.StartNoLaterThan,
                ConstraintType.FinishNoLaterThan
            ];
            if (constraintDateField && constraintTypeField && predecessorLinks && editedRecords) {
                for (let i = 0; i < predecessorLinks.length; i++) {
                    const predecessorTaskId = predecessorLinks[i].to;
                    const taskIndex = editedRecords.findIndex((task) => task.ganttProperties.taskId.toString() === predecessorTaskId &&
                        constraintTypesToCheck.indexOf(task.ganttProperties.constraintType) > -1);
                    if (taskIndex !== -1) {
                        modifiedTask = editedRecords[taskIndex];
                        modifiedTaskIndex = taskIndex;
                        break;
                    }
                }
            }
            else if (editedRecords) {
                for (let i = 0; i < editedRecords.length; i++) {
                    const currentTask = editedRecords[i];
                    if (constraintTypesToCheck.indexOf(currentTask.ganttProperties.constraintType) !== -1) {
                        modifiedTask = currentTask;
                        break;
                    }
                }
                if (modifiedTask &&
                    modifiedTask.ganttProperties &&
                    modifiedTask.ganttProperties.predecessor) {
                    predecessorLinks = modifiedTask.ganttProperties.predecessor;
                    for (let i = 0; i < predecessorLinks.length; i++) {
                        const predecessorTaskId = predecessorLinks[i].from;
                        const taskIndex = editedRecords.findIndex((task) => task.ganttProperties.taskId.toString() === predecessorTaskId);
                        if (taskIndex !== -1) {
                            pairedPredecessorTask = editedRecords[taskIndex];
                            modifiedTaskIndex = taskIndex;
                            break;
                        }
                    }
                }
            }
            const currentTaskId = args.data.ganttProperties.taskId;
            const validationRuleDialog = this.parent.validationDialogElement
                ? this.parent.validationDialogElement.element
                : null;
            let isDialogVisible = false;
            if (validationRuleDialog) {
                isDialogVisible = !validationRuleDialog['ej2_instances'][0].dialogOpen;
            }
            if (isDialogVisible &&
                modifiedTask &&
                (modifiedTask.ganttProperties.taskId !== currentTaskId) &&
                args.taskBarEditAction !== 'ProgressResizing') {
                this.violationArgs = {
                    matchedModifiedTask: modifiedTask,
                    matchedModifiedTaskIndex: modifiedTaskIndex,
                    args: args,
                    matchedParedRecord: pairedPredecessorTask
                };
                const record = modifiedTask;
                const parentGanttRecord = pairedPredecessorTask || args.data;
                if (isNullOrUndefined(this.parent.constraintViolationType) || this.parent.constraintViolationType === '') {
                    const constraintType = modifiedTask.ganttProperties.constraintType;
                    if (constraintType === ConstraintType.MustStartOn) {
                        this.parent.constraintViolationType = 'MustStartOn';
                    }
                    else if (constraintType === ConstraintType.MustFinishOn) {
                        this.parent.constraintViolationType = 'MustFinishOn';
                    }
                    else if (constraintType === ConstraintType.StartNoLaterThan) {
                        this.parent.constraintViolationType = 'StartNoLaterThan';
                    }
                    else if (constraintType === ConstraintType.FinishNoLaterThan) {
                        this.parent.constraintViolationType = 'FinishNoLaterThan';
                    }
                }
                const validationResult = this.parent.editModule['validateConstraintViolation'](args);
                if (validationResult.violationType) {
                    const mode = validationResult.validateMode;
                    const showDialog = this.shouldShowDialog(validationResult.violationType, mode);
                    if (showDialog) {
                        this.parent.connectorLineEditModule.openConstraintValidationDialog({
                            violationType: validationResult.violationType,
                            parentRecord: parentGanttRecord,
                            record: record,
                            predecessorLink: predecessorLinks
                        });
                    }
                    else {
                        validationResult.validateMode.respectLink = true;
                        this.parent.connectorLineEditModule.applyPredecessorOption();
                    }
                }
                else {
                    this.initiateSaveAction(args);
                }
            }
            else {
                this.initiateSaveAction(args);
            }
        }
    }
    updateParentItemOnEditing() {
        const childRecord = getValue('parentRecord', this.parent.predecessorModule);
        for (let i = 0; i < childRecord.length; i++) {
            this.parent.dataOperation.updateParentItems(childRecord[i]);
        }
        if (this.parent.editModule['updateParentRecords'] && this.parent.editModule['updateParentRecords'].length > 0) {
            this.parent.editModule['updateParentRecords'].forEach((record) => {
                if (record.ganttProperties.predecessor && record.ganttProperties.predecessor.length > 0) {
                    this.parent.predecessorModule.validatePredecessor(record, [], '');
                }
            });
        }
        for (let i = 0; i < childRecord.length; i++) {
            this.parent.dataOperation.updateParentItems(childRecord[i]);
        }
        setValue('parentRecord', [], this.parent.predecessorModule);
        setValue('parentIds', [], this.parent.predecessorModule);
    }
    /**
     * To update parent records while perform drag action.
     *
     * @param {IGanttData} data .
     * @returns {void} .
     * @private
     */
    updateParentChildRecord(data) {
        const ganttRecord = data;
        if (ganttRecord.hasChildRecords && this.taskbarMoved && (ganttRecord[this.parent.taskFields.manual] === false ||
            this.parent.taskMode === 'Auto') && (!isNullOrUndefined(this.parent.editModule.cellEditModule) &&
            !this.parent.editModule.cellEditModule.isResourceCellEdited)) {
            this.parent.predecessorModule['updateChildItems'](ganttRecord);
        }
        if (!isNullOrUndefined(this.parent.editModule.cellEditModule)) {
            this.parent.editModule.cellEditModule.isResourceCellEdited = false;
        }
    }
    /**
     * To update records while changing schedule mode.
     *
     * @param {IGanttData} data .
     * @returns {void} .
     * @private
     */
    updateTaskScheduleModes(data) {
        const currentValue = data[this.parent.taskFields.startDate];
        const ganttProp = data.ganttProperties;
        if (this.parent.taskFields.manual) {
            this.parent.setRecordValue(this.parent.taskFields.manual, !data.ganttProperties.isAutoSchedule, data);
        }
        if (data.hasChildRecords && ganttProp.isAutoSchedule) {
            this.parent.setRecordValue('startDate', ganttProp.autoStartDate, ganttProp, true);
            this.parent.setRecordValue('endDate', ganttProp.autoEndDate, ganttProp, true);
            this.parent.setRecordValue('StartDate', ganttProp.autoStartDate, data, true);
            this.parent.setRecordValue('EndDate', ganttProp.autoEndDate, data, true);
            this.parent.setRecordValue('taskData.StartDate', ganttProp.autoStartDate, data, true);
            this.parent.setRecordValue('taskData.EndDate', ganttProp.autoEndDate, data, true);
            this.parent.setRecordValue('width', this.parent.dataOperation.calculateWidth(data, true), ganttProp, true);
            this.parent.setRecordValue('left', this.parent.dataOperation.calculateLeft(ganttProp, data, true), ganttProp, true);
            this.parent.setRecordValue('progressWidth', this.parent.dataOperation.getProgressWidth(ganttProp.width, ganttProp.progress), ganttProp, true);
            this.parent.dataOperation.calculateDuration(data);
        }
        else if (data.hasChildRecords && !ganttProp.isAutoSchedule) {
            this.parent.dataOperation.updateWidthLeft(data);
            this.parent.dataOperation.calculateDuration(data);
            this.parent.setRecordValue('autoStartDate', ganttProp.autoStartDate, ganttProp, true);
            this.parent.setRecordValue('autoEndDate', ganttProp.autoEndDate, ganttProp, true);
            this.parent.setRecordValue('autoDuration', this.parent.dataOperation.calculateAutoDuration(data), ganttProp, true);
            this.parent.dataOperation.updateAutoWidthLeft(data);
        }
        else {
            const startDate = this.parent.dateValidationModule.checkStartDate(currentValue, data.ganttProperties);
            this.parent.setRecordValue('startDate', startDate, data.ganttProperties, true);
            this.parent.dataOperation.updateMappingData(data, 'startDate');
            this.parent.dateValidationModule.calculateEndDate(data);
            this.parent.setRecordValue('taskData.' + this.parent.taskFields.manual, data[this.parent.taskFields.manual], data);
            this.parent.dataOperation.updateWidthLeft(data);
        }
    }
    /**
     * To update progress value of parent tasks
     *
     * @param {IParent} cloneParent .
     * @returns {void} .
     * @private
     */
    updateParentProgress(cloneParent) {
        let parentProgress = 0;
        const parent = this.parent.getParentTask(cloneParent);
        const childRecords = parent.childRecords;
        const childCount = childRecords ? childRecords.length : 0;
        let totalProgress = 0;
        let milesStoneCount = 0;
        let taskCount = 0;
        let totalDuration = 0;
        let progressValues = {};
        if (childRecords) {
            for (let i = 0; i < childCount; i++) {
                if ((!childRecords[i].ganttProperties.isMilestone || childRecords[i].hasChildRecords) &&
                    isScheduledTask(childRecords[i].ganttProperties)) {
                    progressValues = this.parent.dataOperation.getParentProgress(childRecords[i]);
                    totalProgress += getValue('totalProgress', progressValues);
                    totalDuration += getValue('totalDuration', progressValues);
                }
                else {
                    milesStoneCount += 1;
                }
            }
            taskCount = childCount - milesStoneCount;
            parentProgress = taskCount > 0 ? Math.round(totalProgress / totalDuration) : 0;
            if (isNaN(parentProgress)) {
                parentProgress = 0;
            }
            this.parent.setRecordValue('progressWidth', this.parent.dataOperation.getProgressWidth(parent.ganttProperties.isAutoSchedule ? parent.ganttProperties.width : parent.ganttProperties.autoWidth, parentProgress), parent.ganttProperties, true);
            this.parent.setRecordValue('progress', Math.floor(parentProgress), parent.ganttProperties, true);
            this.parent.setRecordValue('totalProgress', totalProgress, parent.ganttProperties, true);
            this.parent.setRecordValue('totalDuration', totalDuration, parent.ganttProperties, true);
        }
        this.parent.dataOperation.updateTaskData(parent);
        if (parent.parentItem) {
            this.updateParentProgress(parent.parentItem);
        }
    }
    /**
     * Method to revert cell edit action
     *
     * @param {object} args .
     * @returns {void} .
     * @private
     */
    // eslint-disable-next-line
    revertCellEdit(args) {
        this.parent.editModule.reUpdatePreviousRecords(false, true);
        this.resetEditProperties();
    }
    /**
     * @param {boolean} isRefreshChart .
     * @param {boolean} isRefreshGrid .
     * @param {string} key .
     * @returns {void} .
     * @private
     */
    reUpdatePreviousRecords(isRefreshChart, isRefreshGrid, key) {
        const collection = this.parent.previousRecords;
        const keys = key ? [key] : Object.keys(collection);
        for (let i = 0; i < keys.length; i++) {
            const uniqueId = keys[i];
            const prevTask = collection[uniqueId];
            if (!prevTask) {
                continue;
            }
            const originalData = this.parent.getTaskByUniqueID(uniqueId);
            this.copyTaskData(originalData.taskData, prevTask.taskData);
            delete prevTask.taskData;
            this.copyTaskData(originalData.ganttProperties, prevTask.ganttProperties);
            delete prevTask.ganttProperties;
            this.copyTaskData(originalData, prevTask);
            const rowIndex = this.parent.currentViewData.indexOf(originalData);
            if (isRefreshChart) {
                this.parent.chartRowsModule.refreshRow(rowIndex);
            }
            if (isRefreshGrid) {
                const dataId = this.parent.viewType === 'ProjectView' ? originalData.ganttProperties.taskId : originalData.ganttProperties.rowUniqueID;
                this.parent.treeGrid.setRowData(dataId, originalData);
                const row = this.parent.treeGrid.grid.getRowObjectFromUID(this.parent.treeGrid.grid.getDataRows()[rowIndex].getAttribute('data-uid'));
                row.data = originalData;
            }
        }
    }
    /**
     * Copy previous task data value to edited task data
     *
     * @param {object} existing .
     * @param {object} newValue .
     * @returns {void} .
     */
    copyTaskData(existing, newValue) {
        if (!isNullOrUndefined(newValue)) {
            extend(existing, newValue);
        }
    }
    /**
     * To update schedule date on editing.
     *
     * @param {ITaskbarEditedEventArgs} args .
     * @returns {void} .
     * @private
     */
    // eslint-disable-next-line
    updateScheduleDatesOnEditing(args) {
        //..
    }
    /**
     * @param {ITaskbarEditedEventArgs} args .
     * @returns {void} .
     * @private
     */
    initiateSaveAction(args) {
        this.parent.editModule['updateParentRecords'] = [];
        const eventArgs = {};
        eventArgs.requestType = 'beforeSave';
        eventArgs.data = args.data;
        eventArgs.cancel = false;
        this.createArray = true;
        if (this.parent.toolbarModule && this.parent.undoRedoModule && !this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent.undoRedoModule['getUndoCollection'].length > 0) {
            this.parent.toolbarModule.enableItems([this.parent.controlId + '_undo'], true); // enable toolbar items.
        }
        eventArgs.modifiedRecords = this.parent.editedRecords;
        if (!isNullOrUndefined(args.target)) {
            eventArgs.target = args.target;
        }
        eventArgs.modifiedTaskData = getTaskData(this.parent.editedRecords, true);
        if (args.action === 'DrawConnectorLine' || args.action === 'DeleteConnectorLine') {
            eventArgs.action = args.action;
            if (args.action === 'DeleteConnectorLine') {
                this.parent.connectorLineEditModule['isPublicDependencyDelete'] = false;
                if (this.parent.contextMenuModule) {
                    this.parent.contextMenuModule['isContextMenuDependencyDelete'] = false;
                }
            }
        }
        const ganttObj = this.parent;
        const currentBaselineStart = Object.assign({}, eventArgs.data.ganttProperties.baselineStartDate);
        const currentBaselineEnd = Object.assign({}, eventArgs.data.ganttProperties.baselineEndDate);
        const currentProgress = eventArgs.data.ganttProperties.progress;
        const currentDuration = eventArgs.data.ganttProperties.duration;
        const currentTaskName = eventArgs.data.ganttProperties.taskName;
        const currentStartDate = eventArgs.data.ganttProperties.startDate;
        const currentEndDate = eventArgs.data.ganttProperties.endDate;
        const currentWork = eventArgs.data.ganttProperties.work;
        const currentMilestone = eventArgs.data.ganttProperties.isMilestone;
        let isDateValidationOccurs = false;
        /* eslint-disable-next-line */
        const unModifiedData = extend({}, eventArgs.data.ganttProperties, undefined, true);
        this.parent.trigger('actionBegin', eventArgs, (eventArg) => {
            this.parent.treeGridModule.setCancelArgs = eventArg.cancel;
            const ganttProps = eventArg.data['ganttProperties'];
            const taskFields = this.parent.taskFields;
            if (currentBaselineStart !== ganttProps.baselineStartDate
                || currentBaselineEnd !== ganttProps.baselineEndDate) {
                ganttObj.setRecordValue('baselineLeft', ganttObj.dataOperation.calculateBaselineLeft(ganttProps), ganttProps, true);
                ganttObj.setRecordValue('baselineWidth', ganttObj.dataOperation.calculateBaselineWidth(ganttProps), ganttProps, true);
            }
            if (this.parent.autoCalculateDateScheduling && unModifiedData !== ganttProps &&
                !isNullOrUndefined(eventArg.data['parentItem'])) {
                this.updateParentItemOnEditing();
                this.parent.dataOperation.updateParentItems(eventArg.data, true);
            }
            const { startDate, endDate, segments, durationUnit, isAutoSchedule, isMilestone } = ganttProps;
            const prevStart = this.parent.treeGridModule['perviousStartDate'];
            const prevEnd = this.parent.treeGridModule['perviousEndDate'];
            if ((isNullOrUndefined(ganttProps.segments) || ganttProps.segments.length === 0) &&
                ((!isNullOrUndefined(prevStart) && prevStart.getTime() !== (!isNullOrUndefined(startDate) && startDate.getTime())) ||
                    (!isNullOrUndefined(prevEnd) && prevEnd.getTime() !== (!isNullOrUndefined(endDate) && endDate.getTime())))) {
                const duration = this.parent.dateValidationModule.getDuration(startDate, endDate, durationUnit, isAutoSchedule, isMilestone, true, ganttProps.calendarContext);
                if (duration !== 0) {
                    ganttProps.isMilestone = false;
                    ganttProps.duration = duration;
                }
            }
            if (!isNullOrUndefined(taskFields.milestone) && !isNullOrUndefined(currentMilestone) &&
                !isNullOrUndefined(eventArg.data[taskFields.milestone]) &&
                currentMilestone !== eventArg.data[taskFields.milestone]) {
                // To handle actionBegin true set for milestone property case
                if (eventArg.data[taskFields.milestone]) {
                    ganttProps.isMilestone = eventArg.data[taskFields.milestone];
                    ganttProps.duration = 0;
                    ganttProps.width = 0;
                    isDateValidationOccurs = true;
                }
                else {
                    ganttProps.isMilestone = eventArg.data[taskFields.milestone];
                    const prevData = this.parent.previousRecords &&
                        this.parent.previousRecords[eventArg.data.uniqueID];
                    ganttProps.duration = prevData.ganttProperties.duration;
                }
                const endDate = this.parent.dataOperation.getEndDate(ganttProps.startDate, ganttProps.duration, ganttProps.durationUnit, ganttProps, false);
                ganttProps.endDate = endDate;
            }
            if (!isNullOrUndefined(taskFields.duration) && !isNullOrUndefined(eventArg.data[taskFields.duration]) &&
                currentDuration !== eventArg.data[taskFields.duration]) {
                ganttProps.duration = eventArg.data[taskFields.duration];
                if (eventArg.data[taskFields.duration] !== 0) {
                    ganttProps.isMilestone = false;
                    if (!isNullOrUndefined(taskFields.milestone)) {
                        eventArg.data[taskFields.milestone] = false;
                    }
                }
                if (!isNullOrUndefined(taskFields.work) && !isNullOrUndefined(eventArg.data[taskFields.work])) {
                    this.parent.editModule.updateResourceRelatedFields(eventArg.data, 'duration');
                }
                const endDate = this.parent.dataOperation.getEndDate(ganttProps.startDate, ganttProps.duration, ganttProps.durationUnit, ganttProps, false);
                ganttProps.endDate = endDate;
                isDateValidationOccurs = true;
            }
            if (!isNullOrUndefined(taskFields.name) && currentTaskName !== eventArg.data[taskFields.name]) {
                ganttProps.taskName = eventArg.data[taskFields.name];
            }
            if (!isNullOrUndefined(taskFields.startDate) && !isNullOrUndefined(currentStartDate) &&
                !isNullOrUndefined(eventArg.data[taskFields.startDate]) &&
                currentStartDate.getTime() !== eventArg.data[taskFields.startDate].getTime()) {
                const startDate = this.parent.dateValidationModule.checkStartDate(eventArg.data[taskFields.startDate], ganttProps);
                ganttProps.startDate = startDate;
                isDateValidationOccurs = true;
            }
            if (!isNullOrUndefined(taskFields.endDate) && !isNullOrUndefined(currentEndDate) &&
                !isNullOrUndefined(eventArg.data[taskFields.endDate]) &&
                currentEndDate.getTime() !== eventArg.data[taskFields.endDate].getTime()) {
                const endDate = this.parent.dateValidationModule.checkEndDate(eventArg.data[taskFields.endDate], ganttProps);
                ganttProps.endDate = endDate;
                const duration = this.parent.dataOperation.getDuration(ganttProps.startDate, endDate, ganttProps.durationUnit, true, false, true, ganttProps.calendarContext);
                ganttProps.duration = duration;
                if (ganttProps.duration !== 0) {
                    ganttProps.isMilestone = false;
                }
                isDateValidationOccurs = true;
            }
            if (!isNullOrUndefined(taskFields.work) && !isNullOrUndefined(currentWork) &&
                !isNullOrUndefined(eventArg.data[taskFields.work]) &&
                currentWork !== eventArg.data[taskFields.work]) {
                ganttProps.work = eventArg.data[taskFields.work];
                this.parent.editModule.updateResourceRelatedFields(eventArg.data, 'work');
                const endDate = this.parent.dataOperation.getEndDate(ganttProps.startDate, ganttProps.duration, ganttProps.durationUnit, ganttProps, false);
                ganttProps.endDate = endDate;
                isDateValidationOccurs = true;
            }
            if (isDateValidationOccurs) {
                this.parent.predecessorModule.validatePredecessor(eventArg.data, [], '');
                isDateValidationOccurs = false;
            }
            this.parent.dataOperation.updateWidthLeft(eventArg.data);
            if (!isNullOrUndefined(taskFields.progress) && !isNullOrUndefined(eventArg.data[taskFields.progress]) &&
                currentProgress !== eventArg.data[taskFields.progress]) {
                const width = ganttProps.isAutoSchedule ? ganttProps.width :
                    ganttProps.autoWidth;
                this.parent.setRecordValue('progressWidth', this.parent.dataOperation.getProgressWidth(width, eventArg.data[taskFields.progress]), ganttProps, true);
                if (eventArg.data[taskFields.progress] > 100) {
                    eventArg.data[taskFields.progress] = 100;
                }
                ganttProps.progress = eventArg.data[taskFields.progress];
            }
            this.parent.chartRowsModule.updateSegment(ganttProps.segments, eventArg.data['ganttProperties'].taskId);
            if (eventArg.cancel) {
                this.reUpdatePreviousRecords();
                this.parent.chartRowsModule.refreshRecords([args.data]);
                this.resetEditProperties(eventArgs);
                // Trigger action complete event with save canceled request type
            }
            else {
                // To update task data for modified records.
                this.parent['showLoadingIndicator']();
                if (eventArg.modifiedRecords) {
                    for (let i = 0; i < eventArg.modifiedRecords.length; i++) {
                        if (this.parent.enableWBS) {
                            this.parent['updateWBSPredecessor'](eventArg.modifiedRecords[i]);
                        }
                        this.parent.dataOperation.updateTaskData(eventArg.modifiedRecords[i]);
                    }
                }
                eventArg.modifiedTaskData = getTaskData(eventArg.modifiedRecords, null, null, this.parent);
                if (isRemoteData(this.parent.dataSource)) {
                    const data = this.parent.dataSource;
                    const updatedData = {
                        changedRecords: eventArg.modifiedTaskData
                    };
                    const query = this.parent.query instanceof Query ? this.parent.query : new Query();
                    let crud = null;
                    const dataAdaptor = data.adaptor;
                    if (!(dataAdaptor instanceof WebApiAdaptor && dataAdaptor instanceof ODataAdaptor) || data.dataSource.batchUrl) {
                        crud = data.saveChanges(updatedData, this.parent.taskFields.id, null, query);
                    }
                    else {
                        const changedRecords = 'changedRecords';
                        crud = data.update(this.parent.taskFields.id, updatedData[changedRecords], null, query);
                    }
                    crud.then((e) => this.dmSuccess(e, args))
                        .catch((e) => this.dmFailure(e, args));
                }
                else {
                    this.saveSuccess(args);
                }
                if (this.parent.enableVirtualization && this.parent.enableTimelineVirtualization) {
                    this.parent.ganttChartModule['setVirtualHeight']();
                }
                if (this.parent.undoRedoModule) {
                    this.parent.previousFlatData = extend([], this.parent.flatData, [], true);
                }
            }
        });
        this.parent.ganttChartModule.reRenderConnectorLines();
    }
    processCustomDateColumns(sourceRecord, targetRecord, parentContext, operation) {
        if (!parentContext.customColumns || parentContext.customColumns.length === 0) {
            return;
        }
        parentContext.customColumns.forEach((column) => {
            const value = sourceRecord[column];
            if (value instanceof Date) {
                const processedDate = parentContext.dateValidationModule[operation](value, parentContext.timezone);
                targetRecord[column] = processedDate;
            }
        });
    }
    processStandardDateFields(sourceRecord, targetRecord, parentContext, operation) {
        if (!parentContext.timezone) {
            return;
        }
        const taskFields = parentContext.taskFields;
        const fields = [
            taskFields.startDate,
            taskFields.endDate,
            taskFields.baselineStartDate,
            taskFields.baselineEndDate
        ];
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            if (field && sourceRecord[field] && sourceRecord[field] instanceof Date) {
                targetRecord[field] =
                    parentContext.dateValidationModule[operation](sourceRecord[field], parentContext.timezone);
            }
        }
    }
    /* eslint-disable-next-line */
    updateEditedFields(e) {
        let eLength;
        let rec;
        if (e.changedRecords) {
            eLength = e.changedRecords['length'];
        }
        else {
            eLength = e['length'];
        }
        for (let i = 0; i < eLength; i++) {
            if (e.changedRecords) {
                rec = e.changedRecords[parseInt(i.toString(), 10)];
            }
            else {
                rec = e[parseInt(i.toString(), 10)];
            }
            if (this.parent.timezone) {
                this.processStandardDateFields(rec, rec, this.parent, 'convert');
            }
            this.processCustomDateColumns(rec, rec, this.parent, 'convert');
            const _aLength = Object.keys(rec).length;
            for (let j = 0, _a = Object.keys(rec); j < _aLength; j++) {
                const key = _a[parseInt(j.toString(), 10)];
                this.parent.editedRecords[parseInt(i.toString(), 10)][`${key}`] = rec[`${key}`];
                this.parent.editedRecords[parseInt(i.toString(), 10)].taskData[`${key}`] = rec[`${key}`];
            }
            this.updateEditedRecordFields(rec, this.parent.editedRecords[parseInt(i.toString(), 10)]);
        }
    }
    /* eslint-disable-next-line */
    dmSuccess(e, args) {
        this.updateEditedFields(e);
        this.saveSuccess(args);
    }
    updateEditedRecordFields(rec, editedRecord) {
        const fields = this.parent.taskFields;
        const _aLength = Object.keys(rec).length;
        for (let j = 0, _a = Object.keys(rec); j < _aLength; j++) {
            const key = _a[parseInt(j.toString(), 10)];
            editedRecord[`${key}`] = rec[`${key}`];
        }
        if (fields.id !== null) {
            editedRecord.ganttProperties['taskId'] = rec[fields.id];
        }
        if (fields.name !== null) {
            editedRecord.ganttProperties['taskName'] = rec[fields.name];
        }
        if (fields.startDate !== null) {
            editedRecord.ganttProperties['startDate'] = rec[fields.startDate];
        }
        if (fields.endDate !== null) {
            editedRecord.ganttProperties['endDate'] = rec[fields.endDate];
        }
        if (fields.duration !== null) {
            editedRecord.ganttProperties['duration'] = parseFloat(rec[fields.duration]);
        }
        if (fields.durationUnit !== null) {
            editedRecord.ganttProperties['durationUnit'] = rec[fields.durationUnit];
        }
        if (fields.progress !== null) {
            editedRecord.ganttProperties['progress'] = rec[fields.progress];
        }
        if (fields.dependency !== null) {
            editedRecord.ganttProperties['dependency'] = rec[fields.dependency];
        }
        if (fields.parentID !== null) {
            editedRecord.ganttProperties['parentID'] = rec[fields.parentID];
        }
        if (fields.baselineEndDate !== null) {
            editedRecord.ganttProperties['baselineEndDate'] = rec[fields.baselineEndDate];
        }
        if (fields.baselineStartDate !== null) {
            editedRecord.ganttProperties['baselineStartDate'] = rec[fields.baselineStartDate];
        }
        if (fields.resourceInfo !== null) {
            editedRecord.ganttProperties['resources'] = rec[fields.resourceInfo];
        }
    }
    dmFailure(e, args) {
        if (this.deletedTaskDetails.length) {
            const deleteRecords = this.deletedTaskDetails;
            for (let d = 0; d < deleteRecords.length; d++) {
                deleteRecords[d].isDelete = false;
            }
            this.deletedTaskDetails = [];
        }
        this.reUpdatePreviousRecords(true, true);
        this.resetEditProperties();
        this.parent.trigger('actionFailure', { error: e });
    }
    updateSharedTask(data) {
        const ids = data.ganttProperties.sharedTaskUniqueIds;
        for (let i = 0; i < ids.length; i++) {
            const editRecord = this.parent.flatData[this.parent.ids.indexOf(ids[i].toString())];
            if (editRecord && editRecord.uniqueID !== data.uniqueID) {
                this.updateGanttProperties(data, editRecord);
                this.parent.setRecordValue('taskData', data.taskData, editRecord, true);
                this.parent.dataOperation.updateTaskData(editRecord);
                this.parent.dataOperation.updateResourceName(editRecord);
                if (!isNullOrUndefined(editRecord.parentItem)) {
                    this.parent.dataOperation.updateParentItems(editRecord.parentItem);
                }
            }
        }
    }
    /**
     * Method for save action success for local and remote data
     *
     * @param {ITaskAddedEventArgs} args .
     * @returns {void} .
     */
    saveSuccess(args) {
        const eventArgs = {};
        if (this.parent.timelineSettings.updateTimescaleView) {
            const tempArray = this.parent.editedRecords;
            this.parent.timelineModule.updateTimeLineOnEditing([tempArray], args.action);
        }
        if (this.parent.viewType === 'ResourceView') {
            if (args.action === 'TaskbarEditing' || args.action === 'DrawConnectorLine' || args.action === 'DeleteConnectorLine') {
                this.updateSharedTask(args.data);
            }
            else if (args.action === 'DialogEditing' || args.action === 'CellEditing' || args.action === 'methodUpdate') {
                if (this.parent.editModule.dialogModule.isResourceUpdate) {
                    /* eslint-disable-next-line */
                    this.updateResoures(this.parent.editModule.dialogModule.previousResource, args.data.ganttProperties.resourceInfo, args.data);
                    this.updateSharedTask(args.data);
                    this.isTreeGridRefresh = true;
                }
                else {
                    this.updateSharedTask(args.data);
                }
            }
            this.parent.editModule.dialogModule.isResourceUpdate = false;
            this.parent.editModule.dialogModule.previousResource = [];
        }
        // method to update the edited parent records
        for (let k = 0; k < this.updateParentRecords.length; k++) {
            this.parent.dataOperation.updateParentItems(this.updateParentRecords[k]);
        }
        this.updateParentRecords = [];
        const criticalModule = this.parent.criticalPathModule;
        if (this.parent.enableCriticalPath && criticalModule && criticalModule.criticalPathCollection) {
            criticalModule.showCriticalPath(true);
            criticalModule.criticalConnectorLine(criticalModule.criticalPathCollection, criticalModule.detailPredecessorCollection, true, criticalModule.predecessorCollectionTaskIds);
        }
        if (!this.isTreeGridRefresh) {
            if (this.parent.editSettings.allowEditing && this.parent.treeGrid.element.getElementsByClassName('e-editedbatchcell').length > 0) {
                if (!this.parent.treeGrid.grid.element.querySelector('form').ej2_instances[0].validate()) {
                    setValue('isEdit', false, this.parent.treeGrid.grid);
                    this.parent.editModule.cellEditModule.isCellEdit = false;
                }
                this.parent.treeGrid.endEdit();
            }
            this.parent.chartRowsModule.refreshRecords(this.parent.editedRecords);
            if (!this.parent.allowTaskbarOverlap && this.parent.showOverAllocation) {
                this.parent.contentHeight = this.parent['element'].getElementsByClassName('e-content')[0].children[0]['offsetHeight'];
                this.parent.ganttChartModule.chartBodyContent.style.height = this.parent.contentHeight + 'px';
                this.parent.ganttChartModule.renderRangeContainer(this.parent.currentViewData);
                if (this.parent.taskFields.dependency) {
                    this.parent.ganttChartModule.reRenderConnectorLines();
                }
            }
            if ((this.parent.isConnectorLineUpdate || (this.parent.undoRedoModule && this.parent.undoRedoModule['currentAction'] &&
                this.parent.undoRedoModule['currentAction']['connectedRecords'])) && !isNullOrUndefined(this.parent.connectorLineEditModule)) {
                this.parent.updatedConnectorLineCollection = [];
                this.parent.connectorLineIds = [];
                this.parent.connectorLineEditModule.refreshEditedRecordConnectorLine(this.parent.editedRecords);
                this.updateScheduleDatesOnEditing(args);
            }
        }
        if (!this.parent.editSettings.allowTaskbarEditing || (this.parent.editSettings.allowTaskbarEditing &&
            !this.taskbarEditModule.dependencyCancel)) {
            eventArgs.requestType = 'save';
            eventArgs.data = args.data;
            eventArgs.modifiedRecords = this.parent.editedRecords;
            eventArgs.modifiedTaskData = getTaskData(this.parent.editedRecords, null, null, this.parent);
            // Prevent currentviewdata index update action while allowTaskbarDragAndDrop is enabled- issue-942005
            if (!this.parent.allowTaskbarDragAndDrop && this.parent.rowDragAndDropModule &&
                this.parent.rowDragAndDropModule['dropPosition'] !== 'middleSegment') {
                this.updateRowIndex();
            }
            if (!isNullOrUndefined(args.action)) {
                setValue('action', args.action, eventArgs);
            }
            if (args.action === 'TaskbarEditing') {
                eventArgs.taskBarEditAction = args.taskBarEditAction;
            }
            this.endEditAction(args);
            this.parent.trigger('actionComplete', eventArgs);
            if (this.parent.allowTaskbarDragAndDrop && this.parent.rowDragAndDropModule && this.parent.rowDragAndDropModule['draggedRecord']) {
                this.parent.rowDragAndDropModule['draggedRecord'] = null;
            }
            this.parent['hideLoadingIndicator']();
        }
        else {
            this.taskbarEditModule.dependencyCancel = false;
            this.resetEditProperties();
            this.parent['hideLoadingIndicator']();
        }
        if (this.parent.viewType === 'ResourceView' && this.isTreeGridRefresh) {
            this.parent.treeGrid.parentData = [];
            this.parent.updatedConnectorLineCollection = [];
            this.parent.connectorLineIds = [];
            this.parent.predecessorModule.createConnectorLinesCollection(this.parent.flatData);
            if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed']) {
                this.parent.undoRedoModule['isPreventRowDeselectOnUndoRedo'] = true;
            }
            this.parent.treeGrid.refresh();
            this.isTreeGridRefresh = false;
        }
        this.parent.editedRecords = [];
    }
    updateResoures(prevResource, currentResource, updateRecord) {
        const flatRecords = this.parent.flatData;
        const currentLength = currentResource ? currentResource.length : 0;
        const previousLength = prevResource ? prevResource.length : 0;
        if (currentLength === 0 && previousLength === 0) {
            return;
        }
        for (let index = 0; index < currentLength; index++) {
            const recordIndex = [];
            let resourceID = parseInt(currentResource[index][this.parent.resourceFields.id], 10).toString();
            if (resourceID === 'NaN') {
                resourceID = currentResource[index][this.parent.resourceFields.id];
            }
            if (!isNullOrUndefined(prevResource)) {
                for (let i = 0; i < prevResource.length; i++) {
                    let prevResourceID = parseInt(prevResource[i][this.parent.resourceFields.id], 10).toString();
                    if (prevResourceID === 'NaN') {
                        prevResourceID = prevResource[i][this.parent.resourceFields.id];
                    }
                    if (prevResourceID === resourceID) {
                        recordIndex.push(i);
                        break;
                    }
                }
            }
            if (recordIndex.length === 0) {
                const parentRecord = flatRecords[this.parent.getTaskIds().indexOf('R' + resourceID)];
                if (parentRecord) {
                    this.addNewRecord(updateRecord, parentRecord);
                }
            }
            else {
                let record1 = parseInt(recordIndex[0].toString(), 10);
                if (record1.toString() === 'NaN') {
                    record1 = recordIndex[0].toString();
                }
                prevResource.splice(record1, 1);
            }
        }
        const prevLength = prevResource ? prevResource.length : 0;
        for (let index = 0; index < prevLength; index++) {
            const taskID = updateRecord.ganttProperties.taskId;
            const resourceID = prevResource[index][this.parent.resourceFields.id];
            const record = flatRecords[this.parent.getTaskIds().indexOf('R' + resourceID)];
            if (!isNullOrUndefined(record)) {
                for (let j = 0; j < record.childRecords.length; j++) {
                    if (record.childRecords[j].ganttProperties.taskId === taskID) {
                        this.removeChildRecord(record.childRecords[j]);
                    }
                }
            }
        }
        if (currentLength > 0) {
            const parentTask = this.parent.getParentTask(updateRecord.parentItem);
            if (parentTask) {
                if (parentTask.ganttProperties.taskName === this.parent.localeObj.getConstant('unassignedTask')) {
                    this.removeChildRecord(updateRecord);
                }
            }
        }
        //Assign resource to unassigned task
        if (currentLength === 0) {
            this.checkWithUnassignedTask(updateRecord);
        }
        if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent.flatData[this.parent.flatData.length - 1].ganttProperties.taskName === this.parent.localeObj.getConstant('unassignedTask')
            && !this.isFlatDataHaveUnAsignedTask) {
            this.parent.removeRecords(this.parent.flatData.length - 1);
            this.parent.currentViewData.splice(this.parent.currentViewData.length - 1, 1);
            this.parent.taskIds.splice(this.parent.flatData.length - 1, 1);
            this.parent.ids.splice(this.parent.flatData.length - 1, 1);
        }
        this.isFlatDataHaveUnAsignedTask = false;
    }
    /**
     * @param {IGanttData} updateRecord .
     * @returns {void} .
     * @private
     */
    checkWithUnassignedTask(updateRecord) {
        let unassignedTasks = null;
        // Block for check the unassigned task.
        for (let i = 0; i < this.parent.flatData.length; i++) {
            if (this.parent.flatData[i].ganttProperties.taskName === this.parent.localeObj.getConstant('unassignedTask')) {
                unassignedTasks = this.parent.flatData[i];
            }
        }
        if (!isNullOrUndefined(unassignedTasks)) {
            this.addNewRecord(updateRecord, unassignedTasks);
            this.isFlatDataHaveUnAsignedTask = true;
        }
        else {
            // Block for create the unassigned task.
            const unassignTaskObj = {};
            unassignTaskObj[this.parent.taskFields.id] = 0;
            unassignTaskObj[this.parent.taskFields.name] = this.parent.localeObj.getConstant('unassignedTask');
            const beforeEditStatus = this.parent.isOnEdit;
            this.parent.isOnEdit = false;
            const cAddedRecord = this.parent.dataOperation.createRecord(unassignTaskObj, 0);
            this.parent.isOnEdit = beforeEditStatus;
            this.addRecordAsBottom(cAddedRecord);
            const parentRecord = this.parent.flatData[this.parent.flatData.length - 1];
            const index = this.parent.flatData.indexOf(parentRecord);
            const childIndex = (index + 1) + parentRecord.childRecords.length;
            updateRecord.index = childIndex;
            updateRecord.level = parentRecord.level + 1;
            this.addNewRecord(updateRecord, parentRecord);
        }
        const updatedData = this.parent.currentViewData.filter((data) => {
            return (data.ganttProperties.taskId === updateRecord.ganttProperties.taskId &&
                (data.hasChildRecords === updateRecord.hasChildRecords));
        })[0];
        updateRecord.parentItem = updatedData.parentItem;
        updateRecord.parentUniqueID = updatedData.parentUniqueID;
    }
    addRecordAsBottom(cAddedRecord) {
        const recordIndex1 = this.parent.flatData.length;
        this.parent.currentViewData.splice(recordIndex1 + 1, 0, cAddedRecord);
        this.parent.insertRecord(cAddedRecord, recordIndex1 + 1);
        this.parent.ids.splice(recordIndex1 + 1, 0, cAddedRecord.ganttProperties.rowUniqueID.toString());
        const taskId = cAddedRecord.level === 0 ? 'R' + cAddedRecord.ganttProperties.taskId : 'T' + cAddedRecord.ganttProperties.taskId;
        this.parent.getTaskIds().splice(recordIndex1 + 1, 0, taskId);
        this.updateTreeGridUniqueID(cAddedRecord, 'add');
    }
    addNewRecord(updateRecord, parentRecord) {
        let cAddedRecord = null;
        cAddedRecord = extend({}, {}, updateRecord, true);
        this.parent.setRecordValue('uniqueID', this.parent.dataOperation['getGanttUid'](this.parent.element.id + '_data_'), cAddedRecord);
        this.parent.setRecordValue('uniqueID', cAddedRecord.uniqueID, cAddedRecord.ganttProperties, true);
        const uniqueId = cAddedRecord.uniqueID.replace(this.parent.element.id + '_data_', '');
        this.parent.setRecordValue('rowUniqueID', uniqueId, cAddedRecord);
        this.parent.setRecordValue('rowUniqueID', uniqueId, cAddedRecord.ganttProperties, true);
        this.parent.setRecordValue('level', 1, cAddedRecord);
        if (this.parent.taskFields.parentID) {
            this.parent.setRecordValue('parentId', parentRecord.ganttProperties.taskId, cAddedRecord.ganttProperties, true);
        }
        this.parent.setRecordValue('parentItem', this.parent.dataOperation.getCloneParent(parentRecord), cAddedRecord);
        const parentUniqId = cAddedRecord.parentItem ? cAddedRecord.parentItem.uniqueID : null;
        this.parent.setRecordValue('parentUniqueID', parentUniqId, cAddedRecord);
        updateRecord.ganttProperties.sharedTaskUniqueIds.push(uniqueId);
        cAddedRecord.ganttProperties.sharedTaskUniqueIds = updateRecord.ganttProperties.sharedTaskUniqueIds;
        this.addRecordAsChild(parentRecord, cAddedRecord);
    }
    removeChildRecord(record) {
        const gObj = this.parent;
        let data = [];
        if (this.parent.dataSource instanceof DataManager && this.parent.dataSource.dataSource.json.length > 0) {
            data = this.parent.dataSource.dataSource.json;
        }
        else {
            data = this.parent.currentViewData;
        }
        const dataSource = this.parent.dataSource;
        const deletedRow = record;
        const flatParentData = this.parent.getParentTask(deletedRow.parentItem);
        if (deletedRow) {
            if (deletedRow.parentItem) {
                const deleteChildRecords = flatParentData ? flatParentData.childRecords : [];
                let childIndex = 0;
                if (deleteChildRecords && deleteChildRecords.length > 0) {
                    if (deleteChildRecords.length === 1) {
                        //For updating the parent record which has no child reords.
                        this.parent.isOnDelete = true;
                        deleteChildRecords[0].isDelete = true;
                        this.parent.dataOperation.updateParentItems(flatParentData);
                        this.parent.isOnDelete = false;
                        deleteChildRecords[0].isDelete = false;
                    }
                    childIndex = deleteChildRecords.indexOf(deletedRow);
                    flatParentData.childRecords.splice(childIndex, 1);
                    // collection for updating parent record
                    this.updateParentRecords.push(flatParentData);
                }
            }
            if (deletedRow.ganttProperties.sharedTaskUniqueIds.length) {
                const uniqueIDIndex = deletedRow.ganttProperties.sharedTaskUniqueIds.indexOf(deletedRow.ganttProperties.rowUniqueID);
                deletedRow.ganttProperties.sharedTaskUniqueIds.splice(uniqueIDIndex, 1);
            }
            this.updateTreeGridUniqueID(deletedRow, 'delete');
            //method to delete the record from datasource collection
            if (!this.parent.taskFields.parentID) {
                const deleteRecordIDs = [];
                deleteRecordIDs.push(deletedRow.ganttProperties.rowUniqueID.toString());
                if (this.parent.viewType === 'ProjectView') {
                    this.parent.editModule.removeFromDataSource(deleteRecordIDs);
                }
            }
            const flatRecordIndex = this.parent.flatData.indexOf(deletedRow);
            if (gObj.taskFields.parentID) {
                let idx;
                const ganttData = this.parent.currentViewData;
                for (let i = 0; i < ganttData.length; i++) {
                    if (ganttData[i].ganttProperties.rowUniqueID === deletedRow.ganttProperties.rowUniqueID) {
                        idx = i;
                    }
                }
                if (idx !== -1) {
                    if (dataSource.length > 0) {
                        dataSource.splice(idx, 1);
                    }
                    data.splice(idx, 1);
                    this.parent.removeRecords(flatRecordIndex);
                    this.parent.ids.splice(flatRecordIndex, 1);
                    this.parent.getTaskIds().splice(flatRecordIndex, 1);
                }
            }
            const recordIndex = data.indexOf(deletedRow);
            if (!gObj.taskFields.parentID) {
                const deletedRecordCount = this.parent.editModule.getChildCount(deletedRow, 0);
                data.splice(recordIndex, deletedRecordCount + 1);
                this.parent.removeRecords(flatRecordIndex, deletedRecordCount + 1);
                this.parent.ids.splice(flatRecordIndex, deletedRecordCount + 1);
                this.parent.getTaskIds().splice(flatRecordIndex, deletedRecordCount + 1);
            }
            if (deletedRow.parentItem && flatParentData && flatParentData.childRecords && !flatParentData.childRecords.length) {
                this.parent.setRecordValue('expanded', false, flatParentData);
                this.parent.setRecordValue('hasChildRecords', false, flatParentData);
            }
        }
    }
    // Method to add new record after resource edit
    addRecordAsChild(droppedRecord, draggedRecord) {
        const gObj = this.parent;
        const recordIndex1 = this.parent.flatData.indexOf(droppedRecord);
        const childRecords = this.parent.editModule.getChildCount(droppedRecord, 0);
        let childRecordsLength;
        let spliceIndex;
        const parentTask = this.parent.getTaskByUniqueID(draggedRecord.parentItem.uniqueID);
        if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'] && this.dialogModule['indexes'] && this.dialogModule['indexes'].deletedIndexes && this.dialogModule['indexes'].deletedIndexes.length > 0) {
            if (parentTask.ganttProperties.taskName === this.parent.localeObj.getConstant('unassignedTask')) {
                childRecordsLength = this.parent.taskIds.length + 1;
            }
            else {
                for (let j = 0; j < this.dialogModule['indexes'].deletedIndexes.length; j++) {
                    if (this.dialogModule['indexes'].deletedIndexes[j].data.parentUniqueID === draggedRecord.parentUniqueID && draggedRecord.ganttProperties.taskId === this.dialogModule['indexes'].deletedIndexes[j].data.ganttProperties.taskId) {
                        const toIndex = this.dialogModule['indexes'].deletedIndexes[j].index;
                        if (this.dialogModule['indexes'].deletedIndexes[j].position === 'above') {
                            (childRecordsLength = toIndex);
                        }
                        else {
                            (childRecordsLength = toIndex + 1);
                        }
                        for (let i = 0; i < droppedRecord.childRecords.length; i++) {
                            if ('T' + droppedRecord.childRecords[i].ganttProperties.taskId === this.dialogModule['indexes'].deletedIndexes[j].id) {
                                if (this.dialogModule['indexes'].deletedIndexes[j].position === 'above') {
                                    spliceIndex = i;
                                }
                                else {
                                    spliceIndex = i + 1;
                                }
                                break;
                            }
                        }
                        break;
                    }
                    else if (this.dialogModule['indexes'].deletedIndexes[j].data.parentUniqueID !== draggedRecord.parentUniqueID && draggedRecord.ganttProperties.taskId === this.dialogModule['indexes'].deletedIndexes[j].data.ganttProperties.taskId) {
                        const draggedParent = this.parent.getTaskByUniqueID(draggedRecord.parentItem.uniqueID);
                        childRecordsLength = draggedParent.index + draggedParent.childRecords.length + 1;
                        break;
                    }
                }
            }
        }
        else if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'] && parentTask.ganttProperties.taskName === this.parent.localeObj.getConstant('unassignedTask') && this.parent.undoRedoModule['currentAction']['deletedRecordsDetails']) {
            for (let i = 0; i < this.parent.undoRedoModule['currentAction']['deletedRecordsDetails'].length; i++) {
                if (this.parent.undoRedoModule['currentAction']['deletedRecordsDetails'][i].data.ganttProperties.taskId === draggedRecord.ganttProperties.taskId) {
                    if (parentTask.index) {
                        childRecordsLength = this.parent.undoRedoModule['currentAction']['deletedRecordsDetails'][i].data.index;
                        spliceIndex = childRecordsLength - parentTask.index - 1;
                    }
                    else {
                        childRecordsLength = (isNullOrUndefined(childRecords) ||
                            childRecords === 0) ? recordIndex1 + 1 :
                            childRecords + recordIndex1 + 1;
                        spliceIndex = 0;
                    }
                    break;
                }
            }
        }
        else if (!isNullOrUndefined(this.addRowIndex) && this.addRowPosition && droppedRecord.childRecords && this.addRowPosition !== 'Child') {
            const dropChildRecord = droppedRecord.childRecords[this.addRowIndex];
            const position = this.addRowPosition === 'Above' || this.addRowPosition === 'Below' ? this.addRowPosition :
                'Child';
            childRecordsLength = dropChildRecord ? this.addRowIndex + recordIndex1 + 1 :
                childRecords + recordIndex1 + 1;
            childRecordsLength = position === 'Above' ? childRecordsLength : childRecordsLength + 1;
        }
        else {
            childRecordsLength = (isNullOrUndefined(childRecords) ||
                childRecords === 0) ? recordIndex1 + 1 :
                childRecords + recordIndex1 + 1;
        }
        //this.ganttData.splice(childRecordsLength, 0, this.draggedRecord);
        this.parent.currentViewData.splice(childRecordsLength, 0, draggedRecord);
        this.parent.insertRecord(draggedRecord, childRecordsLength);
        this.parent.ids.splice(childRecordsLength, 0, draggedRecord.ganttProperties.rowUniqueID.toString());
        this.updateTreeGridUniqueID(draggedRecord, 'add');
        const recordId = draggedRecord.level === 0 ? 'R' + draggedRecord.ganttProperties.taskId : 'T' + draggedRecord.ganttProperties.taskId;
        this.parent.getTaskIds().splice(childRecordsLength, 0, recordId);
        if (!droppedRecord.hasChildRecords) {
            this.parent.setRecordValue('hasChildRecords', true, droppedRecord);
            this.parent.setRecordValue('expanded', true, droppedRecord);
            if (!droppedRecord.childRecords.length) {
                droppedRecord.childRecords = [];
                if (!gObj.taskFields.parentID && isNullOrUndefined(droppedRecord.taskData[this.parent.taskFields.child])) {
                    droppedRecord.taskData[this.parent.taskFields.child] = [];
                }
            }
        }
        if (spliceIndex >= 0) {
            droppedRecord.childRecords.splice(spliceIndex, 0, draggedRecord);
        }
        else {
            droppedRecord.childRecords.splice(droppedRecord.childRecords.length, 0, draggedRecord);
        }
        if (!isNullOrUndefined(draggedRecord) && !this.parent.taskFields.parentID
            && !isNullOrUndefined(droppedRecord.taskData[this.parent.taskFields.child])) {
            droppedRecord.taskData[this.parent.taskFields.child].splice(droppedRecord.childRecords.length, 0, draggedRecord.taskData);
        }
        if (!isNullOrUndefined(draggedRecord.parentItem)) {
            //collection to update the parent records
            this.updateParentRecords.push(droppedRecord);
        }
    }
    resetEditProperties(args) {
        this.parent.currentEditedArgs = {};
        this.resetValidateArgs();
        this.parent.isOnEdit = false;
        this.parent.predecessorModule.validatedChildItems = [];
        this.parent.isConnectorLineUpdate = false;
        this.parent.editedPredecessorRecords = [];
        this.taskbarMoved = false;
        this.predecessorUpdated = false;
        if (!isNullOrUndefined(this.dialogModule) && (isNullOrUndefined(args) ||
            (!isNullOrUndefined(args) && args['requestType'] === 'beforeSave' && !args['cancel']))) {
            if (this.dialogModule.dialog && !this.dialogModule.dialogObj.isDestroyed) {
                this.dialogModule.dialogObj.hide();
            }
            this.dialogModule.dialogClose();
        }
        this.parent.hideSpinner();
        this.parent.initiateEditAction(false);
    }
    /**
     * @param {ITaskAddedEventArgs} args .
     * @returns {void} .
     * @private
     */
    endEditAction(args) {
        this.resetEditProperties();
        if (args.action === 'TaskbarEditing') {
            args.target = this.taskbarEditModule['editElement'];
            this.parent.trigger('taskbarEdited', args);
        }
        else if (args.action === 'CellEditing') {
            this.parent.trigger('endEdit', args);
        }
        else if (args.action === 'DialogEditing') {
            if (this.dialogModule.dialog && !this.dialogModule.dialogObj.isDestroyed) {
                this.dialogModule.dialogObj.hide();
            }
            this.dialogModule.dialogClose();
        }
    }
    // eslint-disable-next-line
    saveFailed(args) {
        this.reUpdatePreviousRecords();
        this.parent.hideSpinner();
        //action failure event trigger
    }
    /**
     * To render delete confirmation dialog
     *
     * @returns {void} .
     */
    renderDeleteConfirmDialog() {
        const dialogObj = new Dialog({
            width: '320px',
            isModal: true,
            visible: false,
            enableRtl: this.parent.enableRtl,
            content: this.parent.localeObj.getConstant('confirmDelete'),
            buttons: [
                {
                    click: this.confirmDeleteOkButton.bind(this),
                    buttonModel: { content: this.parent.localeObj.getConstant('okText'), isPrimary: true }
                },
                {
                    click: this.closeConfirmDialog.bind(this),
                    buttonModel: { content: this.parent.localeObj.getConstant('cancel') }
                }
            ],
            target: this.parent.element,
            animationSettings: { effect: 'None' }
        });
        dialogObj.appendTo('#' + this.parent.element.id + '_deleteConfirmDialog');
        this.confirmDialog = dialogObj;
    }
    closeConfirmDialog() {
        this.confirmDialog.hide();
    }
    confirmDeleteOkButton() {
        this.deleteSelectedItems();
        this.confirmDialog.hide();
        const focussedElement = this.parent.element.querySelector('.e-treegrid');
        focussedElement.focus();
    }
    /**
     * @returns {void} .
     * @private
     */
    startDeleteAction() {
        if (this.parent.editSettings.allowDeleting && !this.parent.readOnly) {
            if (this.parent.editSettings.showDeleteConfirmDialog) {
                this.confirmDialog.show();
            }
            else {
                this.deleteSelectedItems();
                const focusingElement = this.parent.element.querySelector('.e-treegrid');
                focusingElement.focus();
            }
        }
    }
    /**
     *
     * @param {IGanttData[]} selectedRecords - Defines the deleted records
     * @returns {void} .
     * Method to delete the records from resource view Gantt.
     */
    deleteResourceRecords(selectedRecords) {
        const deleteRecords = [];
        for (let i = 0; i < selectedRecords.length; i++) {
            if (selectedRecords[i].parentItem) {
                if (selectedRecords[i].ganttProperties.sharedTaskUniqueIds.length === 1) {
                    const data = selectedRecords[i];
                    const ids = data.ganttProperties.sharedTaskUniqueIds;
                    for (let j = 0; j < ids.length; j++) {
                        if (this.parent.ids.indexOf(ids[j].toString()) !== -1) {
                            deleteRecords.push(this.parent.flatData[this.parent.ids.indexOf(ids[j].toString())]);
                        }
                        else if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent.undoRedoModule['currentAction'] && this.parent.undoRedoModule['currentAction']['action'] === 'Delete') {
                            deleteRecords.push(this.parent.flatData[this.parent.taskIds.indexOf('T' + selectedRecords[i].ganttProperties.taskId)]);
                        }
                    }
                    if (this.parent.ids.indexOf(data.ganttProperties.rowUniqueID) !== -1 &&
                        deleteRecords.indexOf(this.parent.flatData[this.parent.ids.indexOf(data.ganttProperties.rowUniqueID)]) === -1) {
                        deleteRecords.push(this.parent.flatData[this.parent.ids.indexOf(data.ganttProperties.rowUniqueID)]);
                    }
                }
                else if (selectedRecords[i].ganttProperties.sharedTaskUniqueIds.length > 1) {
                    selectedRecords.forEach((record) => {
                        record.ganttProperties.sharedTaskUniqueIds.forEach((uniqueId) => {
                            deleteRecords.push(this.parent.getTaskByUniqueID(this.parent.element.id + '_data_' + uniqueId));
                        });
                    });
                }
            }
            else {
                const resourceParent = this.parent.flatData.filter((data) => {
                    return (data.ganttProperties.taskId === selectedRecords[i].ganttProperties.taskId &&
                        data.hasChildRecords);
                })[0];
                if (!isNullOrUndefined(resourceParent)) {
                    deleteRecords.push(resourceParent);
                }
            }
        }
        this.deleteRow(deleteRecords);
    }
    add(record, totalRecords) {
        totalRecords.push(record);
        if (record.hasChildRecords) {
            const child = record.childRecords;
            for (let i = 0; i < child.length; i++) {
                this.add(child[i], totalRecords);
            }
        }
        else {
            if (totalRecords.indexOf(record) === -1) {
                totalRecords.push(record);
            }
        }
    }
    deleteSelectedItems() {
        if (!this.isFromDeleteMethod) {
            let selectedRecords = [];
            if (this.parent.selectionSettings.mode !== 'Cell') {
                selectedRecords = this.parent.selectionModule.getSelectedRecords();
            }
            else if (this.parent.selectionSettings.mode === 'Cell') {
                selectedRecords = this.parent.selectionModule.getCellSelectedRecords();
            }
            if (this.parent.viewType === 'ResourceView') {
                this.deleteResourceRecords(selectedRecords);
            }
            else {
                this.deleteRow(selectedRecords);
            }
        }
        else {
            if (this.targetedRecords.length) {
                if (this.parent.viewType === 'ResourceView') {
                    this.deleteResourceRecords(this.targetedRecords);
                }
                else {
                    this.deleteRow(this.targetedRecords);
                }
            }
            this.isFromDeleteMethod = false;
        }
    }
    /**
     * Method to delete record.
     *
     * @param {number | string | number[] | string[] | IGanttData | IGanttData[]} taskDetail - Defines the details of data to delete.
     * @returns {void} .
     * @public
     */
    deleteRecord(taskDetail) {
        this.isFromDeleteMethod = true;
        const variableType = typeof (taskDetail);
        this.targetedRecords = [];
        switch (variableType) {
            case 'number':
            case 'string':
                {
                    const taskId = taskDetail.toString();
                    if (this.parent.viewType === 'ResourceView') {
                        if (!isNullOrUndefined(taskId) && this.parent.getTaskIds().indexOf('T' + taskId) !== -1) {
                            this.targetedRecords.push(this.parent.flatData[this.parent.getTaskIds().indexOf('T' + taskId)]);
                        }
                    }
                    else {
                        if (!isNullOrUndefined(taskId) && this.parent.ids.indexOf(taskId) !== -1) {
                            this.targetedRecords.push(this.parent.getRecordByID(taskId));
                        }
                    }
                    break;
                }
            case 'object':
                if (!Array.isArray(taskDetail)) {
                    this.targetedRecords.push(taskDetail.valueOf());
                }
                else {
                    this.updateTargetedRecords(taskDetail);
                }
                break;
            default:
        }
        this.startDeleteAction();
    }
    /**
     * To update 'targetedRecords collection' from given array collection
     *
     * @param {object[]} taskDetailArray .
     * @returns {void} .
     */
    updateTargetedRecords(taskDetailArray) {
        if (taskDetailArray.length) {
            const variableType = typeof (taskDetailArray[0]);
            if (variableType === 'object') {
                this.targetedRecords = taskDetailArray;
            }
            else {
                // Get record from array of task ids
                for (let i = 0; i < taskDetailArray.length; i++) {
                    const id = taskDetailArray[i].toString();
                    if (this.parent.viewType === 'ResourceView') {
                        if (!isNullOrUndefined(id) && this.parent.getTaskIds().indexOf('T' + id) !== -1) {
                            this.targetedRecords.push(this.parent.flatData[this.parent.getTaskIds().indexOf('T' + id)]);
                        }
                    }
                    else if (!isNullOrUndefined(id) && this.parent.ids.indexOf(id) !== -1) {
                        this.targetedRecords.push(this.parent.getRecordByID(id));
                    }
                }
            }
        }
    }
    deleteRow(tasks) {
        let rowItems = tasks && tasks.length ? tasks :
            this.parent.selectionModule.getSelectedRecords();
        this.parent.addDeleteRecord = true;
        if (rowItems.length) {
            this.parent.isOnDelete = true;
            rowItems.forEach((item) => {
                if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed']) {
                    let rec;
                    if (this.parent.viewType === 'ProjectView') {
                        rec = this.parent.getRecordByID(item.ganttProperties.taskId);
                    }
                    else {
                        rec = this.parent.flatData[this.parent.taskIds.indexOf((item.hasChildRecords ? 'R' : 'T') + item.ganttProperties.taskId)];
                    }
                    rec.isDelete = true;
                }
                item.isDelete = true;
            });
            if (this.parent.viewType === 'ResourceView' && !tasks.length) {
                rowItems = [];
            }
            if (this.parent.undoRedoModule && !this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent['isUndoRedoItemPresent']('Delete')) {
                if (this.parent.undoRedoModule['redoEnabled'] && !this.parent.undoRedoModule['isUndoRedoPerformed']) {
                    this.parent.undoRedoModule['disableRedo']();
                    this.parent.undoRedoModule['getUndoCollection'][0] = [];
                }
                if (!this.parent.undoRedoModule['isUndoRedoPerformed']) {
                    this.parent.undoRedoModule['createUndoCollection']();
                }
                const records = {};
                records['action'] = 'Delete';
                records['deletedRecordsDetails'] = [];
                this.parent.undoRedoModule['findPosition'](extend([], [], rowItems, true), records, 'deletedRecordsDetails');
                this.parent.undoRedoModule['getUndoCollection'][this.parent.undoRedoModule['getUndoCollection'].length - 1] = records;
            }
            for (let i = 0; i < rowItems.length; i++) {
                const deleteRecord = rowItems[i];
                if (this.deletedTaskDetails.indexOf(deleteRecord) !== -1) {
                    continue;
                }
                let deleteItems = [deleteRecord];
                if (this.parent.viewType === 'ResourceView' && this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'] && rowItems[i].ganttProperties.resourceInfo && rowItems[i].ganttProperties.resourceInfo.length > 1) {
                    deleteItems = [];
                    if (!rowItems[i].hasChildRecords) {
                        const id = 'T' + rowItems[i].ganttProperties.taskId;
                        this.parent.taskIds.reduce(function (e, j) {
                            if (e === id) {
                                deleteItems.push(this.parent.flatData[j]);
                            }
                        }.bind(this), []);
                    }
                }
                for (let j = 0; j < deleteItems.length; j++) {
                    const parentTask = this.parent.getParentTask(deleteItems[j].parentItem);
                    if (deleteItems[j].parentItem) {
                        let childRecord;
                        if (!isNullOrUndefined(parentTask)) {
                            childRecord = parentTask.childRecords;
                            const filteredRecord = childRecord.length === 1 ?
                                childRecord : childRecord.filter((data) => {
                                return !data.isDelete;
                            });
                            if (filteredRecord.length > 0) {
                                this.parent.dataOperation.updateParentItems(deleteItems[j].parentItem);
                            }
                        }
                    }
                    const predecessor = deleteItems[j].ganttProperties.predecessor;
                    let canDeletePredecessor = true;
                    if (this.parent.viewType === 'ResourceView' && parentTask && parentTask.ganttProperties.taskName !==
                        this.parent.localeObj.getConstant('unassignedTask')) {
                        canDeletePredecessor = false;
                    }
                    if (predecessor && predecessor.length && canDeletePredecessor) {
                        this.removePredecessorOnDelete(deleteItems[j]);
                    }
                    this.deletedTaskDetails.push(deleteItems[j]);
                    if (deleteItems[j].hasChildRecords) {
                        this.deleteChildRecords(deleteItems[j]);
                    }
                }
            }
            if (this.parent.selectionModule && this.parent.allowSelection) {
                // clear selection
                this.parent.selectionModule.clearSelection();
            }
            const delereArgs = {};
            delereArgs.deletedRecordCollection = this.deletedTaskDetails;
            delereArgs.updatedRecordCollection = this.parent.editedRecords;
            delereArgs.cancel = false;
            delereArgs.action = 'delete';
            this.initiateDeleteAction(delereArgs);
            this.parent.isOnDelete = false;
        }
        if (!isNullOrUndefined(this.parent.toolbarModule)) {
            this.parent.toolbarModule.refreshToolbarItems();
        }
    }
    removePredecessorOnDelete(record) {
        const predecessors = record.ganttProperties.predecessor;
        for (let i = 0; i < predecessors.length; i++) {
            const predecessor = predecessors[i];
            const recordId = this.parent.viewType === 'ResourceView' ? record.ganttProperties.taskId :
                record.ganttProperties.rowUniqueID;
            const isMiddleDrop = (this.parent.rowDragAndDropModule && this.parent.rowDragAndDropModule['dropPosition'] === 'middleSegment') ||
                (this.dropPosition && this.dropPosition === 'middleSegment');
            const isChildToMatch = record.hasChildRecords && record.childRecords.some((child) => {
                return predecessor.to.toString() === child[this.parent.taskFields.id].toString();
            });
            const isChildFromMatch = record.hasChildRecords && record.childRecords.some((child) => {
                return predecessor.from.toString() === child[this.parent.taskFields.id].toString();
            });
            if (predecessor.from.toString() === recordId.toString() &&
                ((isChildToMatch && isMiddleDrop) || this.parent.isOnDelete)) {
                const toRecord = this.parent.connectorLineModule.getRecordByID(predecessor.to.toString());
                if (!isNullOrUndefined(toRecord)) {
                    const toRecordPredcessor = extend([], [], toRecord.ganttProperties.predecessor, true);
                    let index;
                    for (let t = 0; t < toRecordPredcessor.length; t++) {
                        const toId = this.parent.viewType === 'ResourceView' ? toRecord.ganttProperties.taskId :
                            toRecord.ganttProperties.rowUniqueID;
                        if (toRecordPredcessor[t].to.toString() === toId.toString()
                            && toRecordPredcessor[t].from.toString() === recordId.toString()) {
                            index = t;
                            break;
                        }
                    }
                    toRecordPredcessor.splice(index, 1);
                    this.updatePredecessorValues(toRecord, toRecordPredcessor);
                }
            }
            else if (predecessor.to.toString() === recordId.toString() &&
                ((isChildFromMatch && isMiddleDrop) || this.parent.isOnDelete)) {
                const fromRecord = this.parent.connectorLineModule.getRecordByID(predecessor.from.toString());
                if (!isNullOrUndefined(fromRecord)) {
                    const fromRecordPredcessor = extend([], [], fromRecord.ganttProperties.predecessor, true);
                    let index;
                    for (let t = 0; t < fromRecordPredcessor.length; t++) {
                        const fromId = this.parent.viewType === 'ResourceView' ? fromRecord.ganttProperties.taskId :
                            fromRecord.ganttProperties.rowUniqueID;
                        if (fromRecordPredcessor[t].from.toString() === fromId.toString()
                            && fromRecordPredcessor[t].to.toString() === recordId.toString()) {
                            index = t;
                            break;
                        }
                    }
                    if (record.uniqueID === fromRecord.parentUniqueID ||
                        record.parentUniqueID === fromRecord.uniqueID || this.parent.isOnDelete) {
                        fromRecordPredcessor.splice(index, 1);
                        this.updatePredecessorValues(fromRecord, fromRecordPredcessor);
                    }
                }
            }
        }
    }
    updatePredecessorValues(record, predcessorArray) {
        this.parent.setRecordValue('predecessor', predcessorArray, record.ganttProperties, true);
        const predecessorString = this.parent.predecessorModule.getPredecessorStringValue(record);
        this.parent.setRecordValue('predecessorsName', predecessorString, record.ganttProperties, true);
        this.parent.setRecordValue('taskData.' + this.parent.taskFields.dependency, predecessorString, record);
        this.parent.setRecordValue(this.parent.taskFields.dependency, predecessorString, record);
    }
    /**
     * Method to update TaskID of a gantt record
     *
     * @param {string | number} currentId .
     * @param {number | string} newId .
     * @returns {void} .
     */
    updateTaskId(currentId, newId) {
        if (!this.parent.readOnly) {
            const cId = typeof currentId === 'number' ? currentId.toString() : currentId;
            const nId = typeof newId === 'number' ? newId.toString() : newId;
            const ids = this.parent.ids;
            if (!isNullOrUndefined(cId) && !isNullOrUndefined(nId)) {
                const cIndex = ids.indexOf(cId);
                const nIndex = ids.indexOf(nId);
                // return false for invalid taskID
                if (cIndex === -1 || nIndex > -1) {
                    return;
                }
                const thisRecord = this.parent.flatData[cIndex];
                if (!isNullOrUndefined(thisRecord)) {
                    this.parent.setRecordValue('taskId', newId, thisRecord.ganttProperties, true);
                    this.parent.setRecordValue('rowUniqueID', nId, thisRecord.ganttProperties, true);
                    this.parent.setRecordValue(this.parent.taskFields.id, newId, thisRecord, true);
                    this.parent.setRecordValue(this.parent.taskFields.id, newId, thisRecord.taskData, true);
                }
                ids[cIndex] = nId;
                if (thisRecord.hasChildRecords && this.parent.taskFields.parentID) {
                    const childRecords = thisRecord.childRecords;
                    for (let count = 0; count < childRecords.length; count++) {
                        const childRecord = childRecords[count];
                        childRecord[this.parent.taskFields.parentID] = newId;
                        this.parent.chartRowsModule.refreshRecords([childRecord]);
                    }
                }
                if (this.parent.taskFields.dependency && !isNullOrUndefined(thisRecord.ganttProperties.predecessor)) {
                    const predecessors = thisRecord.ganttProperties.predecessor;
                    let currentGanttRecord;
                    for (let i = 0; i < predecessors.length; i++) {
                        const predecessor = predecessors[i];
                        if (predecessor.to === cId) {
                            currentGanttRecord = this.parent.flatData[ids.indexOf(predecessor.from)];
                        }
                        else if (predecessor.from === cId) {
                            currentGanttRecord = this.parent.flatData[ids.indexOf(predecessor.to)];
                        }
                        this.updatePredecessorOnUpdateId(currentGanttRecord, cId, nId);
                    }
                }
                this.parent.treeGrid.parentData = [];
                this.parent.treeGrid.refresh();
            }
        }
    }
    updatePredecessorOnUpdateId(currentGanttRecord, cId, nId) {
        if (this.parent.flatData.indexOf(currentGanttRecord) > -1) {
            const pred = currentGanttRecord.ganttProperties.predecessor;
            for (let j = 0; j < pred.length; j++) {
                const pre = pred[j];
                if (pre.to === cId) {
                    pre.to = nId;
                }
                else if (pre.from === cId) {
                    pre.from = nId;
                }
            }
        }
        this.updatePredecessorValues(currentGanttRecord, currentGanttRecord.ganttProperties.predecessor);
    }
    deleteChildRecords(record) {
        const childRecords = record.childRecords;
        for (let c = 0; c < childRecords.length; c++) {
            const childRecord = childRecords[c];
            if (this.deletedTaskDetails.indexOf(childRecord) !== -1) {
                continue;
            }
            const predecessor = childRecord.ganttProperties.predecessor;
            let canDeletePredecessor = true;
            const parentTask = this.parent.getParentTask(childRecord.parentItem);
            if (this.parent.viewType === 'ResourceView' && parentTask && parentTask.ganttProperties.taskName !==
                this.parent.localeObj.getConstant('unassignedTask')) {
                canDeletePredecessor = false;
            }
            if (predecessor && predecessor.length && canDeletePredecessor) {
                this.removePredecessorOnDelete(childRecord);
            }
            if (!isNullOrUndefined(childRecord.ganttProperties.sharedTaskUniqueIds) &&
                childRecord.ganttProperties.sharedTaskUniqueIds.length > 1) {
                childRecord.ganttProperties.sharedTaskUniqueIds.forEach((uniqueId) => {
                    this.deletedTaskDetails.push(this.parent.getTaskByUniqueID(this.parent.element.id + '_data_' + uniqueId));
                });
            }
            else {
                this.deletedTaskDetails.push(childRecord);
            }
            if (childRecord.hasChildRecords) {
                this.deleteChildRecords(childRecord);
            }
        }
    }
    removeFromDataSource(deleteRecordIDs) {
        let dataSource;
        if (this.parent.dataSource instanceof DataManager) {
            dataSource = this.parent.dataSource.dataSource.json;
        }
        else {
            dataSource = this.parent.dataSource;
        }
        this.removeData(dataSource, deleteRecordIDs);
        this.isBreakLoop = false;
    }
    removeData(dataCollection, record) {
        for (let i = 0; i < dataCollection.length; i++) {
            if (this.isBreakLoop) {
                break;
            }
            if (record.indexOf(getValue(this.parent.taskFields.id, dataCollection[i]).toString()) !== -1) {
                if (dataCollection[i][this.parent.taskFields.child]) {
                    const childRecords = dataCollection[i][this.parent.taskFields.child];
                    this.removeData(childRecords, record);
                }
                record.splice(record.indexOf(getValue(this.parent.taskFields.id, dataCollection[i]).toString()), 1);
                dataCollection.splice(i, 1);
                if (record.length === 0) {
                    this.isBreakLoop = true;
                    break;
                }
            }
            else if (dataCollection[i][this.parent.taskFields.child]) {
                const childRecords = dataCollection[i][this.parent.taskFields.child];
                this.removeData(childRecords, record);
            }
        }
    }
    initiateDeleteAction(args) {
        this.parent.showSpinner();
        const eventArgs = {};
        eventArgs.requestType = 'beforeDelete';
        eventArgs.data = args.deletedRecordCollection;
        eventArgs.modifiedRecords = args.updatedRecordCollection;
        eventArgs.modifiedTaskData = getTaskData(args.updatedRecordCollection, null, null, this.parent);
        this.parent.trigger('actionBegin', eventArgs, (eventArg) => {
            if (eventArg.cancel) {
                const deleteRecords = this.deletedTaskDetails;
                for (let d = 0; d < deleteRecords.length; d++) {
                    deleteRecords[d].isDelete = false;
                }
                this.deletedTaskDetails = [];
                this.reUpdatePreviousRecords();
                this.parent.initiateEditAction(false);
                this.parent.hideSpinner();
            }
            else {
                this.parent['showLoadingIndicator']();
                if (isRemoteData(this.parent.dataSource)) {
                    const data = this.parent.dataSource;
                    if (this.parent.timezone) {
                        eventArg.modifiedRecords.forEach((modifiedRecord) => {
                            updateDates(modifiedRecord, this.parent);
                        });
                    }
                    const updatedData = {
                        deletedRecords: getTaskData(eventArg.data, null, null, this.parent),
                        changedRecords: eventArg.modifiedTaskData
                    };
                    const adaptor = data.adaptor;
                    const query = this.parent.query instanceof Query ? this.parent.query : new Query();
                    if (!(adaptor instanceof WebApiAdaptor && adaptor instanceof ODataAdaptor) || data.dataSource.batchUrl) {
                        const crud = data.saveChanges(updatedData, this.parent.taskFields.id, null, query);
                        crud.then(() => this.deleteSuccess(args))
                            .catch((e) => this.dmFailure(e, args));
                    }
                    else {
                        const deletedRecords = 'deletedRecords';
                        let deleteCrud = null;
                        for (let i = 0; i < updatedData[deletedRecords].length; i++) {
                            deleteCrud = data.remove(this.parent.taskFields.id, updatedData[deletedRecords][i], null, query);
                        }
                        deleteCrud.then(() => {
                            const changedRecords = 'changedRecords';
                            for (let i = updatedData[changedRecords].length - 1; i >= 0; i--) {
                                if (updatedData['deletedRecords'].some((record) => record[this.parent.taskFields.id] === updatedData[changedRecords][i].taskId)) {
                                    updatedData[changedRecords].splice(i, 1);
                                }
                            }
                            const updateCrud = data.update(this.parent.taskFields.id, updatedData[changedRecords], null, query);
                            updateCrud.then(() => this.deleteSuccess(args))
                                .catch((e) => this.dmFailure(e, args));
                        }).catch((e) => this.dmFailure(e, args));
                    }
                }
                else {
                    this.deleteSuccess(args);
                }
            }
        });
    }
    deleteSuccess(args) {
        const flatData = this.parent.flatData;
        const currentData = this.parent.currentViewData;
        const deletedRecords = [];
        for (let i = 0; i < args.deletedRecordCollection.length; i++) {
            if (this.parent.viewType === 'ProjectView') {
                const record = this.parent.getRecordByID(args.deletedRecordCollection[i].ganttProperties.taskId);
                if (!isNullOrUndefined(record)) {
                    deletedRecords.push(record);
                }
            }
            else {
                let id;
                const hasChildRecords = args.deletedRecordCollection[i].hasChildRecords;
                const isUnassignedTask = args.deletedRecordCollection[i].ganttProperties.taskName === this.parent.localeObj.getConstant('unassignedTask');
                const isCollapsed = !args.deletedRecordCollection[i].expanded;
                if (hasChildRecords && isUnassignedTask && isCollapsed) {
                    this.parent['hideLoadingIndicator']();
                    this.deletedTaskDetails = [];
                    this.parent.initiateEditAction(false);
                    return;
                }
                if (args.deletedRecordCollection[i].hasChildRecords) {
                    id = 'R' + args.deletedRecordCollection[i].ganttProperties.taskId;
                }
                else {
                    id = 'T' + args.deletedRecordCollection[i].ganttProperties.taskId;
                }
                deletedRecords.push(this.parent.getTaskByUniqueID(args.deletedRecordCollection[i].uniqueID));
            }
        }
        const deleteRecordIDs = [];
        if (deletedRecords.length > 0) {
            this.parent['regenerateCycles'] = true;
            let recordIndex;
            let record;
            if (this.parent.enableVirtualization) {
                recordIndex = this.parent.flatData.indexOf(deletedRecords[deletedRecords.length - 1]);
                this.parent.staticSelectedRowIndex = this.parent.selectedRowIndex = recordIndex;
                record = this.parent.flatData[this.parent.selectedRowIndex + 1];
                // perform toolbar CollapseAll later delete action issue handle
                if (!this.parent.isCollapseAll) {
                    // Random parent record delete action, maintaining proper record selection
                    this.parent.staticSelectedRowIndex = (recordIndex - (deletedRecords.length - 1));
                }
            }
            else {
                recordIndex = this.parent.currentViewData.indexOf(deletedRecords[deletedRecords.length - 1]);
                this.parent.staticSelectedRowIndex = this.parent.selectedRowIndex = recordIndex;
                record = this.parent.currentViewData[this.parent.selectedRowIndex + 1];
            }
            if (!isNullOrUndefined(record)) {
                this.parent.currentSelection = record;
            }
            else {
                const prevRecordIndex = this.parent.selectedRowIndex - 1;
                if (prevRecordIndex >= 0) {
                    this.parent.currentSelection = this.parent.enableVirtualization ?
                        this.parent.flatData[prevRecordIndex] : this.parent.currentViewData[prevRecordIndex];
                }
            }
        }
        this.deletedRecord = deletedRecords;
        for (let i = 0; i < deletedRecords.length; i++) {
            const deleteRecord = deletedRecords[i];
            let currentIndex;
            let flatIndex;
            let updateParentIndex;
            if (this.parent.viewType === 'ResourceView') {
                if (deleteRecord.hasChildRecords) {
                    currentIndex = this.parent.taskIds.indexOf('R' + deleteRecord.ganttProperties.taskId.toString());
                    flatIndex = this.parent.taskIds.indexOf('R' + deleteRecord.ganttProperties.taskId.toString());
                }
                else {
                    currentIndex = this.parent.taskIds.indexOf('T' + deleteRecord.ganttProperties.taskId.toString());
                    flatIndex = this.parent.taskIds.indexOf('T' + deleteRecord.ganttProperties.taskId.toString());
                }
            }
            else {
                currentIndex = currentData.indexOf(deleteRecord);
                flatIndex = flatData.indexOf(deleteRecord);
                if (this.parent.editModule && this.parent.editModule['updateParentRecords'] && this.parent.editModule['updateParentRecords'].length > 0) {
                    updateParentIndex = this.parent.editModule['updateParentRecords'].indexOf(deleteRecord);
                }
            }
            const treeGridParentIndex = this.parent.treeGrid.parentData.indexOf(deleteRecord);
            const tempData = getValue('dataOperation.dataArray', this.parent);
            const dataIndex = tempData.indexOf(deleteRecord.taskData);
            let childIndex;
            if (currentIndex !== -1) {
                currentData.splice(currentIndex, 1);
            }
            if (flatIndex !== -1) {
                this.parent.removeRecords(flatIndex);
            }
            if (dataIndex !== -1) {
                tempData.splice(dataIndex, 1);
            }
            if (typeof updateParentIndex === 'number' && updateParentIndex !== -1) {
                this.parent.editModule['updateParentRecords'].splice(updateParentIndex, 1);
            }
            if (!isNullOrUndefined(deleteRecord)) {
                deleteRecordIDs.push(deleteRecord.ganttProperties.taskId.toString());
                if (flatIndex !== -1) {
                    this.parent.ids.splice(flatIndex, 1);
                    if (this.parent.viewType === 'ResourceView') {
                        this.parent.getTaskIds().splice(flatIndex, 1);
                        if (!deleteRecord.hasChildRecords) {
                            deleteRecord.ganttProperties.resourceInfo = null;
                            delete deleteRecord.ganttProperties.resourceNames;
                            deleteRecord[this.parent.taskFields.resourceInfo] = null;
                            deleteRecord.ganttProperties.sharedTaskUniqueIds = [];
                            delete deleteRecord.taskData[this.parent.taskFields.resourceInfo];
                        }
                    }
                }
                if (deleteRecord.level === 0 && treeGridParentIndex !== -1) {
                    this.parent.treeGrid.parentData.splice(treeGridParentIndex, 1);
                }
                if (deleteRecord.parentItem) {
                    const parentItem = this.parent.getParentTask(deleteRecord.parentItem);
                    if (parentItem) {
                        const childRecords = parentItem.childRecords;
                        childIndex = childRecords.indexOf(deleteRecord);
                        if (childIndex !== -1) {
                            childRecords.splice(childIndex, 1);
                        }
                        if (!childRecords.length) {
                            this.parent.setRecordValue('hasChildRecords', false, parentItem);
                        }
                    }
                }
                this.updateTreeGridUniqueID(deleteRecord, 'delete');
            }
        }
        if (deleteRecordIDs.length > 0) {
            this.removeFromDataSource(deleteRecordIDs);
        }
        this.updateRowIndex();
        const eventArgs = {};
        this.parent.updatedConnectorLineCollection = [];
        this.parent.connectorLineIds = [];
        this.parent.predecessorModule.createConnectorLinesCollection(this.parent.flatData);
        this.parent.treeGrid.parentData = [];
        this.isAdded = false;
        if (this.parent.treeGrid.editModule) {
            this.parent.treeGrid.editModule['isOnBatch'] = false;
        }
        this.parent.treeGrid.refresh();
        if (this.parent.enableImmutableMode) {
            this.refreshRecordInImmutableMode();
        }
        // Trigger actioncomplete event for delete action
        eventArgs.requestType = 'delete';
        eventArgs.data = args.deletedRecordCollection;
        eventArgs.modifiedRecords = args.updatedRecordCollection;
        eventArgs.modifiedTaskData = getTaskData(args.updatedRecordCollection, null, null, this.parent);
        setValue('action', args.action, eventArgs);
        this.parent.isOnDelete = false;
        if (this.parent.viewType === 'ResourceView' && (!this.parent.undoRedoModule || (this.parent.undoRedoModule && (!this.parent.undoRedoModule['isUndoRedoPerformed'] || (this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent.undoRedoModule['currentAction']['action'] === 'Delete'))))) {
            const updateUnAssignedResources = eventArgs.data.filter((data) => {
                return !data.hasChildRecords;
            });
            for (let i = 0; i < updateUnAssignedResources.length; i++) {
                let unassignedTask = this.parent.flatData.filter((data) => {
                    return data.ganttProperties.taskName === this.parent.localeObj.getConstant('unassignedTask');
                })[0];
                let isDuplicate = [];
                if (unassignedTask) {
                    isDuplicate = unassignedTask.childRecords.filter((data) => {
                        return data.ganttProperties.taskId === updateUnAssignedResources[i].ganttProperties.taskId;
                    });
                }
                const parentTask = this.parent.getParentTask(updateUnAssignedResources[i].parentItem);
                if (parentTask && parentTask.ganttProperties.taskName !==
                    this.parent.localeObj.getConstant('unassignedTask') && isDuplicate.length === 0) {
                    this.checkWithUnassignedTask(updateUnAssignedResources[i]);
                }
                else if (!parentTask && (!isDuplicate || isDuplicate.length === 0)) {
                    this.checkWithUnassignedTask(updateUnAssignedResources[i]);
                }
                unassignedTask = this.parent.flatData.filter((data) => {
                    return data.ganttProperties.taskName === this.parent.localeObj.getConstant('unassignedTask');
                })[0];
                const parentItem = this.parent.currentViewData.filter((data) => {
                    if (data.ganttProperties.taskId === updateUnAssignedResources[i].ganttProperties.taskId &&
                        (!data.hasChildRecords && data.parentItem)
                        && unassignedTask.uniqueID === data.parentItem.uniqueID) {
                        return data;
                    }
                    else {
                        return null;
                    }
                });
                if (parentItem[0]) {
                    this.parent.dataOperation.updateParentItems(parentItem[0]);
                }
            }
        }
        this.parent.trigger('actionComplete', eventArgs);
        this.deletedTaskDetails = [];
        this.parent.initiateEditAction(false);
        this.parent['hideLoadingIndicator']();
        this.parent.hideSpinner();
    }
    /**
     * Generates a new task ID for a Gantt chart component, ensuring it's unique within the current context.
     * @returns {number | string} - The new unique task ID.
     * @private
     */
    getNewTaskId() {
        let newTaskId;
        const ids = this.parent.viewType === 'ResourceView' ? this.parent.getTaskIds() : this.parent.ids;
        const maxId = ids.length;
        if (this.parent.dataOperation['isTaskIDInteger'] && this.parent.ids.length > 0) {
            newTaskId = Math.max(...this.parent.ids.map((id) => Number(id)));
        }
        else {
            newTaskId = maxId + 1;
        }
        if (this.parent.viewType === 'ResourceView') {
            if (ids.indexOf('T' + newTaskId) !== -1 || ids.indexOf('R' + newTaskId) !== -1) {
                newTaskId = newTaskId + 1;
                if (ids.indexOf('T' + newTaskId) !== -1 || ids.indexOf('R' + newTaskId) !== -1) {
                    do {
                        newTaskId = newTaskId + 1;
                    } while (ids.indexOf('T' + newTaskId) !== -1 || ids.indexOf('R' + newTaskId) !== -1);
                }
            }
        }
        else {
            if (ids.indexOf(newTaskId.toString()) !== -1) {
                newTaskId = newTaskId + 1;
                if (ids.indexOf(newTaskId.toString()) !== -1) {
                    do {
                        newTaskId = newTaskId + 1;
                    } while (ids.indexOf(newTaskId.toString()) !== -1);
                }
            }
        }
        if (this.parent.columnByField[this.parent.taskFields.id].editType === 'stringedit') {
            return newTaskId = newTaskId.toString();
        }
        else {
            return newTaskId;
        }
    }
    /**
     * @param {object} obj .
     * @param {RowPosition} rowPosition .
     * @returns {void} .
     * @private
     */
    // eslint-disable-next-line
    prepareNewlyAddedData(obj, rowPosition) {
        const taskModel = this.parent.taskFields;
        let id;
        let newTaskIDmd;
        const ids = this.parent.ids;
        /*Validate Task Id of data*/
        if (obj[taskModel.id]) {
            if (ids.indexOf(obj[taskModel.id].toString()) !== -1) {
                obj[taskModel.id] = null;
            }
            else {
                if (typeof (obj[taskModel.id]) === 'string') {
                    newTaskIDmd = obj[taskModel.id];
                }
                else {
                    newTaskIDmd = parseInt(obj[taskModel.id], 10);
                }
                obj[taskModel.id] = isNullOrUndefined(newTaskIDmd) ? null : newTaskIDmd;
            }
        }
        if (!obj[taskModel.id]) {
            id = this.getNewTaskId();
            obj[taskModel.id] = id;
        }
        if (!this.parent.allowUnscheduledTasks && !obj[taskModel.startDate]) {
            obj[taskModel.startDate] = this.parent.projectStartDate;
        }
        if (!this.parent.allowUnscheduledTasks && taskModel.duration && isNullOrUndefined(obj[taskModel.duration])) {
            if (!obj[taskModel.endDate]) {
                obj[taskModel.duration] = '5';
            }
        }
        if (taskModel.progress) {
            obj[taskModel.progress] = obj[taskModel.progress] ? (obj[taskModel.progress] > 100 ? 100 : obj[taskModel.progress]) : 0;
        }
        if (!this.parent.allowUnscheduledTasks && !obj[taskModel.endDate] && taskModel.endDate) {
            if (!obj[taskModel.duration]) {
                const startDate = this.parent.dataOperation.getDateFromFormat(this.parent.projectStartDate);
                if (!isNullOrUndefined(startDate)) {
                    startDate.setDate(startDate.getDate() + 4);
                    obj[taskModel.endDate] = this.parent.getFormatedDate(startDate, this.parent.getDateFormat());
                }
            }
        }
    }
    processPredecessors(record, isUndoRedoAction) {
        if (record.ganttProperties.predecessorsName) {
            if (!this.parent.undoRedoModule || !this.parent.undoRedoModule['isUndoRedoPerformed']) {
                this.parent.predecessorModule.ensurePredecessorCollectionHelper(record, record.ganttProperties);
            }
            this.parent.predecessorModule.updatePredecessorHelper(record);
            this.parent.predecessorModule.validatePredecessorDates(record);
        }
        if (record.childRecords && record.childRecords.length > 0 && isUndoRedoAction) {
            for (let i = 0; i < record.childRecords.length; i++) {
                this.processPredecessors(record.childRecords[i], isUndoRedoAction);
            }
        }
    }
    /**
     * @param {object} obj .
     * @param {number} level .
     * @param {RowPosition} rowPosition .
     * @param {IGanttData} parentItem .
     * @param {number} rowIndex .
     * @returns {IGanttData} .
     * @private
     */
    updateNewlyAddedDataBeforeAjax(obj, level, rowPosition, parentItem, rowIndex) {
        const cAddedRecord = this.parent.dataOperation.createRecord(obj, level);
        switch (rowPosition) {
            case 'Above':
                cAddedRecord.index = rowIndex;
                break;
            case 'Below':
            case 'Child':
                cAddedRecord.index = rowIndex + 1;
                break;
            case 'Bottom':
                cAddedRecord.index = this.parent.enableVirtualization ? this.parent.flatData.length : this.parent.currentViewData.length;
                break;
            case 'Top':
                cAddedRecord.index = 0;
                break;
            default:
                break;
        }
        if (!isNullOrUndefined(parentItem)) {
            this.parent.setRecordValue('parentItem', this.parent.dataOperation.getCloneParent(parentItem), cAddedRecord);
            const pIndex = cAddedRecord.parentItem ? cAddedRecord.parentItem.index : null;
            this.parent.setRecordValue('parentIndex', pIndex, cAddedRecord);
            const parentUniqId = cAddedRecord.parentItem ? cAddedRecord.parentItem.uniqueID : null;
            this.parent.setRecordValue('parentUniqueID', parentUniqId, cAddedRecord);
            if (!isNullOrUndefined(this.parent.taskFields.id) &&
                !isNullOrUndefined(this.parent.taskFields.parentID) && cAddedRecord.parentItem) {
                if (this.parent.viewType === 'ProjectView') {
                    this.parent.setRecordValue(this.parent.taskFields.parentID, cAddedRecord.parentItem.taskId, cAddedRecord.taskData, true);
                }
                this.parent.setRecordValue('parentId', cAddedRecord.parentItem.taskId, cAddedRecord.ganttProperties, true);
                this.parent.setRecordValue(this.parent.taskFields.parentID, cAddedRecord.parentItem.taskId, cAddedRecord, true);
            }
        }
        this.parent.isOnEdit = true;
        if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed']) {
            if (obj['hasChildRecords']) {
                const totalRecords = [];
                const currentAction = this.parent.undoRedoModule['currentAction']['deletedRecordsDetails'];
                for (let i = 0; i < obj['childRecords'].length; i++) {
                    this.add(obj['childRecords'][i], totalRecords);
                }
                for (let j = 0; j < currentAction.length; j++) {
                    if (obj['ganttProperties'].taskId === currentAction[j]['data'].ganttProperties.taskId) {
                        if (currentAction[j]['position'] === 'child') {
                            rowPosition = 'Child';
                        }
                        else if (currentAction[j]['position'] === 'below') {
                            rowPosition = 'Below';
                        }
                        else if (currentAction[j]['position'] === 'above') {
                            rowPosition = 'Above';
                        }
                        this.backUpAndPushNewlyAddedRecord(cAddedRecord, rowPosition, parentItem);
                        for (let i = 0; i < totalRecords.length; i++) {
                            if (this.parent.viewType === 'ProjectView') {
                                if (totalRecords[i].parentItem.taskId === cAddedRecord.ganttProperties.taskId) {
                                    totalRecords[i].parentItem.index = cAddedRecord.index;
                                    totalRecords[i].parentItem.taskId = cAddedRecord.ganttProperties.taskId;
                                    totalRecords[i].parentItem.uniqueID = cAddedRecord.ganttProperties.uniqueID;
                                    totalRecords[i].parentUniqueID = cAddedRecord.ganttProperties.uniqueID;
                                }
                            }
                            else {
                                totalRecords[i].parentItem.index = cAddedRecord.index;
                                totalRecords[i].parentItem.taskId = cAddedRecord.ganttProperties.rowUniqueID;
                                totalRecords[i].parentItem.uniqueID = cAddedRecord.ganttProperties.uniqueID;
                                totalRecords[i].parentUniqueID = cAddedRecord.ganttProperties.uniqueID;
                            }
                        }
                        break;
                    }
                }
                this.addRowSelectedItem = cAddedRecord;
                cAddedRecord.taskData[this.parent.taskFields.child] = [];
                for (let k = 0; k < totalRecords.length; k++) {
                    this.parent.isOnEdit = false;
                    const newRecord = this.parent.dataOperation.createRecord(totalRecords[k], totalRecords[k].level);
                    if (newRecord.childRecords.length === 0 && newRecord.taskData[this.parent.taskFields.child]) {
                        newRecord.taskData[this.parent.taskFields.child] = [];
                    }
                    parentItem = this.parent.getRecordByID(totalRecords[k].parentItem.taskId);
                    if (!isNullOrUndefined(parentItem)) {
                        this.parent.setRecordValue('parentItem', this.parent.dataOperation.getCloneParent(parentItem), newRecord);
                        const pIndex = newRecord.parentItem ? newRecord.parentItem.index : null;
                        this.parent.setRecordValue('parentIndex', pIndex, newRecord);
                        const parentUniqId = newRecord.parentItem ? newRecord.parentItem.uniqueID : null;
                        this.parent.setRecordValue('parentUniqueID', parentUniqId, newRecord);
                        if (!isNullOrUndefined(this.parent.taskFields.id) &&
                            !isNullOrUndefined(this.parent.taskFields.parentID) && newRecord.parentItem) {
                            if (this.parent.viewType === 'ProjectView') {
                                this.parent.setRecordValue(this.parent.taskFields.parentID, newRecord.parentItem.taskId, newRecord.taskData, true);
                            }
                            this.parent.setRecordValue('parentId', newRecord.parentItem.taskId, newRecord.ganttProperties, true);
                            this.parent.setRecordValue(this.parent.taskFields.parentID, newRecord.parentItem.taskId, newRecord, true);
                        }
                    }
                    this.parent.isOnEdit = true;
                    if (newRecord.parentItem) {
                        if (parentItem.childRecords.length === 0) {
                            rowPosition = 'Child';
                        }
                        else {
                            rowPosition = 'Below';
                        }
                    }
                    if (this.parent.getParentTask(newRecord.parentItem).childRecords.length > 0) {
                        this.addRowSelectedItem = this.parent.getParentTask(newRecord.parentItem).childRecords[this.parent.getParentTask(newRecord.parentItem).childRecords.length - 1];
                    }
                    else {
                        this.addRowSelectedItem = this.parent.getParentTask(newRecord.parentItem);
                    }
                    this.backUpAndPushNewlyAddedRecord(newRecord, rowPosition, parentItem);
                    for (let i = 0; i < totalRecords.length; i++) {
                        if (this.parent.viewType === 'ProjectView') {
                            if (totalRecords[i].parentItem.taskId === newRecord.ganttProperties.taskId) {
                                totalRecords[i].parentItem.index = newRecord.index;
                                totalRecords[i].parentItem.taskId = newRecord.ganttProperties.taskId;
                                totalRecords[i].parentItem.uniqueID = newRecord.ganttProperties.uniqueID;
                                totalRecords[i].parentUniqueID = newRecord.ganttProperties.uniqueID;
                            }
                        }
                        else {
                            totalRecords[i].parentItem.index = cAddedRecord.index;
                            totalRecords[i].parentItem.taskId = cAddedRecord.ganttProperties.rowUniqueID;
                            totalRecords[i].parentItem.uniqueID = cAddedRecord.ganttProperties.uniqueID;
                            totalRecords[i].parentUniqueID = cAddedRecord.ganttProperties.uniqueID;
                        }
                    }
                }
            }
            else {
                this.backUpAndPushNewlyAddedRecord(cAddedRecord, rowPosition, parentItem);
            }
        }
        else {
            this.backUpAndPushNewlyAddedRecord(cAddedRecord, rowPosition, parentItem);
        }
        // need to push in dataSource also.
        if (this.parent.taskFields.dependency) {
            const isUndoRedoAction = this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'] ? true : false;
            if (cAddedRecord.ganttProperties.predecessorsName) {
                this.processPredecessors(cAddedRecord, isUndoRedoAction);
            }
            else if (!cAddedRecord.ganttProperties.predecessorsName && cAddedRecord.childRecords.length > 0 && isUndoRedoAction) {
                for (let i = 0; i < cAddedRecord.childRecords.length; i++) {
                    this.processPredecessors(cAddedRecord.childRecords[i], isUndoRedoAction);
                }
            }
        }
        if (cAddedRecord.parentItem && this.parent.getParentTask(cAddedRecord.parentItem).ganttProperties.isAutoSchedule) {
            this.parent.dataOperation.updateParentItems(cAddedRecord.parentItem);
        }
        this.parent.isOnEdit = false;
        return cAddedRecord;
    }
    /**
     * @param {IGanttData} record .
     * @param {number} count .
     * @returns {number} .
     * @private
     */
    getChildCount(record, count) {
        let currentRecord;
        if (!record.hasChildRecords) {
            return 0;
        }
        for (let i = 0; i < record.childRecords.length; i++) {
            currentRecord = record.childRecords[i];
            count++;
            if (currentRecord.hasChildRecords) {
                count = this.getChildCount(currentRecord, count);
            }
        }
        return count;
    }
    /**
     * @param {IGanttData} data .
     * @param {number} count .
     * @param {IGanttData[]} collection .
     * @returns {number} .
     * @private
     */
    getVisibleChildRecordCount(data, count, collection) {
        let childRecords;
        let length;
        if (data.hasChildRecords) {
            childRecords = data.childRecords;
            length = childRecords.length;
            for (let i = 0; i < length; i++) {
                if (collection.indexOf(childRecords[i]) !== -1) {
                    count++;
                }
                if (childRecords[i].hasChildRecords) {
                    count = this.getVisibleChildRecordCount(childRecords[i], count, collection);
                }
            }
        }
        else {
            if (collection.indexOf(data) !== -1) {
                count++;
            }
        }
        return count;
    }
    /**
     * @param {IGanttData} parentRecord .
     * @returns {void} .
     * @private
     */
    updatePredecessorOnIndentOutdent(parentRecord) {
        const len = parentRecord.ganttProperties.predecessor.length;
        const parentRecordTaskData = parentRecord.ganttProperties;
        const predecessorCollection = parentRecordTaskData.predecessor;
        let childRecord;
        let predecessorIndex;
        const updatedPredecessor = [];
        let validPredecessor;
        for (let count = 0; count < len; count++) {
            const fromRecord = this.parent.getRecordByID(predecessorCollection[count].from);
            const toRecord = this.parent.getRecordByID(predecessorCollection[count].to);
            validPredecessor = this.parent.predecessorModule.validateParentPredecessor(fromRecord, toRecord);
            if (!validPredecessor || !this.parent.allowParentDependency) {
                if (predecessorCollection[count].to === parentRecordTaskData.rowUniqueID.toString()) {
                    childRecord = this.parent.getRecordByID(predecessorCollection[count].from);
                    predecessorIndex = getIndex(predecessorCollection[count], 'from', childRecord.ganttProperties.predecessor, 'to');
                    // eslint-disable-next-line
                    let predecessorCollections = (extend([], childRecord.ganttProperties.predecessor, [], true));
                    predecessorCollections.splice(predecessorIndex, 1);
                    this.parent.setRecordValue('predecessor', predecessorCollections, childRecord.ganttProperties, true);
                }
                else if (predecessorCollection[count].from === parentRecordTaskData.rowUniqueID.toString()) {
                    childRecord = this.parent.getRecordByID(predecessorCollection[count].to);
                    const prdcList = (childRecord.ganttProperties.predecessorsName.toString()).split(',');
                    const str = predecessorCollection[count].from + predecessorCollection[count].type;
                    const ind = prdcList.indexOf(str);
                    prdcList.splice(ind, 1);
                    this.parent.setRecordValue('predecessorsName', prdcList.join(','), childRecord.ganttProperties, true);
                    this.parent.setRecordValue(this.parent.taskFields.dependency, prdcList.join(','), childRecord);
                    predecessorIndex = getIndex(predecessorCollection[count], 'from', childRecord.ganttProperties.predecessor, 'to');
                    // eslint-disable-next-line
                    const temppredecessorCollection = (extend([], childRecord.ganttProperties.predecessor, [], true));
                    temppredecessorCollection.splice(predecessorIndex, 1);
                    this.parent.setRecordValue('predecessor', temppredecessorCollection, childRecord.ganttProperties, true);
                    this.parent.predecessorModule.validatePredecessorDates(childRecord);
                }
            }
        }
        if (!validPredecessor || !this.parent.allowParentDependency) {
            this.parent.setRecordValue('predecessor', updatedPredecessor, parentRecord.ganttProperties, true);
            this.parent.setRecordValue('predecessorsName', '', parentRecord.ganttProperties, true);
        }
    }
    /**
     * @param {IGanttData} record .
     * @param {RowPosition} rowPosition .
     * @param {IGanttData} parentItem .
     * @returns {void} .
     * @private
     */
    backUpAndPushNewlyAddedRecord(record, rowPosition, parentItem) {
        const flatRecords = this.parent.flatData;
        const currentViewData = this.parent.currentViewData;
        const ids = this.parent.ids;
        let currentItemIndex;
        let recordIndex;
        let updatedCollectionIndex;
        let childIndex;
        switch (rowPosition) {
            case 'Top':
                flatRecords.splice(0, 0, record);
                currentViewData.splice(0, 0, record);
                ids.splice(0, 0, record.ganttProperties.rowUniqueID.toString()); // need to check NAN
                break;
            case 'Bottom':
                flatRecords.push(record);
                currentViewData.push(record);
                ids.push(record.ganttProperties.rowUniqueID.toString()); // need to check NAN
                if (this.parent.viewType === 'ResourceView') {
                    const taskId = record.level === 0 ? 'R' + record.ganttProperties.taskId : 'T' + record.ganttProperties.taskId;
                    this.parent.getTaskIds().push(taskId);
                }
                break;
            case 'Above':
                /*Record Updates*/
                recordIndex = flatRecords.indexOf(this.addRowSelectedItem);
                updatedCollectionIndex = currentViewData.indexOf(this.addRowSelectedItem);
                this.recordCollectionUpdate(childIndex, recordIndex, updatedCollectionIndex, record, parentItem, rowPosition);
                break;
            case 'Below':
                currentItemIndex = flatRecords.indexOf(this.addRowSelectedItem);
                if (this.addRowSelectedItem.hasChildRecords) {
                    const dataChildCount = this.getChildCount(this.addRowSelectedItem, 0);
                    recordIndex = currentItemIndex + dataChildCount + 1;
                    updatedCollectionIndex = currentViewData.indexOf(this.addRowSelectedItem) +
                        this.getVisibleChildRecordCount(this.addRowSelectedItem, 0, currentViewData) + 1;
                }
                else {
                    recordIndex = currentItemIndex + 1;
                    updatedCollectionIndex = currentViewData.indexOf(this.addRowSelectedItem) + 1;
                }
                this.recordCollectionUpdate(childIndex + 1, recordIndex, updatedCollectionIndex, record, parentItem, rowPosition);
                break;
            case 'Child':
                currentItemIndex = flatRecords.indexOf(this.addRowSelectedItem);
                if (this.addRowSelectedItem.hasChildRecords) {
                    const dataChildCount = this.getChildCount(this.addRowSelectedItem, 0);
                    recordIndex = currentItemIndex + dataChildCount + 1;
                    //Expand Add record's parent item for project view
                    if (!this.addRowSelectedItem.expanded && !this.parent.enableMultiTaskbar) {
                        this.parent.expandByID(this.addRowSelectedItem.ganttProperties.rowUniqueID);
                    }
                    updatedCollectionIndex = currentViewData.indexOf(this.addRowSelectedItem) +
                        this.getVisibleChildRecordCount(this.addRowSelectedItem, 0, currentViewData) + 1;
                }
                else {
                    this.parent.setRecordValue('hasChildRecords', true, this.addRowSelectedItem);
                    this.parent.setRecordValue('isMilestone', false, this.addRowSelectedItem.ganttProperties, true);
                    this.parent.setRecordValue('expanded', true, this.addRowSelectedItem);
                    this.parent.setRecordValue('childRecords', [], this.addRowSelectedItem);
                    recordIndex = currentItemIndex + 1;
                    updatedCollectionIndex = currentViewData.indexOf(this.addRowSelectedItem) + 1;
                    if (this.addRowSelectedItem.ganttProperties.predecessor) {
                        this.updatePredecessorOnIndentOutdent(this.addRowSelectedItem);
                    }
                    if (!isNullOrUndefined(this.addRowSelectedItem.ganttProperties.segments)) {
                        this.addRowSelectedItem.ganttProperties.segments = null;
                    }
                }
                this.recordCollectionUpdate(childIndex + 1, recordIndex, updatedCollectionIndex, record, parentItem, rowPosition);
                break;
        }
        this.newlyAddedRecordBackup = record;
    }
    /**
     * @param {number} childIndex .
     * @param {number} recordIndex .
     * @param {number} updatedCollectionIndex .
     * @param {IGanttData} record .
     * @param {IGanttData} parentItem .
     * @param {RowPosition} rowPosition .
     * @returns {void} .
     * @private
     */
    recordCollectionUpdate(childIndex, recordIndex, updatedCollectionIndex, record, parentItem, rowPosition) {
        const flatRecords = this.parent.flatData;
        const currentViewData = this.parent.currentViewData;
        const ids = this.parent.ids;
        /* Record collection update */
        flatRecords.splice(recordIndex, 0, record);
        currentViewData.splice(updatedCollectionIndex, 0, record);
        ids.splice(recordIndex, 0, record.ganttProperties.rowUniqueID.toString());
        if (this.parent.viewType === 'ResourceView') {
            const taskId = record.level === 0 ? 'R' + record.ganttProperties.taskId : 'T' + record.ganttProperties.taskId;
            this.parent.getTaskIds().splice(recordIndex, 0, taskId);
        }
        /* data Source update */
        if (!isNullOrUndefined(parentItem)) {
            if (rowPosition === 'Above') {
                childIndex = parentItem.childRecords.indexOf(this.addRowSelectedItem);
            }
            else if (rowPosition === 'Below') {
                childIndex = parentItem.childRecords.indexOf(this.addRowSelectedItem) + 1;
            }
            else {
                childIndex = parentItem.childRecords.length;
            }
            /*Child collection update*/
            parentItem.childRecords.splice(childIndex, 0, record);
            if (!this.parent.taskFields.child) {
                this.parent.taskFields.child = 'Children';
            }
            if ((this.parent.dataSource instanceof DataManager &&
                isNullOrUndefined(parentItem.taskData[this.parent.taskFields.parentID])) ||
                !isNullOrUndefined(this.parent.dataSource)) {
                const child = this.parent.taskFields.child;
                if (parentItem.taskData[child] && parentItem.taskData[child].length > 0) {
                    if (rowPosition === 'Above' || rowPosition === 'Below') {
                        parentItem.taskData[child].splice(childIndex, 0, record.taskData);
                    }
                    else {
                        parentItem.taskData[child].push(record.taskData);
                    }
                }
                else if (!this.parent.taskFields.parentID) {
                    parentItem.taskData[child] = [];
                    parentItem.taskData[child].push(record.taskData);
                }
            }
        }
    }
    /**
     * @param {IGanttData} cAddedRecord .
     * @param {IGanttData} modifiedRecords .
     * @param {string} event .
     * @returns {ITaskAddedEventArgs} .
     * @private
     */
    constructTaskAddedEventArgs(cAddedRecord, modifiedRecords, event) {
        const eventArgs = {};
        eventArgs.action = eventArgs.requestType = event;
        if (cAddedRecord.length > 1) {
            eventArgs.data = [];
            eventArgs.newTaskData = [];
            eventArgs.recordIndex = [];
            for (let i = 0; i < cAddedRecord.length; i++) {
                eventArgs.data[i] = cAddedRecord[i];
                (eventArgs.newTaskData[i]) = (getTaskData([cAddedRecord[i]], eventArgs.data[i], eventArgs, this.parent));
                eventArgs.recordIndex[i] = cAddedRecord[i].index;
            }
        }
        else if (cAddedRecord.length === 1) {
            for (let i = 0; i < cAddedRecord.length; i++) {
                (eventArgs.data) = (cAddedRecord[i]);
                (eventArgs.newTaskData) = (getTaskData([cAddedRecord[i]], eventArgs.data, eventArgs, this.parent));
                eventArgs.recordIndex = cAddedRecord[i].index;
            }
        }
        const isRecordAdded = cAddedRecord && cAddedRecord.length > 0;
        eventArgs.modifiedRecords = modifiedRecords;
        eventArgs.modifiedTaskData = getTaskData(modifiedRecords, null, null, this.parent, isRecordAdded);
        return eventArgs;
    }
    /**
     * @param {ITaskAddedEventArgs} args .
     * @returns {void} .
     * @private
     */
    addSuccess(args) {
        // let addedRecords: IGanttData = args.addedRecord;
        // let eventArgs: IActionBeginEventArgs = {};
        this.parent['regenerateCycles'] = true;
        this.parent.updatedConnectorLineCollection = [];
        this.parent.connectorLineIds = [];
        this.parent.predecessorModule.createConnectorLinesCollection(this.parent.flatData);
        this.parent.treeGrid.parentData = [];
        this.parent.addDeleteRecord = true;
        this.parent.treeGrid['isAddedFromGantt'] = true;
        this.isAdded = true;
        if (this.parent.treeGrid.editModule) {
            this.parent.treeGrid.editModule['isOnBatch'] = false;
        }
        this.parent.treeGrid.refresh();
        if (this.parent.enableImmutableMode) {
            this.parent.modifiedRecords = args.modifiedRecords;
            this.parent.modifiedRecords.push(args.data);
            this.refreshRecordInImmutableMode();
        }
    }
    refreshRecordInImmutableMode(data, dragged) {
        if (!dragged && !isNullOrUndefined(data)) {
            for (let i = data[0].index + 1; i < this.parent.currentViewData.length; i++) {
                if (data[0].level < this.parent.currentViewData[i].level) {
                    this.parent.modifiedRecords.push(this.parent.currentViewData[i]);
                }
                else {
                    break;
                }
            }
        }
        for (let i = 0; i < this.parent.modifiedRecords.length; i++) {
            const originalData = this.parent.modifiedRecords[i];
            const treeIndex = this.parent.rowDragAndDropModule && this.parent.allowRowDragAndDrop ? 1 : 0;
            const uniqueTaskID = this.parent.taskFields.id;
            const originalIndex = this.parent.currentViewData.findIndex((data) => {
                return (data[uniqueTaskID] === originalData[uniqueTaskID]);
            });
            if (this.parent.treeGrid.getRows()[originalIndex]) {
                const row = this.parent.treeGrid.grid.getRowObjectFromUID(this.parent.treeGrid.grid.getDataRows()[originalIndex].getAttribute('data-uid'));
                this.parent.treeGrid.renderModule.cellRender({
                    data: row.data,
                    cell: this.parent.treeGrid.getRows()[originalIndex].cells[this.parent.treeColumnIndex + treeIndex],
                    column: this.parent.treeGrid.grid.getColumns()[this.parent.treeColumnIndex],
                    requestType: 'rowDragAndDrop'
                });
                this.parent.treeGrid.renderModule.RowModifier({
                    data: originalData, row: this.parent.treeGrid.getRows()[originalIndex], rowHeight: this.parent.rowHeight
                });
            }
        }
    }
    /**
     * @param {IGanttData} addedRecord .
     * @param {RowPosition} rowPosition .
     * @returns {void} .
     * @private
     */
    updateRealDataSource(addedRecord, rowPosition) {
        const taskFields = this.parent.taskFields;
        let dataSource = isCountRequired(this.parent) ? getValue('result', this.parent.dataSource) :
            this.parent.dataSource;
        if (this.parent.dataSource instanceof DataManager) {
            dataSource = this.parent.dataSource.dataSource.json;
        }
        for (let i = 0; i < addedRecord.length; i++) {
            if (isNullOrUndefined(rowPosition) || isNullOrUndefined(this.addRowSelectedItem)) {
                rowPosition = rowPosition === 'Bottom' ? 'Bottom' : 'Top';
            }
            if (rowPosition === 'Top') {
                dataSource.splice(0, 0, addedRecord[i].taskData);
            }
            else if (rowPosition === 'Bottom') {
                dataSource.push(addedRecord[i].taskData);
            }
            else {
                if (!isNullOrUndefined(taskFields.id) && !isNullOrUndefined(taskFields.parentID) && rowPosition === 'Child') {
                    dataSource.push(addedRecord[i].taskData);
                }
                else {
                    this.addDataInRealDataSource(dataSource, addedRecord[i].taskData, rowPosition);
                }
            }
            this.isBreakLoop = false;
        }
    }
    /**
     * @param {object[]} dataCollection .
     * @param {IGanttData} record .
     * @param {RowPosition} rowPosition .
     * @returns {void} .
     * @private
     */
    addDataInRealDataSource(dataCollection, record, rowPosition) {
        for (let i = 0; i < dataCollection.length; i++) {
            const child = this.parent.taskFields.child;
            if (this.isBreakLoop) {
                break;
            }
            if (getValue(this.parent.taskFields.id, dataCollection[i]).toString() ===
                this.addRowSelectedItem.ganttProperties.rowUniqueID.toString()) {
                let index;
                if (this.parent.rowDragAndDropModule && this.parent.rowDragAndDropModule['droppedRecord'] && this.parent.viewType === 'ResourceView') {
                    for (let i = 0; i < dataCollection.length; i++) {
                        if (dataCollection[i][this.parent.taskFields.id] === this.parent.rowDragAndDropModule['droppedRecord'].ganttProperties.taskId) {
                            index = i;
                            break;
                        }
                    }
                }
                if (rowPosition === 'Above' && dataCollection.indexOf(record) === -1) {
                    if (index) {
                        dataCollection.splice(index, 0, record);
                    }
                    else {
                        dataCollection.splice(i, 0, record);
                    }
                }
                else if (rowPosition === 'Below' && dataCollection.indexOf(record) === -1) {
                    if (index) {
                        dataCollection.splice(index + 1, 0, record);
                    }
                    else {
                        dataCollection.splice(i + 1, 0, record);
                    }
                }
                else if (rowPosition === 'Child') {
                    if (dataCollection[i][child] && dataCollection[i][child].length > 0) {
                        if (dataCollection[i][child].indexOf(record) === -1) {
                            dataCollection[i][child].push(record);
                        }
                    }
                    else {
                        dataCollection[i][child] = [];
                        dataCollection[i][child].push(record);
                    }
                }
                this.isBreakLoop = true;
                break;
            }
            else if (dataCollection[i][child] && dataCollection.indexOf(record) === -1) {
                const childRecords = dataCollection[i][child];
                this.addDataInRealDataSource(childRecords, record, rowPosition);
            }
        }
    }
    /**
     * Method to update the values to client side from server side.
     *
     * @param {Object} e - Defines the new modified data from the server.
     * @param {Object[]} e.addedRecords .
     * @param {Object[]} e.changedRecords .
     * @param {ITaskAddedEventArgs} args - Defines the client side data.
     * @returns {void} .
     */
    updateClientDataFromServer(e, args) {
        const serverReturnedValue = e.addedRecords[0];
        const convertedRecord = Object.assign({}, serverReturnedValue);
        if (this.parent.timezone) {
            this.processStandardDateFields(serverReturnedValue, convertedRecord, this.parent, 'convert');
        }
        this.processCustomDateColumns(convertedRecord, convertedRecord, this.parent, 'convert');
        const customColumnSet = new Set(this.parent.customColumns || []);
        const _a = Object.keys(serverReturnedValue);
        const _aLength = _a.length;
        for (let j = 0; j < _aLength; j++) {
            const key = _a[j];
            const isStartDate = key === this.parent.taskFields.startDate;
            const isEndDate = key === this.parent.taskFields.endDate;
            const isCustomColumn = customColumnSet.has(key);
            const isBaselineStartDate = key === this.parent.taskFields.baselineStartDate;
            const isBaselineEndDate = key === this.parent.taskFields.baselineEndDate;
            const shouldUseConverted = isStartDate || isEndDate || isBaselineStartDate || isBaselineEndDate || isCustomColumn;
            args.data[key] = shouldUseConverted
                ? convertedRecord[key]
                : serverReturnedValue[key];
        }
        if (this.parent.taskFields.id !== null) {
            args.data.ganttProperties['taskId'] = serverReturnedValue[this.parent.taskFields.id];
        }
        if (this.parent.taskFields.name !== null) {
            args.data.ganttProperties['taskName'] = serverReturnedValue[this.parent.taskFields.name];
        }
        if (this.parent.taskFields.startDate !== null) {
            args.data.ganttProperties['startDate'] = convertedRecord[this.parent.taskFields.startDate];
        }
        if (this.parent.taskFields.endDate !== null) {
            args.data.ganttProperties['endDate'] = convertedRecord[this.parent.taskFields.endDate];
        }
        if (this.parent.taskFields.duration !== null) {
            args.data.ganttProperties['duration'] = parseFloat(serverReturnedValue[this.parent.taskFields.duration]);
        }
        if (this.parent.taskFields.durationUnit !== null) {
            args.data.ganttProperties['durationUnit'] = serverReturnedValue[this.parent.taskFields.durationUnit];
        }
        if (this.parent.taskFields.progress !== null) {
            args.data.ganttProperties['progress'] = serverReturnedValue[this.parent.taskFields.progress];
        }
        if (this.parent.taskFields.dependency !== null) {
            args.data.ganttProperties['dependency'] = serverReturnedValue[this.parent.taskFields.dependency];
        }
        if (this.parent.taskFields.parentID !== null) {
            args.data.ganttProperties['parentID'] = serverReturnedValue[this.parent.taskFields.parentID];
        }
        if (this.parent.taskFields.baselineEndDate !== null) {
            args.data.ganttProperties['baselineEndDate'] = convertedRecord[this.parent.taskFields.baselineEndDate];
        }
        if (this.parent.taskFields.baselineStartDate !== null) {
            args.data.ganttProperties['baselineStartDate'] = convertedRecord[this.parent.taskFields.baselineStartDate];
        }
        if (this.parent.taskFields.resourceInfo !== null) {
            args.data.ganttProperties['resources'] = serverReturnedValue[this.parent.taskFields.resourceInfo];
        }
    }
    addNewUndoCollection(record, args) {
        if (this.parent.undoRedoModule && (!this.parent.undoRedoModule['isUndoRedoPerformed'] || (this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent.undoRedoModule['currentAction']['action'] === 'Add' && this.parent.viewType === 'ResourceView'))
            && this.parent['isUndoRedoItemPresent']('Add')) {
            record['action'] = 'Add';
            const tempArray = args.data.length > 0 ?
                extend([], [], args.data, true) : [args.data];
            let addedRec = [];
            for (let i = 0; i < tempArray.length; i++) {
                addedRec = (this.parent.flatData.filter((data) => {
                    return (tempArray[i].index === data.index && tempArray[i]['ganttProperties'].taskId === data.ganttProperties.taskId);
                }));
            }
            record['addedRecords'] = extend([], [], addedRec, true);
            this.parent.undoRedoModule['getUndoCollection'][this.parent.undoRedoModule['getUndoCollection'].length - 1] = record;
        }
    }
    /**
     * Method to add new record.
     *
     * @param {Object[] | Object} data - Defines the new data to add.
     * @param {RowPosition} rowPosition - Defines the position of row.
     * @param {number} rowIndex - Defines the row index.
     * @returns {void} .
     * @private
     */
    addRecord(data, rowPosition, rowIndex) {
        const tempTaskID = this.parent.taskFields.id;
        if (this.parent.editModule && this.parent.editSettings.allowAdding) {
            this.parent.isDynamicData = true;
            this.parent.treeGrid['isAddedFromGantt'] = true;
            this.parent.isOnAdded = true;
            const cAddedRecord = [];
            if (isNullOrUndefined(data)) {
                this.validateTaskPosition(data, rowPosition, rowIndex, cAddedRecord);
            }
            else if (data instanceof Array) {
                if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed']) {
                    let addData = data;
                    let addIndex = rowIndex;
                    if (this.parent.viewType === 'ResourceView') {
                        if (data[0]['position'] === 'below') {
                            rowPosition = 'Below';
                        }
                        else if (data[0]['position'] === 'above') {
                            rowPosition = 'Above';
                        }
                        else if (data[0]['position'] === 'child') {
                            rowPosition = 'Child';
                        }
                        addIndex = this.parent.taskIds.indexOf(data[0]['id'].toString());
                        addData = data[0]['data'];
                    }
                    this.validateTaskPosition(addData, rowPosition, addIndex, cAddedRecord);
                }
                else {
                    for (let i = 0; i < data.length; i++) {
                        this.validateTaskPosition(data[i], rowPosition, rowIndex, cAddedRecord);
                    }
                }
            }
            else if (typeof (data) === 'object') {
                this.validateTaskPosition(data, rowPosition, rowIndex, cAddedRecord);
            }
            else {
                return;
            }
            let args = {};
            args = this.constructTaskAddedEventArgs(cAddedRecord, this.parent.editedRecords, 'beforeAdd');
            if (!isNullOrUndefined(rowPosition)) {
                args.rowPosition = rowPosition;
            }
            if (this.parent.undoRedoModule && !this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent['isUndoRedoItemPresent']('Add')) {
                if (this.parent.undoRedoModule['redoEnabled']) {
                    this.parent.undoRedoModule['disableRedo']();
                }
                this.parent.undoRedoModule['createUndoCollection']();
            }
            this.parent.trigger('actionBegin', args, (args) => {
                this.parent.previousRecords = {};
                const tasks = this.parent.taskFields;
                let ganttData;
                if (this.parent.viewType === 'ResourceView') {
                    if (args.data['childRecords'].length > 0) {
                        ganttData = this.parent.flatData[this.parent.getTaskIds().indexOf('R' + args.data[tasks.id])];
                    }
                    else {
                        ganttData = this.parent.flatData[this.parent.getTaskIds().indexOf('T' + args.data[tasks.id])];
                    }
                }
                else {
                    ganttData = this.parent.getRecordByID(args.data[tasks.id]);
                }
                if (!isNullOrUndefined(ganttData)) {
                    this.validateUpdateValues(args.newTaskData, ganttData, true);
                    this.parent.dateValidationModule.calculateEndDate(ganttData);
                    this.parent.dataOperation.updateWidthLeft(ganttData);
                    this.parent.dataOperation.updateParentItems(ganttData);
                }
                if (!isNullOrUndefined(args.data[`${tempTaskID}`])) {
                    if (args.data[tempTaskID] !== args.data['ganttProperties']['taskId']) {
                        for (const key of Object.keys(this.parent.ids)) {
                            if (this.parent.ids[key] === args.data['ganttProperties']['taskId'].toString()) {
                                this.parent.ids[key] = args.data[tempTaskID].toString();
                                break;
                            }
                        }
                        args.data['ganttProperties']['taskId'] = args.data[tempTaskID];
                        args.newTaskData[tempTaskID] = args.data[tempTaskID];
                        args.data['ganttProperties']['rowUniqueID'] = args.data[tempTaskID].toString();
                    }
                }
                if (!args.cancel) {
                    this.parent['showLoadingIndicator']();
                    const record = {};
                    if (this.parent.timezone) {
                        this.processStandardDateFields(args.newTaskData, args.newTaskData, this.parent, 'remove');
                    }
                    this.processCustomDateColumns(args.newTaskData, args.newTaskData, this.parent, 'remove');
                    if (isRemoteData(this.parent.dataSource)) {
                        const data = this.parent.dataSource;
                        const updatedData = {
                            addedRecords: [args.newTaskData],
                            changedRecords: args.modifiedTaskData
                        };
                        /* tslint:disable-next-line */
                        const query = this.parent.query instanceof Query ? this.parent.query : new Query();
                        const adaptor = data.adaptor;
                        if (!(adaptor instanceof WebApiAdaptor || adaptor instanceof ODataAdaptor ||
                            adaptor instanceof ODataV4Adaptor) || data.dataSource.batchUrl) {
                            const processedID = args.data['ganttProperties']['taskId'];
                            /* tslint:disable-next-line */
                            const crud = data.saveChanges(updatedData, this.parent.taskFields.id, null, query);
                            crud.then((e) => {
                                if (e.addedRecords[0][this.parent.taskFields.id].toString() !== args.data['ganttProperties']['taskId']) {
                                    const previousIDIndex = this.parent.ids.indexOf(processedID);
                                    args.data['ganttProperties']['taskId'] = e.addedRecords[0][this.parent.taskFields.id].toString();
                                    args.newTaskData[tempTaskID] = e.addedRecords[0][this.parent.taskFields.id].toString();
                                    args.data['ganttProperties']['rowUniqueID'] = e.addedRecords[0][this.parent.taskFields.id].toString();
                                    if (this.parent.ids.some((id) => id ===
                                        e.addedRecords[0][this.parent.taskFields.id].toString())) {
                                        const err = `The provided ID value ${e.addedRecords[0][this.parent.taskFields.id]} already exists. Please provide a unique ID value.`;
                                        this.parent.trigger('actionFailure', { error: err });
                                    }
                                    else {
                                        if (previousIDIndex !== -1) {
                                            this.parent.ids.splice(previousIDIndex, 1, e.addedRecords[0][this.parent.taskFields.id].toString());
                                        }
                                        else {
                                            this.parent.ids.push(e.addedRecords[0][this.parent.taskFields.id].toString());
                                        }
                                    }
                                }
                                const prevID = args.data.ganttProperties.taskId.toString();
                                if (this.parent.taskFields.id && !isNullOrUndefined(e.addedRecords[0][this.parent.taskFields.id]) &&
                                    e.addedRecords[0][this.parent.taskFields.id].toString() === prevID) {
                                    this.parent.setRecordValue('taskId', e.addedRecords[0][this.parent.taskFields.id], args.data.ganttProperties, true);
                                    this.parent.setRecordValue('taskData', e.addedRecords[0], args.data);
                                    this.parent.setRecordValue(this.parent.taskFields.id, e.addedRecords[0][this.parent.taskFields.id], args.data);
                                    this.parent.setRecordValue('rowUniqueID', e.addedRecords[0][this.parent.taskFields.id].toString(), args.data.ganttProperties, true);
                                    this.updateClientDataFromServer(e, args);
                                    const idsIndex = this.parent.ids.indexOf(prevID);
                                    if (idsIndex !== -1) {
                                        this.parent.ids[idsIndex] = e.addedRecords[0][this.parent.taskFields.id].toString();
                                    }
                                }
                                this.updateNewRecord(cAddedRecord, args);
                            }).catch((e) => {
                                this.removeAddedRecord();
                                this.dmFailure(e, args);
                                this._resetProperties();
                            });
                        }
                        else {
                            const addedRecords = 'addedRecords';
                            const insertCrud = data.insert(updatedData[addedRecords], null, query);
                            insertCrud.then((e) => {
                                let addedRecords;
                                if (!isNullOrUndefined(e[0])) {
                                    addedRecords = e[0];
                                }
                                else {
                                    addedRecords = updatedData['addedRecords'][0];
                                }
                                cAddedRecord.forEach((record) => {
                                    if (!isNullOrUndefined(record)) {
                                        this.updateEditedRecordFields(addedRecords, record);
                                        this.parent.dataOperation.updateTaskData(record);
                                    }
                                });
                                this.updateNewRecord(cAddedRecord, args);
                            }).catch((e) => {
                                this.removeAddedRecord();
                                this.dmFailure(e, args);
                                this._resetProperties();
                            });
                        }
                    }
                    else {
                        this.parent.currentSelection = cAddedRecord[0];
                        if (this.parent.viewType === 'ProjectView') {
                            if ((rowPosition === 'Top' || rowPosition === 'Bottom') ||
                                ((rowPosition === 'Above' || rowPosition === 'Below' || rowPosition === 'Child') || isNullOrUndefined(rowPosition) && !args.data.parentItem)) {
                                if (args.data instanceof Array) {
                                    this.updateRealDataSource(args.data, rowPosition);
                                }
                                else {
                                    const data = [];
                                    data.push(args.data);
                                    this.updateRealDataSource(data, rowPosition);
                                    this.parent.currentSelection = cAddedRecord[0];
                                }
                            }
                        }
                        else {
                            const dataSource = isCountRequired(this.parent) ? getValue('result', this.parent.dataSource) :
                                this.parent.dataSource; // eslint-disable-line
                            dataSource.push(args.data.taskData);
                        }
                        if (cAddedRecord.level === 0) {
                            this.parent.treeGrid.parentData.splice(0, 0, cAddedRecord);
                        }
                        this.updateTreeGridUniqueID(cAddedRecord, 'add');
                        if (this.parent.viewType === 'ResourceView' && this.parent.undoRedoModule && this.parent.undoRedoModule['currentAction']) {
                            let canDelete = false;
                            if (args.data['hasChildRecords']) {
                                canDelete = true;
                            }
                            else {
                                if (args.data['parentItem']) {
                                    const parentTask = this.parent.getTaskByUniqueID(args.data['parentItem'].uniqueID);
                                    if (parentTask.ganttProperties.taskName !== this.parent.localeObj.getConstant('unassignedTask')) {
                                        canDelete = true;
                                    }
                                }
                            }
                            if (this.parent.taskIds.indexOf('R0') !== -1 && this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent.undoRedoModule['currentAction']['action'] === 'Delete'
                                && canDelete) {
                                const unassignedTask = this.parent.flatData[this.parent.taskIds.indexOf('R0')];
                                let isPresent = unassignedTask.childRecords.filter((data) => { return data.ganttProperties.taskId === args.data['ganttProperties'].taskId; });
                                if (args.data['hasChildRecords']) {
                                    isPresent = args.data['childRecords'];
                                }
                                for (let j = 0; j < isPresent.length; j++) {
                                    if (unassignedTask.childRecords.length === 1) {
                                        this.parent.removeRecords(this.parent.taskIds.indexOf('R0'), 2);
                                        this.parent.ids.splice(this.parent.taskIds.indexOf('R0'), 2);
                                        this.parent.taskIds.splice(this.parent.taskIds.indexOf('R0'), 2);
                                    }
                                    else {
                                        let index = this.parent.taskIds.indexOf('T' + isPresent[j].ganttProperties.taskId);
                                        const id = 'T' + isPresent[j].ganttProperties.taskId;
                                        const indexes = this.parent.taskIds.reduce(function (a, e, i) {
                                            if (e === id) {
                                                a.push(i);
                                            }
                                            return a;
                                        }, []);
                                        index = indexes[indexes.length - 1];
                                        this.parent.removeRecords(index);
                                        this.parent.taskIds.splice(index, 1);
                                        this.parent.ids.splice(index, 1);
                                    }
                                }
                                if (unassignedTask && this.parent.viewType === 'ResourceView') {
                                    const r0Index = this.parent.taskIds.indexOf('R0');
                                    const isValid = this.parent.flatData[r0Index + 1];
                                    if (!isValid) {
                                        this.parent.removeRecords(r0Index);
                                        this.parent.ids.splice(r0Index, 1);
                                        this.parent.taskIds.splice(r0Index, 1);
                                    }
                                }
                            }
                        }
                        this.refreshNewlyAddedRecord(args, cAddedRecord);
                        if (this.parent.viewType === 'ResourceView' && this.parent.taskFields.work && ganttData) {
                            this.parent.dataOperation.updateParentItems(ganttData, true);
                        }
                        this.addNewUndoCollection(record, args);
                        this._resetProperties();
                    }
                    this.parent.isOnAdded = false;
                }
                else {
                    this.removeAddedRecord();
                    this.reUpdatePreviousRecords();
                    this._resetProperties();
                }
            });
        }
    }
    createNewRecord() {
        const tempRecord = {};
        const ganttColumns = this.parent.ganttColumns;
        const taskSettingsFields = this.parent.taskFields;
        const taskId = this.parent.editModule.getNewTaskId();
        for (let i = 0; i < ganttColumns.length; i++) {
            const fieldName = ganttColumns[i].field;
            if (fieldName === taskSettingsFields.id) {
                tempRecord[fieldName] = taskId;
            }
            else if (ganttColumns[i].field === taskSettingsFields.startDate) {
                if (isNullOrUndefined(tempRecord[taskSettingsFields.endDate])) {
                    tempRecord[fieldName] = this.parent.editModule.dialogModule.getMinimumStartDate();
                }
                else {
                    tempRecord[fieldName] = new Date(tempRecord[taskSettingsFields.endDate]);
                }
                if (this.parent.timezone) {
                    tempRecord[fieldName] = this.parent.dateValidationModule.remove(tempRecord[fieldName], this.parent.timezone);
                }
            }
            else if (ganttColumns[i].field === taskSettingsFields.endDate) {
                if (isNullOrUndefined(tempRecord[taskSettingsFields.startDate])) {
                    tempRecord[fieldName] = this.parent.editModule.dialogModule.getMinimumStartDate();
                }
                else {
                    tempRecord[fieldName] = new Date(tempRecord[taskSettingsFields.startDate]);
                }
                if (this.parent.timezone) {
                    tempRecord[fieldName] = this.parent.dateValidationModule.remove(tempRecord[fieldName], this.parent.timezone);
                }
            }
            else if (ganttColumns[i].field === taskSettingsFields.duration) {
                tempRecord[fieldName] = 1;
            }
            else if (ganttColumns[i].field === taskSettingsFields.name) {
                tempRecord[fieldName] = this.parent.editModule.dialogModule['localeObj'].getConstant('addDialogTitle') + ' ' + taskId;
            }
            else if (ganttColumns[i].field === taskSettingsFields.progress) {
                tempRecord[fieldName] = 0;
            }
            else if (ganttColumns[i].field === taskSettingsFields.work) {
                tempRecord[fieldName] = 0;
            }
            else if (ganttColumns[i].field === taskSettingsFields.type || ganttColumns[i].field === 'taskType') {
                tempRecord[fieldName] = this.parent.taskType;
            }
            else if (ganttColumns[i].field === taskSettingsFields.milestone) {
                tempRecord[fieldName] = null;
            }
            else if (ganttColumns[i].field === taskSettingsFields.segments) {
                tempRecord[this.parent.ganttColumns[i].field] = [];
            }
            else {
                tempRecord[this.parent.ganttColumns[i].field] = '';
            }
        }
        return tempRecord;
    }
    /**
     * Method to validateTaskPosition.
     *
     * @param {Object | object[] } data - Defines the new data to add.
     * @param {RowPosition} rowPosition - Defines the position of row.
     * @param {number} rowIndex - Defines the row index.
     * @param {IGanttData} cAddedRecord - Defines the single data to validate.
     * @returns {void} .
     * @private
     */
    validateTaskPosition(data, rowPosition, rowIndex, cAddedRecord) {
        const selectedRowIndex = isNullOrUndefined(rowIndex) || isNaN(parseInt(rowIndex.toString(), 10)) ?
            this.parent.selectionModule ?
                (this.parent.selectionSettings.mode === 'Row' || this.parent.selectionSettings.mode === 'Both') &&
                    this.parent.selectionModule.selectedRowIndexes.length === 1 ?
                    this.parent.selectionModule.selectedRowIndexes[0] :
                    this.parent.selectionSettings.mode === 'Cell' &&
                        this.parent.selectionModule.getSelectedRowCellIndexes().length === 1 ?
                        this.parent.selectionModule.getSelectedRowCellIndexes()[0].rowIndex : null : null : rowIndex;
        this.addRowSelectedItem = isNullOrUndefined(selectedRowIndex) ? null : this.parent.updatedRecords[selectedRowIndex];
        rowPosition = isNullOrUndefined(rowPosition) ? this.parent.editSettings.newRowPosition : rowPosition;
        data = isNullOrUndefined(data) ? this.createNewRecord() : data;
        if (((isNullOrUndefined(selectedRowIndex) || selectedRowIndex < 0 ||
            isNullOrUndefined(this.addRowSelectedItem)) && (rowPosition === 'Above'
            || rowPosition === 'Below'
            || rowPosition === 'Child')) || !rowPosition || (rowPosition !== 'Above'
            && rowPosition !== 'Below'
            && rowPosition !== 'Child' && rowPosition !== 'Top' &&
            rowPosition !== 'Bottom')) {
            rowPosition = 'Top';
        }
        let level = 0;
        let parentItem;
        switch (rowPosition) {
            case 'Top':
            case 'Bottom':
                if (this.parent.viewType === 'ResourceView') {
                    level = 1;
                }
                else {
                    level = 0;
                }
                break;
            case 'Above':
            case 'Below':
                level = this.addRowSelectedItem.level;
                parentItem = this.parent.getParentTask(this.addRowSelectedItem.parentItem);
                break;
            case 'Child':
                level = this.addRowSelectedItem.level + 1;
                parentItem = this.addRowSelectedItem;
                break;
        }
        if (!this.parent.undoRedoModule || !this.parent.undoRedoModule['isUndoRedoPerformed']) {
            this.prepareNewlyAddedData(data, rowPosition);
        }
        const AddRecord = (this.updateNewlyAddedDataBeforeAjax(data, level, rowPosition, parentItem, rowIndex));
        cAddedRecord.push(AddRecord);
    }
    updateRowIndex() {
        const currentViewData = this.parent.currentViewData;
        const rowCount = currentViewData.length;
        for (let index = 0; index < rowCount; index++) {
            const rowIndex = index;
            currentViewData[index].index = rowIndex;
            if (currentViewData[index].parentItem && currentViewData[index].parentItem.taskId) {
                const parentRecord = this.parent.getRecordByID(currentViewData[index].parentItem.taskId);
                if (!isNullOrUndefined(parentRecord) && !isNullOrUndefined(parentRecord.index) &&
                    !isNullOrUndefined(parentRecord.level)) {
                    currentViewData[index].parentItem.index = parentRecord.index;
                    currentViewData[index].parentItem.level = parentRecord.level;
                }
            }
        }
    }
    updateNewRecord(cAddedRecord, args) {
        cAddedRecord.forEach((record) => {
            if (record.level === 0) {
                this.parent.treeGrid.parentData.splice(0, 0, record);
                const tempData = getValue('dataOperation.dataArray', this.parent);
                tempData.splice(0, 0, record.taskData);
            }
            this.updateTreeGridUniqueID(record, 'add');
        });
        this.updateTreeGridUniqueID(cAddedRecord, 'add');
        this.refreshNewlyAddedRecord(args, cAddedRecord);
        const objCollection = {};
        this.addNewUndoCollection(objCollection, args);
        this._resetProperties();
    }
    /**
     * Method to reset the flag after adding new record
     *
     * @returns {void} .
     */
    _resetProperties() {
        this.parent.isOnEdit = false;
        this.parent.hideSpinner();
        this.addRowSelectedItem = null;
        this.newlyAddedRecordBackup = null;
        this.isBreakLoop = false;
        this.parent.element.tabIndex = 0;
        this.parent.initiateEditAction(false);
    }
    /**
     * Method to update unique id collection in TreeGrid
     *
     * @param {IGanttData} data .
     * @param {string} action .
     * @returns {void} .
     */
    updateTreeGridUniqueID(data, action) {
        if (action === 'add') {
            setValue('uniqueIDCollection.' + data.uniqueID, data, this.parent.treeGrid);
        }
        else if (action === 'delete') {
            deleteObject(getValue('uniqueIDCollection', this.parent.treeGrid), data.uniqueID);
        }
    }
    refreshNewlyAddedRecord(args, cAddedRecord) {
        if (this.parent.selectionModule && this.parent.allowSelection &&
            (this.parent.selectionSettings.mode === 'Row' || this.parent.selectionSettings.mode === 'Both')) {
            this.parent.staticSelectedRowIndex = this.parent.currentViewData.indexOf(args.data);
        }
        if (this.parent.timelineSettings.updateTimescaleView) {
            let tempArray = [];
            if (args.modifiedRecords.length > 0) {
                tempArray = args.data.length > 0 ?
                    extend([], [], args.data, true) : [args.data]; // eslint-disable-next-line
                tempArray.push.apply(tempArray, args.modifiedRecords);
            }
            else {
                tempArray = args.data.length > 0 ?
                    extend([], [], args.data, true) : [args.data];
            }
            this.parent.timelineModule.updateTimeLineOnEditing([tempArray], args.action);
        }
        this.addSuccess(args);
        args = this.constructTaskAddedEventArgs(cAddedRecord, args.modifiedRecords, 'add');
        this.updateRowIndex();
        this.parent.trigger('actionComplete', args);
        this.parent['hideLoadingIndicator']();
        if (this.dialogModule.dialog && !this.dialogModule.dialogObj.isDestroyed) {
            this.dialogModule.dialogObj.hide();
        }
        this.dialogModule.dialogClose();
        if (this.parent.viewType === 'ResourceView') {
            if (cAddedRecord.length > 1) {
                for (let i = 0; i < cAddedRecord.length; i++) {
                    args.data[i].ganttProperties.sharedTaskUniqueIds.push(args.data[i]
                        .ganttProperties.rowUniqueID);
                    if (args.data[i].ganttProperties.resourceInfo) {
                        // if ((args.data[i] as IGanttData).ganttProperties.resourceInfo.length > 1) {
                        const resources = extend([], [], args.data[i].ganttProperties.resourceInfo, true);
                        resources.splice(0, 1);
                        this.updateResoures([], resources, args.data[i]);
                        // }
                    }
                    else {
                        if (!this.parent.undoRedoModule || (this.parent.undoRedoModule && !args.data[i]['hasChildRecords'] && this.parent.undoRedoModule['isUndoRedoPerformed'] || !this.parent.undoRedoModule['isUndoRedoPerformed'])) {
                            this.removeChildRecord(args.data[i]);
                            this.parent.editModule.checkWithUnassignedTask(args.data[i]);
                        }
                    }
                    for (let k = 0; k < this.updateParentRecords.length; k++) {
                        this.parent.dataOperation.updateParentItems(this.updateParentRecords[k]);
                    }
                    this.updateParentRecords = [];
                }
            }
            else {
                args.data.ganttProperties.sharedTaskUniqueIds.push(args.data.ganttProperties.rowUniqueID);
                // eslint-disable-next-line
                const ganttData = args.data;
                const ganttProperties = ganttData.ganttProperties;
                const childRecords = ganttData.childRecords;
                const undoRedoModule = this.parent.undoRedoModule;
                const handleResourceUpdate = (resourceInfo, targetData) => {
                    if (resourceInfo && resourceInfo.length > 1) {
                        const resources = extend([], [], resourceInfo, true);
                        resources.splice(0, 1);
                        if (undoRedoModule && undoRedoModule['isUndoRedoPerformed']) {
                            this.parent.editModule.addRowIndex = undefined;
                        }
                        this.updateResoures([], resources, targetData);
                    }
                };
                // eslint-disable-next-line
                if (ganttProperties.resourceInfo && ganttProperties.resourceInfo.length) {
                    handleResourceUpdate(ganttProperties.resourceInfo, ganttData);
                }
                else if (childRecords.length > 0 && childRecords[0].ganttProperties.resourceInfo &&
                    childRecords[0].ganttProperties.resourceInfo.length) {
                    handleResourceUpdate(childRecords[0].ganttProperties.resourceInfo, args.data.childRecords[0]);
                }
                else {
                    if (!this.parent.undoRedoModule || (this.parent.undoRedoModule && !args.data['hasChildRecords'] && this.parent.undoRedoModule['isUndoRedoPerformed'] || !this.parent.undoRedoModule['isUndoRedoPerformed'])) {
                        this.removeChildRecord(args.data);
                        this.parent.editModule.checkWithUnassignedTask(args.data);
                    }
                }
                for (let k = 0; k < this.updateParentRecords.length; k++) {
                    this.parent.dataOperation.updateParentItems(this.updateParentRecords[k]);
                }
                this.updateParentRecords = [];
            }
        }
    }
    /**
     *
     * @returns {void} .
     * @private
     */
    removeAddedRecord() {
        const flatRecords = this.parent.flatData;
        const currentViewData = this.parent.currentViewData;
        const ids = this.parent.ids;
        const flatRecordsIndex = flatRecords.indexOf(this.newlyAddedRecordBackup);
        const currentViewDataIndex = currentViewData.indexOf(this.newlyAddedRecordBackup);
        const idsIndex = ids.indexOf(this.newlyAddedRecordBackup.ganttProperties.rowUniqueID.toString());
        deleteObject(this.parent.previousRecords, flatRecords[flatRecordsIndex].uniqueID);
        if (this.newlyAddedRecordBackup.parentItem) {
            const parentItem = this.parent.getParentTask(this.newlyAddedRecordBackup.parentItem);
            const parentIndex = parentItem.childRecords.indexOf(this.newlyAddedRecordBackup);
            parentItem.childRecords.splice(parentIndex, 1);
            if (parentItem.childRecords.length === 0 && parentItem.hasChildRecords) {
                parentItem.hasChildRecords = false;
            }
        }
        flatRecords.splice(flatRecordsIndex, 1);
        currentViewData.splice(currentViewDataIndex, 1);
        ids.splice(idsIndex, 1);
    }
    getPrevRecordIndex() {
        const prevRecord = this.parent.updatedRecords[this.parent.selectionModule.getSelectedRowIndexes()[0] - 1];
        const selectedRecord = this.parent.selectionModule.getSelectedRecords()[0];
        const parent = this.parent.getRootParent(prevRecord, selectedRecord.level);
        const prevIndex = this.parent.updatedRecords.indexOf(parent);
        return prevIndex;
    }
    /**
     * indent a selected record
     *
     * @returns {void} .
     */
    indent() {
        const index = this.parent.selectedRowIndex;
        const isSelected = this.parent.selectionModule ? this.parent.selectionModule.selectedRowIndexes.length === 1 ||
            this.parent.selectionModule.getSelectedRowCellIndexes().length === 1 ? true : false : false;
        let dropIndex;
        const prevRecord = this.parent.updatedRecords[this.parent.selectionModule.getSelectedRowIndexes()[0] - 1];
        const selectedRecord = this.parent.selectionModule.getSelectedRecords()[0];
        if (!this.parent.editSettings.allowEditing || index === 0 || index === -1 || !isSelected ||
            this.parent.viewType === 'ResourceView' || !isNullOrUndefined(this.parent.updatedRecords[index]) && this.parent.updatedRecords[index].level - prevRecord.level === 1) {
            return;
        }
        else {
            if (prevRecord.level - selectedRecord.level === 0) {
                dropIndex = this.parent.selectionModule.getSelectedRowIndexes()[0] - 1;
            }
            else {
                dropIndex = this.getPrevRecordIndex();
            }
            this.indentOutdentRow([this.parent.selectionModule.getSelectedRowIndexes()[0]], dropIndex, 'child');
        }
    }
    /**
     * To perform outdent operation for selected row
     *
     * @returns {void} .
     */
    outdent() {
        const index = this.parent.selectionModule.getSelectedRowIndexes()[0];
        let dropIndex;
        const isSelected = this.parent.selectionModule ? this.parent.selectionModule.selectedRowIndexes.length === 1 ||
            this.parent.selectionModule.getSelectedRowCellIndexes().length === 1 ? true : false : false;
        if (!this.parent.editSettings.allowEditing || index === -1 || index === 0 || !isSelected ||
            this.parent.viewType === 'ResourceView' || this.parent.updatedRecords[index].level === 0) {
            return;
        }
        else {
            const thisParent = this.parent.getTaskByUniqueID(this.parent.selectionModule.getSelectedRecords()[0].parentItem.uniqueID);
            dropIndex = this.parent.updatedRecords.indexOf(thisParent);
            this.indentOutdentRow([index], dropIndex, 'below');
        }
    }
    indentOutdentRow(fromIndexes, toIndex, pos) {
        // eslint-disable-next-line
        if (fromIndexes[0] !== toIndex && pos === 'above' || 'below' || 'child') {
            if (pos === 'above') {
                this.dropPosition = 'topSegment';
            }
            if (pos === 'below') {
                this.dropPosition = 'bottomSegment';
            }
            if (pos === 'child') {
                this.dropPosition = 'middleSegment';
            }
            let action;
            const record = [];
            for (let i = 0; i < fromIndexes.length; i++) {
                if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent.undoRedoModule['currentAction']) {
                    if (this.parent.undoRedoModule['currentAction']['modifiedRecord']) {
                        record[i] = this.parent.undoRedoModule['currentAction']['modifiedRecord'][i].data;
                    }
                    else {
                        record[i] = this.parent.undoRedoModule['currentAction']['data'][i];
                    }
                }
                else {
                    record[i] = this.parent.updatedRecords[fromIndexes[i]];
                }
            }
            const isByMethod = true;
            const args = {
                data: record,
                dropIndex: toIndex,
                dropPosition: this.dropPosition
            };
            if (this.dropPosition === 'middleSegment') {
                action = 'indenting';
            }
            else if (this.dropPosition === 'bottomSegment') {
                action = 'outdenting';
            }
            const actionArgs = {
                action: action,
                data: record[0],
                cancel: false
            };
            this.parent.trigger('actionBegin', actionArgs, (actionArg) => {
                if (!actionArg.cancel) {
                    this.parent['showLoadingIndicator']();
                    this.reArrangeRows(args, isByMethod);
                }
                else {
                    this.parent['hideLoadingIndicator']();
                    return;
                }
            });
        }
        else {
            return;
        }
    }
    reArrangeRows(args, isByMethod) {
        this.dropPosition = args.dropPosition;
        this.parent['oldRecords'] = [];
        this.parent['oldRecords'] = extend([], [], args.data, true);
        if (args.dropPosition !== 'Invalid' && this.parent.editModule) {
            const obj = this.parent;
            let draggedRec;
            this.droppedRecord = obj.updatedRecords[args.dropIndex];
            const action = args.dropPosition === 'middleSegment' ? 'Indent' : 'Outdent';
            if (this.parent.undoRedoModule && !this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent['isUndoRedoItemPresent'](action)) {
                const record = {};
                record['action'] = action;
                record['modifiedRecord'] = [];
                record['selectedRowIndexes'] = extend([], [], this.parent.selectionModule.selectedRowIndexes, true);
                this.parent.undoRedoModule['findPosition'](extend([], [], [args.data], true)[0], record, 'modifiedRecord');
                record['droppedRecord'] = extend([], [], this.droppedRecord, true);
                if (this.parent.undoRedoModule['redoEnabled']) {
                    this.parent.undoRedoModule['redoEnabled'] = false;
                    this.parent.undoRedoModule['getUndoCollection'] = [];
                    this.parent.undoRedoModule['getRedoCollection'] = [];
                    if (this.parent.toolbarModule) {
                        this.parent.toolbarModule.enableItems([this.parent.controlId + '_redo'], false);
                    }
                    this.parent.undoRedoModule['getUndoCollection'][0] = [];
                }
                this.parent.undoRedoModule['createUndoCollection']();
                this.parent.undoRedoModule['getUndoCollection'][this.parent.undoRedoModule['getUndoCollection'].length - 1] = record;
            }
            let dragRecords = [];
            const droppedRec = this.droppedRecord;
            if (!args.data[0]) {
                dragRecords.push(args.data);
            }
            else {
                dragRecords = args.data;
            }
            let c = 0;
            const dLength = dragRecords.length;
            for (let i = 0; i < dLength; i++) {
                this.parent.isOnEdit = true;
                draggedRec = dragRecords[i];
                this.draggedRecord = draggedRec;
                if (this.dropPosition !== 'Invalid') {
                    if (isByMethod) {
                        this.deleteDragRow();
                    }
                    const recordIndex1 = this.treeGridData.indexOf(droppedRec);
                    this.parent.editModule.updateResourceRelatedFields(droppedRec, '');
                    if (this.dropPosition === 'bottomSegment' && recordIndex1 !== -1) {
                        if (!droppedRec.hasChildRecords) {
                            if (this.parent.taskFields.parentID && this.ganttData.length > 0) {
                                this.ganttData.splice(recordIndex1 + 1, 0, this.draggedRecord.taskData);
                            }
                            this.treeGridData.splice(recordIndex1 + 1, 0, this.draggedRecord);
                            this.parent.ids.splice(recordIndex1 + 1, 0, this.draggedRecord.ganttProperties.rowUniqueID.toString());
                        }
                        else {
                            c = this.parent.editModule.getChildCount(droppedRec, 0);
                            if (this.parent.taskFields.parentID && this.ganttData.length > 0) {
                                this.ganttData.splice(recordIndex1 + c + 1, 0, this.draggedRecord.taskData);
                            }
                            this.treeGridData.splice(recordIndex1 + c + 1, 0, this.draggedRecord);
                            this.parent.ids.splice(recordIndex1 + c + 1, 0, this.draggedRecord.ganttProperties.rowUniqueID.toString());
                            const idIndex = this.parent.ids.indexOf(this.draggedRecord[this.parent.taskFields.id].toString());
                            if (idIndex !== recordIndex1 + c + 1) {
                                this.parent.ids.splice(idIndex, 1);
                                this.parent.ids.splice(recordIndex1 + c + 1, 0, this.draggedRecord[this.parent.taskFields.id].toString());
                            }
                        }
                        this.parent.setRecordValue('parentItem', this.treeGridData[recordIndex1].parentItem, draggedRec);
                        this.parent.setRecordValue('parentUniqueID', this.treeGridData[recordIndex1].parentUniqueID, draggedRec);
                        this.parent.setRecordValue('level', this.treeGridData[recordIndex1].level, draggedRec);
                        if (draggedRec.hasChildRecords) {
                            const level = 1;
                            this.updateChildRecordLevel(draggedRec, level);
                            this.updateChildRecord(draggedRec, recordIndex1 + c + 1);
                        }
                        if (droppedRec.parentItem) {
                            const record = this.parent.getParentTask(droppedRec.parentItem).childRecords;
                            const childRecords = record;
                            const droppedRecordIndex = childRecords.indexOf(droppedRec) + 1;
                            childRecords.splice(droppedRecordIndex, 0, draggedRec);
                        }
                    }
                    if (this.dropPosition === 'middleSegment') {
                        this.dropMiddle(recordIndex1);
                    }
                    if (!isNullOrUndefined(draggedRec.parentItem && this.updateParentRecords.indexOf(draggedRec.parentItem) !== -1)) {
                        this.updateParentRecords.push(this.parent.getTaskByUniqueID(draggedRec.parentItem.uniqueID));
                    }
                }
                if (isNullOrUndefined(draggedRec.parentItem)) {
                    const parentRecords = this.parent.treeGrid.parentData;
                    const newParentIndex = parentRecords.indexOf(this.droppedRecord);
                    if (this.dropPosition === 'bottomSegment') {
                        parentRecords.splice(newParentIndex + 1, 0, draggedRec);
                    }
                }
                this.refreshDataSource();
            }
            if (this.dropPosition === 'middleSegment') {
                if (!isNullOrUndefined(droppedRec.ganttProperties.predecessor)) {
                    const len = droppedRec.ganttProperties.predecessor.length;
                    for (let count = len - 1; count >= 0; count--) {
                        if (!isNullOrUndefined(droppedRec.ganttProperties.predecessor)) {
                            const fromRecord = this.parent.getRecordByID(droppedRec.ganttProperties.predecessor[count].from);
                            const toRecord = this.parent.getRecordByID(droppedRec.ganttProperties.predecessor[count].to);
                            const validPredecessor = this.parent.predecessorModule.validateParentPredecessor(fromRecord, toRecord);
                            if (droppedRec.ganttProperties.predecessor && (!validPredecessor || !this.parent.allowParentDependency)) {
                                this.parent.editModule.removePredecessorOnDelete(droppedRec);
                                droppedRec.ganttProperties.predecessor.splice(count, 1);
                                droppedRec.ganttProperties.predecessorsName = null;
                                droppedRec[this.parent.taskFields.dependency] = null;
                                droppedRec.taskData[this.parent.taskFields.dependency] = null;
                            }
                        }
                    }
                }
                if (droppedRec.ganttProperties.isMilestone) {
                    this.parent.setRecordValue('isMilestone', false, droppedRec.ganttProperties, true);
                    if (!isNullOrUndefined(droppedRec.taskData[this.parent.taskFields.milestone])) {
                        if (droppedRec.taskData[this.parent.taskFields.milestone] === true) {
                            droppedRec.taskData[this.parent.taskFields.milestone] = false;
                        }
                    }
                }
            }
            for (let k = 0; k < this.updateParentRecords.length; k++) {
                this.parent.dataOperation.updateParentItems(this.updateParentRecords[k]);
            }
            this.parent.editedRecords.forEach((record) => {
                this.isFirstCall = true;
                this.parent.predecessorModule.validatePredecessor(record, [], '');
            });
            for (let k = 0; k < this.updateParentRecords.length; k++) {
                this.parent.dataOperation.updateParentItems(this.updateParentRecords[k]);
            }
            this.updateParentRecords = [];
            this.parent.isOnEdit = false;
        }
        this.canReset = true;
        this.refreshRecord(args);
    }
    /**
     * @returns {void} .
     * @param {RowDropEventArgs} args .
     * @param {boolean} isDrag .
     * @private
     */
    refreshRecord(args, isDrag) {
        if (isRemoteData(this.parent.dataSource)) {
            const data = this.parent.dataSource;
            const updatedData = {
                changedRecords: getTaskData(this.parent.editedRecords, null, null, this.parent)
            };
            const queryValue = this.parent.query instanceof Query ? this.parent.query : new Query();
            let crud = null;
            const adaptor = data.adaptor;
            if (!(adaptor instanceof WebApiAdaptor && adaptor instanceof ODataAdaptor) || data.dataSource.batchUrl) {
                crud = data.saveChanges(updatedData, this.parent.taskFields.id, null, queryValue);
            }
            else {
                const changedRecords = 'changedRecords';
                crud = data.update(this.parent.taskFields.id, updatedData[changedRecords], null, queryValue);
            }
            crud.then((e) => this.indentSuccess(e, args, isDrag))
                .catch((e) => this.indentFailure(e));
        }
        else {
            this.indentOutdentSuccess(args, isDrag);
        }
    }
    indentSuccess(e, args, isDrag) {
        this.updateEditedFields(e);
        this.indentOutdentSuccess(args, isDrag);
    }
    indentFailure(e) {
        this.parent.trigger('actionFailure', { error: e });
    }
    indentOutdentSuccess(args, isDrag) {
        this.parent.treeGrid.parentData = [];
        if (this.parent.viewType === 'ProjectView' && this.parent.allowParentDependency) {
            this.parent['validateCycles']();
        }
        if (this.parent.treeGrid.editModule) {
            this.parent.treeGrid.editModule['isOnBatch'] = false;
        }
        if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed']) {
            this.parent.undoRedoModule['isPreventRowDeselectOnUndoRedo'] = true;
        }
        this.parent.treeGrid.refresh();
        if (this.parent.enableImmutableMode) {
            this.refreshRecordInImmutableMode(args.data, isDrag);
            this.parent.chartRowsModule.refreshRecords(this.parent.editedRecords);
        }
        if (isDrag) {
            args.requestType = 'rowDropped';
        }
        else {
            if (this.dropPosition === 'middleSegment') {
                args.requestType = 'indented';
            }
            else if (this.dropPosition === 'bottomSegment') {
                args.requestType = 'outdented';
            }
        }
        args.modifiedRecords = this.parent.editedRecords;
        if (this.parent.timezone) {
            for (let i = 0; i < args.modifiedRecords.length; i++) {
                updateDates(args.modifiedRecords[i], this.parent);
            }
        }
        if (this.canReset) {
            this.parent.previousRecords = {};
        }
        this.canReset = false;
        this.parent.trigger('actionComplete', args);
        this.parent['hideLoadingIndicator']();
        if (this.parent.rowDragAndDropModule) {
            this.parent.rowDragAndDropModule['draggedRecord'] = null;
        }
    }
    refreshDataSource() {
        const draggedRec = this.draggedRecord;
        const droppedRec = this.droppedRecord;
        const proxy = this.parent;
        let tempData;
        let indx;
        if (this.parent.dataSource instanceof DataManager) {
            tempData = getValue('dataOperation.dataArray', this.parent);
        }
        else {
            tempData = proxy.dataSource;
        }
        if (tempData.length > 0 && (!isNullOrUndefined(droppedRec) && !droppedRec.parentItem)) {
            for (let i = 0; i < Object.keys(tempData).length; i++) {
                if (tempData[i][this.parent.taskFields.child] === droppedRec.taskData[this.parent.taskFields.child]) {
                    indx = i;
                }
            }
            if (this.dropPosition === 'topSegment') {
                if (!this.parent.taskFields.parentID) {
                    tempData.splice(indx, 0, draggedRec.taskData);
                }
            }
            else if (this.dropPosition === 'bottomSegment') {
                if (!this.parent.taskFields.parentID) {
                    tempData.splice(indx + 1, 0, draggedRec.taskData);
                }
            }
        }
        else if (!this.parent.taskFields.parentID && (!isNullOrUndefined(droppedRec) && droppedRec.parentItem)) {
            if (this.dropPosition === 'topSegment' || this.dropPosition === 'bottomSegment') {
                const rowPos = this.dropPosition === 'topSegment' ? 'Above' : 'Below';
                this.parent.editModule.addRowSelectedItem = droppedRec;
                let dragRecord = [];
                if (!Array.isArray(draggedRec)) {
                    dragRecord[0] = draggedRec;
                }
                else {
                    dragRecord = draggedRec;
                }
                this.parent.editModule.updateRealDataSource(dragRecord, rowPos);
                delete this.parent.editModule.addRowSelectedItem;
            }
        }
        if (this.parent.taskFields.parentID) {
            if (draggedRec.parentItem) {
                if (this.dropPosition === 'topSegment' || this.dropPosition === 'bottomSegment') {
                    draggedRec[this.parent.taskFields.parentID] = droppedRec[this.parent.taskFields.parentID];
                    draggedRec.taskData[this.parent.taskFields.parentID] = droppedRec[this.parent.taskFields.parentID];
                    draggedRec.ganttProperties['parentId'] = droppedRec[this.parent.taskFields.parentID];
                }
                else {
                    draggedRec[this.parent.taskFields.parentID] = droppedRec[this.parent.taskFields.id];
                    draggedRec.taskData[this.parent.taskFields.parentID] = droppedRec[this.parent.taskFields.id];
                    draggedRec.ganttProperties['parentId'] = droppedRec[this.parent.taskFields.id];
                }
            }
            else {
                draggedRec[this.parent.taskFields.parentID] = null;
                draggedRec.taskData[this.parent.taskFields.parentID] = null;
                draggedRec.ganttProperties['parentId'] = null;
            }
        }
    }
    deleteDragRow() {
        if (this.parent.dataSource instanceof DataManager) {
            this.ganttData = getValue('dataOperation.dataArray', this.parent);
        }
        else {
            this.ganttData = isCountRequired(this.parent) ? getValue('result', this.parent.dataSource) :
                this.parent.dataSource;
        }
        this.treeGridData = isCountRequired(this.parent) ?
            getValue('result', this.parent.treeGrid.dataSource) : this.parent.treeGrid.dataSource;
        const delRow = this.parent.getTaskByUniqueID(this.draggedRecord.uniqueID);
        this.removeRecords(delRow);
    }
    updateIndentedChildRecords(indentedRecord) {
        const createParentItem = {
            uniqueID: indentedRecord.uniqueID,
            expanded: indentedRecord.expanded,
            level: indentedRecord.level,
            index: indentedRecord.index,
            taskId: indentedRecord.ganttProperties.rowUniqueID
        };
        for (let i = 0; i < indentedRecord.childRecords.length; i++) {
            this.parent.setRecordValue('parentItem', createParentItem, indentedRecord.childRecords[i]);
            this.parent.setRecordValue('parentUniqueID', indentedRecord.uniqueID, indentedRecord.childRecords[i]);
        }
        if (indentedRecord.hasChildRecords) {
            indentedRecord = indentedRecord.childRecords;
            for (let j = 0; j < indentedRecord['length']; j++) {
                this.updateIndentedChildRecords(indentedRecord[j]);
            }
        }
    }
    dropMiddle(recordIndex1) {
        const obj = this.parent;
        let childRec;
        let childRecordsLength;
        if (this.parent.undoRedoModule && this.parent.undoRedoModule['isUndoRedoPerformed'] && this.parent.undoRedoModule['currentAction']['modifiedRecord']
            && this.parent.undoRedoModule['currentAction']['modifiedRecord'][0].position !== 'child') {
            if (this.parent.undoRedoModule['currentAction']['modifiedRecord'][0].position === 'above') {
                childRecordsLength = this.parent.ids.indexOf(this.parent.undoRedoModule['currentAction']['modifiedRecord'][0].id.toString());
            }
            else if (this.parent.undoRedoModule['currentAction']['modifiedRecord'][0].position === 'below') {
                childRecordsLength = this.parent.ids.indexOf(this.parent.undoRedoModule['currentAction']['modifiedRecord'][0].id.toString()) + 1;
            }
        }
        else {
            childRec = this.parent.editModule.getChildCount(this.droppedRecord, 0);
            childRecordsLength = (isNullOrUndefined(childRec) ||
                childRec === 0) ? recordIndex1 + 1 :
                childRec + recordIndex1 + 1;
        }
        if (this.dropPosition === 'middleSegment') {
            if (this.droppedRecord.ganttProperties.isAutoSchedule &&
                !isNullOrUndefined(this.droppedRecord.ganttProperties.predecessorsName) &&
                this.droppedRecord.ganttProperties.predecessorsName !== '') {
                const startDate = this.droppedRecord.ganttProperties.startDate;
                this.parent.setRecordValue('startDate', startDate, this.draggedRecord.ganttProperties, true);
                this.parent.dateValidationModule.calculateEndDate(this.draggedRecord);
                this.parent.dataOperation.updateTaskData(this.draggedRecord);
                this.parent.dataOperation['updateTaskLeftWidth'](this.draggedRecord);
            }
            if (obj.taskFields.parentID && this.ganttData.length > 0) {
                this.ganttData.splice(childRecordsLength, 0, this.draggedRecord.taskData);
            }
            this.treeGridData.splice(childRecordsLength, 0, this.draggedRecord);
            this.parent.ids.splice(childRecordsLength, 0, this.draggedRecord[this.parent.taskFields.id].toString());
            this.recordLevel();
            if (this.draggedRecord.hasChildRecords) {
                this.updateChildRecord(this.draggedRecord, childRecordsLength, this.droppedRecord.expanded);
                if (this.parent.enableImmutableMode) {
                    const indentedRecord = this.draggedRecord;
                    this.updateIndentedChildRecords(indentedRecord);
                }
            }
            if (isNullOrUndefined(this.draggedRecord.parentItem &&
                this.updateParentRecords.indexOf(this.draggedRecord.parentItem) !== -1)) {
                this.updateParentRecords.push(this.draggedRecord.parentItem);
            }
        }
    }
    updateChildRecordLevel(record, levl) {
        let length = 0;
        let currentRec;
        levl++;
        if (!record.hasChildRecords) {
            return 0;
        }
        length = record.childRecords.length;
        for (let j = 0; j < length; j++) {
            currentRec = record.childRecords[j];
            let parentData;
            if (record.parentItem) {
                const id = 'uniqueIDCollection';
                parentData = this.parent.treeGrid[id][record.parentItem.uniqueID];
            }
            currentRec.level = record.parentItem ? parentData.level + levl : record.level + 1;
            if (currentRec.hasChildRecords) {
                levl--;
                levl = this.updateChildRecordLevel(currentRec, levl);
            }
        }
        return levl;
    }
    /* eslint-disable-next-line */
    updateChildRecord(record, count, expanded) {
        let currentRec;
        const obj = this.parent;
        let length = 0;
        if (!record.hasChildRecords) {
            return 0;
        }
        length = record.childRecords.length;
        for (let i = 0; i < length; i++) {
            currentRec = record.childRecords[i];
            count++;
            this.parent.insertRecord(currentRec, count);
            this.parent.ids.splice(count, 0, currentRec.ganttProperties.rowUniqueID.toString());
            if (obj.taskFields.parentID && this.ganttData.length > 0) {
                this.ganttData.splice(count, 0, currentRec.taskData);
            }
            if (currentRec.hasChildRecords) {
                count = this.updateChildRecord(currentRec, count);
            }
        }
        return count;
    }
    removeRecords(record) {
        const obj = this.parent;
        let dataSource;
        if (this.parent.dataSource instanceof DataManager) {
            dataSource = getValue('dataOperation.dataArray', this.parent);
        }
        else {
            dataSource = this.parent.dataSource;
        }
        const delRow = record;
        if (delRow) {
            const flatParent = this.parent.getParentTask(delRow.parentItem);
            if (delRow.parentItem) {
                const childRecords = flatParent ? flatParent.childRecords : [];
                let childIndex = 0;
                if (childRecords && childRecords.length > 0) {
                    childIndex = childRecords.indexOf(delRow);
                    flatParent.childRecords.splice(childIndex, 1);
                    if (!this.parent.taskFields.parentID) {
                        flatParent.taskData[this.parent.taskFields.child].splice(childIndex, 1);
                    }
                    // collection for updating parent record
                    this.updateParentRecords.push(flatParent);
                }
            }
            if (obj.taskFields.parentID) {
                if (delRow.hasChildRecords && delRow.childRecords.length > 0) {
                    this.removeChildItem(delRow);
                }
                let indx;
                const ganttData = dataSource.length > 0 ?
                    dataSource : this.parent.currentViewData;
                for (let i = 0; i < ganttData.length; i++) {
                    if (ganttData[i][this.parent.taskFields.id] === delRow.taskData[this.parent.taskFields.id]) {
                        indx = i;
                    }
                }
                if (indx !== -1) {
                    if (dataSource.length > 0) {
                        dataSource.splice(indx, 1);
                    }
                    let gridIndx;
                    for (let i = 0; i < this.treeGridData.length; i++) {
                        if (this.treeGridData[i][this.parent.taskFields.id] === delRow.taskData[this.parent.taskFields.id]) {
                            gridIndx = i;
                        }
                    }
                    this.treeGridData.splice(gridIndx, 1);
                    this.parent.ids.splice(gridIndx, 1);
                    if (this.parent.treeGrid.parentData.indexOf(delRow) !== -1) {
                        this.parent.treeGrid.parentData.splice(this.parent.treeGrid.parentData.indexOf(delRow), 1);
                    }
                }
            }
            const recordIdx = this.treeGridData.indexOf(delRow);
            if (!obj.taskFields.parentID) {
                const deletedRecordCount = this.getChildCount(delRow, 0);
                this.treeGridData.splice(recordIdx, deletedRecordCount + 1);
                this.parent.ids.splice(recordIdx, deletedRecordCount + 1);
                const parentIndex = this.ganttData.indexOf(delRow.taskData);
                if (parentIndex !== -1) {
                    this.ganttData.splice(parentIndex, 1);
                    this.parent.treeGrid.parentData.splice(parentIndex, 1);
                }
            }
            if (delRow.parentItem && flatParent && flatParent.childRecords && !flatParent.childRecords.length) {
                this.parent.setRecordValue('expanded', false, flatParent);
                this.parent.setRecordValue('hasChildRecords', false, flatParent);
            }
        }
    }
    removeChildItem(record) {
        let currentRec;
        let indx;
        for (let i = 0; i < record.childRecords.length; i++) {
            currentRec = record.childRecords[i];
            let data;
            if (this.parent.dataSource instanceof DataManager) {
                data = getValue('dataOperation.dataArray', this.parent);
            }
            else {
                data = this.parent.dataSource;
            }
            for (let j = 0; j < data.length; j++) {
                if (data[j][this.parent.taskFields.id] === currentRec.taskData[this.parent.taskFields.id]) {
                    indx = j;
                }
            }
            if (indx !== -1) {
                if (data.length > 0) {
                    data.splice(indx, 1);
                }
                let gridIndx;
                for (let i = 0; i < this.treeGridData.length; i++) {
                    if (this.treeGridData[i][this.parent.taskFields.id] === currentRec.taskData[this.parent.taskFields.id]) {
                        gridIndx = i;
                    }
                }
                this.treeGridData.splice(gridIndx, 1);
                this.parent.ids.splice(gridIndx, 1);
            }
            if (currentRec.hasChildRecords) {
                this.removeChildItem(currentRec);
            }
        }
    }
    insertDraggedRecordAtIndex(droppedRec, draggedRec) {
        const draggedIndex = draggedRec.index;
        const insertPosition = droppedRec.childRecords.findIndex((child) => child.index >= draggedIndex);
        if (insertPosition === -1) {
            droppedRec.childRecords.splice(droppedRec.childRecords.length, 0, draggedRec);
        }
        else {
            droppedRec.childRecords.splice(insertPosition, 0, draggedRec);
        }
    }
    recordLevel() {
        const obj = this.parent;
        const draggedRec = this.draggedRecord;
        const droppedRec = this.droppedRecord;
        const childItem = obj.taskFields.child;
        if (!droppedRec.hasChildRecords) {
            droppedRec.hasChildRecords = true;
            if (!droppedRec.childRecords.length) {
                droppedRec.childRecords = [];
                if (!obj.taskFields.parentID && isNullOrUndefined(droppedRec.taskData[childItem])) {
                    droppedRec.taskData[childItem] = [];
                }
            }
        }
        if (this.dropPosition === 'middleSegment') {
            const parentItem = extend({}, droppedRec);
            delete parentItem.childRecords;
            const createParentItem = {
                uniqueID: parentItem.uniqueID,
                expanded: parentItem.expanded,
                level: parentItem.level,
                index: parentItem.index,
                taskId: parentItem.ganttProperties.rowUniqueID
            };
            this.parent.setRecordValue('parentItem', createParentItem, draggedRec);
            this.parent.setRecordValue('parentUniqueID', droppedRec.uniqueID, draggedRec);
            this.insertDraggedRecordAtIndex(droppedRec, draggedRec);
            if (!isNullOrUndefined(draggedRec) && !obj.taskFields.parentID &&
                !isNullOrUndefined(droppedRec.taskData[childItem])) {
                droppedRec.taskData[obj.taskFields.child].splice(droppedRec.childRecords.length, 0, draggedRec.taskData);
            }
            if (!isNullOrUndefined(droppedRec.ganttProperties.segments) && droppedRec.ganttProperties.segments.length > 0) {
                droppedRec.ganttProperties.segments = null;
                droppedRec.taskData[obj.taskFields.segments] = null;
            }
            if (!draggedRec.hasChildRecords) {
                draggedRec.level = droppedRec.level + 1;
            }
            else {
                const level = 1;
                draggedRec.level = droppedRec.level + 1;
                this.updateChildRecordLevel(draggedRec, level);
            }
            droppedRec.expanded = true;
        }
    }
}
